import React, { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle, memo } from "react";
import GroupInfo from "./GroupInfo";
import SubConversationInfo from "./SubConversationInfo";
import ChatHeader from "./ChatHeader";
import ForwardDialog from "./ForwardDialog";
import OrderDetailsDialog from "./OrderDetailsDialog";
import BusinessOrderDetailsDialog from "./BusinessOrderDetailsDialog";
import PaymentDialog from "./PaymentDialog";
import PaymentStrategyDialog from "./PaymentStrategyDialog";
import PaymentRequestDialog from "./PaymentRequestDialog";
import PaymentConfirmationDialog from "./PaymentConfirmationDialog";

// NEW EXTRACTED COMPONENTS
import { ExitGroupDialog, ClearChatDialog } from "./ChatConfirmationDialogs";
import SubConversationMenu from "./SubConversationMenu";
import ChatMessageList from "./ChatMessageList";

import {
  Box,
  Typography,
  Button,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSocketContext } from "../../../context/SocketContext";
import { useSelector, shallowEqual } from "react-redux";
import { ApiEndPoints } from "src/network/endpoints";
import { axiosInstance } from "src/network/adapter";
import { ObjectId } from "bson";
import { useQuery, useQueryClient } from "react-query";
import { toast } from "react-hot-toast";
import authConfig from "src/configs/auth";
import { useUpload } from "src/contexts/UploadContext";

import SubConversations from "./SubConversations";
import ChatCategoriesPopup from "./ChatCategoriesPopup";
import MediaViewerDialog from "./MediaViewerDialog";
import MessageInput from "./MessageInput";
import MenuDialog from "./MenuDialog";
import ItemOrderDetailDialog from "./ItemOrderDetailDialog";
import CartDialog from "./CartDialog";
import AddMemberDialog from "./AddMemberDialog";
import ParticipantSelectionDialog from "./ParticipantSelectionDialog";
import NotesActionSheet from "./NotesActionSheet";
import SelectLocation from "src/@core/components/SelectLocation";
import LocationViewDialog from "./LocationViewDialog";
import { getBusinessChatSuggestions } from "src/services/businessChatSuggestionsHelper";

// Styled components
const MainChat = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.palette.background.paper,
  height: "100vh", // Full height for mobile
  width: "100%", // Full width for mobile
  overflow: "hidden",
  position: "relative", // Ensure proper positioning context
  [theme.breakpoints.up("md")]: {
    height: "calc(100vh - 64px)", // Account for app bar height on desktop
  },
  [theme.breakpoints.down("md")]: {
    // Mobile-specific adjustments
    paddingBottom: "80px", // Space for fixed input
    height: "100vh",
    // Ensure sticky positioning works properly on mobile
    overflow: "visible", // Allow sticky elements to work properly
    // Create proper stacking context for sticky elements
    isolation: "isolate",
  },
}));

const ChatBanner = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1, 2),
  backgroundColor: "#EFC1FF", // purple banner to match the theme
  color: "#4D0068", // white text
  textAlign: "center",
  fontSize: "0.875rem",
  flexShrink: 0,
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: "0.8rem",
  },
}));



// MessageInput component is now imported separately

// Location Dialog Component - uses SelectLocation component for map-based location selection

const NoConversationMessage = styled(Box)(({ theme }) => ({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  color: theme.palette.text.secondary,
  gap: theme.spacing(2),
  [theme.breakpoints.down("md")]: {
    gap: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
}));

const ChatView = forwardRef(({
  selectedContact,
  onClose,
  isNewConversation,
  isSidebarOpen = false,
  hideHeader = false, // New prop to hide header when used in popup
  conversationInfo: conversationInfoProp = null, // Passed from parent (e.g., popup)
}, ref) => {
  console.log("Chat Rendered")
  // Combine related state into objects to reduce useState calls
  const [chatState, setChatState] = useState({
    messages: [],
    newMessage: "",
    isTyping: false,
    typingUsers: [],
    isLoadingMore: false,
    hasMoreMessages: true,
  });

  const [mediaState, setMediaState] = useState({
    mediaViewerOpen: false,
    selectedMedia: null,
    currentMediaIndex: 0,
    selectedFiles: [],
    isUploading: false,
    uploadProgress: {},
  });

  const [groupInfoState, setGroupInfoState] = useState({
    isOpen: false,
    isMinimized: false,
    isFullscreen: false,
  });

  const [subConversationInfoState, setSubConversationInfoState] = useState({
    isOpen: false,
  });

  // State to store sub-conversation data for info dialog
  const [infoSubConversation, setInfoSubConversation] = useState(null);

  // State to store conversation information
  // Use prop if provided (from popup), otherwise use local state
  const [conversationInfoState, _setConversationInfo] = useState(null);
  const conversationInfo = conversationInfoProp || conversationInfoState;
  // console.log("conversationInfo---->", conversationInfo);
  // State to track opposite user's online status
  const [oppositeUserOnline, setOppositeUserOnline] = useState(false);

  // Use a ref to store current conversation info for comparison
  const conversationInfoRef = useRef(null);
  
  const setConversationInfo = useCallback((newInfo) => {
    _setConversationInfo(prev => {
      const data = typeof newInfo === 'function' ? newInfo(prev) : newInfo;
      
      // Only update if ID or a key property changed to avoid redundant re-renders
      if (prev && data && prev._id === data._id && prev.updatedAt === data.updatedAt) {
        return prev;
      }
      
      conversationInfoRef.current = data;
      return data;
    });
  }, []);

  // Keep conversationInfoRef in sync with conversationInfo (prop or state) so timeouts see latest
  useEffect(() => {
    conversationInfoRef.current = conversationInfo;
  }, [conversationInfo]);

  // State for wallet payment status
  const [enableInAppWalletPayments, setEnableInAppWalletPayments] = useState(true);
  // State for chat options menu
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // State for chat categories popup
  const [chatCategoriesOpen, setChatCategoriesOpen] = useState(false);

  // State for menu dialog
  const [menuDialogOpen, setMenuDialogOpen] = useState(false);

  // State for item order detail dialog
  const [itemOrderDetailOpen, setItemOrderDetailOpen] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);

  const [selectedCartItem, setSelectedCartItem] = useState(null);

  // State for cart dialog
  const [cartDialogOpen, setCartDialogOpen] = useState(false);
  const [cartData, setCartData] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);

  // State for add member dialog
  const [addMemberDialogOpen, setAddMemberDialogOpen] = useState(false);

  // State for participant selection dialog (for regular groups)
  const [participantSelectionDialogOpen, setParticipantSelectionDialogOpen] = useState(false);

  // State for location dialog (for selecting location to send)
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // State for location view dialog (for viewing location from messages)
  const [locationViewDialogOpen, setLocationViewDialogOpen] = useState(false);
  const [locationViewData, setLocationViewData] = useState(null);

  // State for sub-conversations
  const [subConversations, setSubConversations] = useState([]);

  // State for sub-conversation options menu
  const [subConversationMenuAnchor, setSubConversationMenuAnchor] =
    useState(null);
  const [selectedSubConversation, setSelectedSubConversation] = useState(null);

  // State for add member dialog sub-conversation context
  const [addMemberSubConversation, setAddMemberSubConversation] =
    useState(null);

  // forward dialog state
  const [forwardDialogState, setForwardDialogState] = useState({
    open: false,
    message: null,
  });

  // payment dialog state
  const [paymentDialogState, setPaymentDialogState] = useState({
    open: false,
    message: null,
    orderId: null,
    groupMemberCount: 1,
  });

  // payment flow dialog states
  const [paymentStrategyDialogState, setPaymentStrategyDialogState] = useState({
    open: false,
    message: null,
    orderData: null,
  });

  const [paymentRequestDialogState, setPaymentRequestDialogState] = useState({
    open: false,
    message: null,
  });

  const [paymentConfirmationDialogState, setPaymentConfirmationDialogState] =
    useState({
      open: false,
      message: null,
      orderData: null,
      paymentType: null,
    });

  // State for exit group confirmation dialog
  const [exitGroupDialogOpen, setExitGroupDialogOpen] = useState(false);

  // State for clear chat confirmation dialog
  const [clearChatDialogOpen, setClearChatDialogOpen] = useState(false);

  // order details dialog state
  const [orderDetailsState, setOrderDetailsState] = useState({
    open: false,
    orderId: null,
  });

  // cancellation details dialog state (for owner/team members)
  const [cancellationDetailsState, setCancellationDetailsState] = useState({
    open: false,
    orderId: null,
    messageType: null, // Store message type for dialog context
  });

  // notes action sheet state - support multiple dialogs
  const [openNotesDialogs, setOpenNotesDialogs] = useState([]);

  const messageContainerRef = useRef(null);
  // Track whether current fetch is pagination to avoid stale closure in listener
  const paginationInFlightRef = useRef(false);
  // Get current user ID from Redux
  const currentUserId = useSelector((state) => state.auth.user?.userId);

  // Helper function to check if current user is owner or team member
  const isCurrentUserOwnerOrTeamMember = useCallback(() => {
    const info = conversationInfoRef.current ?? conversationInfo;
    if (!info || !currentUserId) return false;

    const { admin = [], users = [] } = info;
    const allMembers = [...admin, ...users];

    // Find current user in members
    const currentUserMember = allMembers.find(
      (member) => member?.userId?._id === currentUserId
    );

    if (!currentUserMember) return false;

    // Check if user is owner or team member
    return (
      currentUserMember.userType === "owner" ||
      currentUserMember.userType === "teamMember"
    );
  }, [conversationInfo, currentUserId]);

  // Check if current user is removed by admin or exited the group
  const isCurrentUserRemovedByAdmin = useMemo(() => {
    if (!currentUserId) return false;
    const currentUserIdStr = String(currentUserId);
    
    const removed = conversationInfo?.removeUsersByAdmin && Array.isArray(conversationInfo.removeUsersByAdmin) &&
      conversationInfo.removeUsersByAdmin.some((u) => String(u.userId) === currentUserIdStr);
    if (removed) return true;
    
    const exited = conversationInfo?.exitUsers && Array.isArray(conversationInfo.exitUsers) &&
      conversationInfo.exitUsers.some((u) => String(u.userId) === currentUserIdStr);
    return !!exited;
  }, [conversationInfo?.removeUsersByAdmin, conversationInfo?.exitUsers, currentUserId]);

  // Memoized array of existing participant IDs for the selection dialog
  const existingParticipantIds = useMemo(() => {
    if (!conversationInfo) return [];
    
    const admins = conversationInfo.admin?.map((member) => member.userId?._id).filter(Boolean) || [];
    const regularUsers = conversationInfo.users?.map((member) => member.userId?._id).filter(Boolean) || [];
    
    return [...admins, ...regularUsers];
  }, [conversationInfo]);

  // Business chat quick reply suggestions: only for business groups; based on last message (add-remove not considered)
  const quickReplySuggestions = useMemo(() => {
    if (conversationInfo?.groupType !== "business") return [];
    const considered = (chatState.messages || []).filter((msg) => msg.type !== "add-remove");
    const lastMsg = considered.length > 0 ? considered[considered.length - 1] : null;
    const lastMessageText = lastMsg ? (lastMsg.text || lastMsg.message || "").trim() : "";
    const userType =
      conversationInfo?.admin?.find((m) => m.userId?._id === currentUserId)?.userType ||
      conversationInfo?.users?.find((m) => m.userId?._id === currentUserId)?.userType ||
      "user";
    return getBusinessChatSuggestions({
      lastMessage: lastMessageText,
      isEmpty: considered.length === 0,
      userType: userType || "user",
    });
  }, [
    conversationInfo?.groupType,
    conversationInfo?.admin,
    conversationInfo?.users,
    chatState.messages,
    currentUserId,
  ]);

  // Track component mount state and current conversation
  const isMountedRef = useRef(true);
  const currentConversationRef = useRef(null);
  const menuRefetchRef = useRef(null); // Ref to store menu refetch function
  const emitRef = useRef(null); // Set after useSocketContext() to avoid TDZ; used by useImperativeHandle

  // Get theme for responsive styling
  const theme = useTheme();
  
  // Expose handlers to parent components (e.g., ChatPopup) via ref
  useImperativeHandle(ref, () => ({
    // Menu handlers
    handleMenuOpen: (event) => {
      setMenuAnchorEl(event.currentTarget);
      setIsMenuOpen(true);
    },
    handleMenuClose: () => {
      setMenuAnchorEl(null);
      setIsMenuOpen(false);
    },
    handleMenuAction: (action) => {
      // This will call the actual handleMenuAction defined below
      // We expose it here so parent can trigger menu actions
      handleMenuActionInternal(action);
    },
    
    // Group info handlers
    handleOpenGroupInfo: () => {
      setGroupInfoState((prev) => ({
        ...prev,
        isOpen: true,
        isMinimized: false,
      }));
    },
    handleCloseGroupInfo: () => {
      setGroupInfoState((prev) => ({ ...prev, isOpen: false }));
    },
    
    // Dialog handlers
    handleToggleMute: async () => {
      try {
        const conversationId = selectedContact?.id;
        if (!conversationId) {
          toast.error("No conversation selected");
          return;
        }
        const isMuted = conversationInfo?.isMuteUser?.some(
          (muteUserId) => String(muteUserId) === String(currentUserId)
        );
        if (emitRef.current) emitRef.current("isMute", { conversationId });
        toast.success(isMuted ? "conversation unmuted" : "conversation muted");
      } catch (error) {
        console.error("Error toggling mute:", error);
        toast.error("Failed to toggle mute status");
      }
    },
    handleCloseConversation: async () => {
      try {
        if (!selectedContact?.id) {
          toast.error("Conversation not found");
          return;
        }
        await axiosInstance.post(
          ApiEndPoints.CHAT.closeConvertion(selectedContact.id),
          { isOpen: false }
        );
        toast.success("Conversation closed");
      } catch (error) {
        console.error("Error closing conversation:", error);
        toast.error(error.response?.data?.message || "Failed to close conversation");
      }
    },
    
    // Dialog state setters
    setAddMemberDialogOpen,
    setParticipantSelectionDialogOpen,
    setExitGroupDialogOpen,
    setClearChatDialogOpen,
    setMenuDialogOpen,
    
    // Data accessors
    get conversationInfo() { return conversationInfo; },
    get currentUserId() { return currentUserId; },
  }), [
    selectedContact?.id,
    conversationInfo,
    currentUserId,
  ]);

  // Get query client for invalidating queries
  const queryClient = useQueryClient();

  // Get current user from Redux state (shallowEqual avoids re-renders when store updates but user ref changes unnecessarily)
  const user = useSelector((state) => state.auth.user, shallowEqual);

  // Upload context for background uploads
  const { uploadFiles } = useUpload();

  // Fetch chat categories
  const {
    data: chatCategories,
    isLoading: chatCategoriesLoading,
    refetch: refetchChatCategories,
  } = useQuery(
    ["chatCategories", conversationInfo?.businessLocationId?._id],
    async () => {
      try {
        const businessLocationId = conversationInfo?.businessLocationId?._id;
        if (!businessLocationId) {
          return { data: [] };
        }

        const response = await axiosInstance.get(
          `${ApiEndPoints.CHAT.chatCategories}${businessLocationId}`
        );

        return response.data;
      } catch (error) {
        return { data: [] };
      }
    },
    {
      enabled: false, // Don't fetch automatically, only when needed
      refetchOnWindowFocus: false,
      retry: 1,
    }
  );

  // Fetch menu data using useQuery
  const {
    data: menuData,
    isLoading: menuDataLoading,
    refetch: refetchMenuData,
  } = useQuery(
    ["menuData", conversationInfo?.businessLocationId?._id],
    async () => {
      try {
        const businessLocationId = conversationInfo?.businessLocationId?._id;
        if (!businessLocationId) {
          return null;
        }

        const response = await axiosInstance.get(
          ApiEndPoints.MENU.getByLocation(businessLocationId)
        );

        return response.data;
      } catch (error) {
        console.error("Error fetching menu data:", error);
        throw error;
      }
    },
    {
      enabled: false, // Don't fetch automatically, only when needed
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );

  // Fetch wallet payment status when conversation info is available
  useEffect(() => {
    const fetchWalletPaymentStatus = async () => {
      const businessLocationId = conversationInfo?.businessLocationId?._id;
      if (!businessLocationId) {
        setEnableInAppWalletPayments(true); // Default to true if no location
        return;
      }

      try {
        const response = await axiosInstance.get(
          ApiEndPoints.CHAT.enableInAppWalletPayments(businessLocationId)
        );
        
        if (response.data?.status === "success" && response.data?.data?.data) {
          const walletPaymentsEnabled = response.data.data.data.enableInAppWalletPayments ?? true;
          console.log("Wallet payment status fetched:", walletPaymentsEnabled);
          setEnableInAppWalletPayments(walletPaymentsEnabled);
        }
      } catch (error) {
        console.error("Error fetching wallet payment status:", error);
        setEnableInAppWalletPayments(true); // Default to true on error
      }
    };

    fetchWalletPaymentStatus();
  }, [conversationInfo?.businessLocationId?._id]);

  // Ensure mount ref is properly initialized
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper function to safely compare conversation IDs
  const compareConversationIds = useCallback((id1, id2) => {
    if (!id1 || !id2) return false;
    return String(id1).trim() === String(id2).trim();
  }, []);

  // Helper function to check if message already exists by _id
  // FIXED: This function now only checks by message ID, not text content
  // This allows multiple messages with the same text to be displayed
  const isMessageDuplicate = useCallback((messageId, messages) => {
    if (!messageId) return false;

    // Check by _id first (most reliable)
    const existingBy_id = messages.findIndex((msg) => msg._id === messageId);

    if (existingBy_id !== -1) {
      return true;
    }

    // Check by id as fallback
    const existingById = messages.findIndex((msg) => msg.id === messageId);

    if (existingById !== -1) {
      return true;
    }

    return false;
  }, []);

  // Helper function to check if message is a true duplicate (same message ID only)
  // FIXED: Only check by message ID (_id) to prevent filtering out legitimate messages with same text
  const isTrueDuplicate = useCallback((incomingMessage, messages) => {
    if (!incomingMessage || !messages.length) return false;

    // Only check for duplicate message IDs, not text content
    // This prevents network duplicates while allowing multiple messages with same text
    const incomingId = incomingMessage._id || incomingMessage.id;

    if (!incomingId) {
      // If no ID available, don't treat as duplicate
      return false;
    }

    // Check if there's already a message with the same ID
    const isDuplicate = messages.some((msg) => {
      return msg._id === incomingId || msg.id === incomingId;
    });

    if (isDuplicate) {
    }

    return isDuplicate;
  }, []);

  // Helper function to clean up orphaned temporary messages
  const cleanupOrphanedTempMessages = useCallback(() => {
    // Clean up pending queue first
    const now = Date.now();
    let cleanedPending = 0;

    for (const [key, pending] of pendingTempMessages.current.entries()) {
      if (now - pending.sentAt > 5000) {
        // 5 seconds (much faster cleanup for quick messages)
        pendingTempMessages.current.delete(key);
        cleanedPending++;
      }
    }

    // Clean up temporary messages in state
    setChatState((prev) => {
      const filteredMessages = prev.messages.filter((msg) => {
        if (msg.isTemp) {
          // Remove temporary messages older than 45 seconds (increased to prevent premature removal)
          const messageTime = new Date(msg.timestamp).getTime();
          const isOld = now - messageTime > 45000; // 45 seconds

          // Also remove from pending messages if it exists
          if (isOld && msg._id) {
            pendingTempMessages.current.delete(msg._id);
          }

          return !isOld;
        }
        return true;
      });

      return { ...prev, messages: filteredMessages };
    });
  }, []);

  // Helper function to manually replace temporary messages by content
  const replaceTemporaryMessageByContent = useCallback((incomingMessage) => {
    const { _id, message: text, type } = incomingMessage;

    setChatState((prev) => {
      const messageIndex = prev.messages.findIndex((msg) => {
        if (!msg.isTemp || !msg.isOwn) return false;
        return msg.text === text && msg.type === type;
      });

      if (messageIndex !== -1) {
        const newMessages = [...prev.messages];
        newMessages[messageIndex] = {
          ...newMessages[messageIndex],
          isTemp: false,
          id: _id,
          _id: _id,
        };

        // Remove from pending messages
        if (_id) {
          pendingTempMessages.current.delete(_id);
        }

        return { ...prev, messages: newMessages };
      }

      return prev;
    });
  }, []);

  // Helper function to immediately replace temporary messages for quick sending
  const immediateReplaceTemporaryMessage = useCallback(
    (incomingMessage) => {
      const { _id, message: text, type } = incomingMessage;

      // Try to find and replace immediately without state update delay
      const currentMessages = chatState.messages;
      const messageIndex = currentMessages.findIndex((msg) => {
        if (!msg.isTemp || !msg.isOwn) return false;
        return msg.text === text && msg.type === type;
      });

      if (messageIndex !== -1) {
        setChatState((prev) => {
          const newMessages = [...prev.messages];
          newMessages[messageIndex] = {
            ...newMessages[messageIndex],
            isTemp: false,
            id: _id,
            _id: _id,
          };

          // Remove from pending messages
          if (_id) {
            pendingTempMessages.current.delete(_id);
          }

          return { ...prev, messages: newMessages };
        });

        return true; // Successfully replaced
      }

      return false; // No replacement made
    },
    [chatState.messages]
  );


  // Set to track processed message IDs to prevent duplicates
  const processedMessageIds = useRef(new Set());

  // Queue to track pending temporary messages that haven't been added to state yet
  const pendingTempMessages = useRef(new Map());

  // Track active listeners to prevent duplicates
  const activeListenersRef = useRef(new Map());

  // Track rapid message sending to optimize processing
  const rapidMessageTracker = useRef({
    lastMessageTime: 0,
    messageCount: 0,
    isRapidMode: false,
  });

  // Timer to auto re-enable AI if owner/team doesn't reply within 30s after AI is disabled
  const aiReenableTimerRef = useRef(null);

  // Store AI menu images by message ID to preserve them when messages come back from server
  const aiMenuImagesRef = useRef(new Map());

  const clearAiReenableTimer = useCallback(() => {
    if (aiReenableTimerRef.current) {
      clearTimeout(aiReenableTimerRef.current);
      aiReenableTimerRef.current = null;
    }
  }, []);

  const isOwnerOrTeamMemberByUserId = useCallback(
    (userId) => {
      if (!conversationInfo || !userId) return false;

      const { admin = [], users = [] } = conversationInfo;
      // Merge members and dedupe by userId._id
      const allMembers = [...admin, ...users];
      const member = allMembers.find(
        (m) => (m?.userId?._id || m?._id) === (userId?._id || userId)
      );
      const userType = member?.userType;
      return userType === "owner" || userType === "teamMember";
    },
    [conversationInfo]
  );

  const enableAiForConversation = useCallback(async () => {
    try {
      const convId = selectedContact?.id;
      if (!convId) return;
      await axiosInstance.post(ApiEndPoints.CHAT.enebleAI(convId), {
        enabled: true,
      });
      // Optimistically mark as enabled locally
      setConversationInfo((prev) =>
        prev ? { ...prev, aiEnabled: true } : prev
      );
    } catch (e) {
      // swallow; not critical for UI flow
    } finally {
      clearAiReenableTimer();
    }
  }, [selectedContact?.id, clearAiReenableTimer]);

  const scheduleAiReenableIfNeeded = useCallback(() => {
    // Always schedule when we receive the disable message; we'll re-check on fire
    clearAiReenableTimer();
    aiReenableTimerRef.current = setTimeout(() => {
      enableAiForConversation();
    }, 30000); // 30 seconds
  }, [clearAiReenableTimer, enableAiForConversation]);

  const handleAiReenableOnIncoming = useCallback(
    (incoming) => {
      if (!incoming) return;
      // Start timer when AI posts "ai message disabled"
      const text = (incoming.message || incoming.text || "").toLowerCase();
      const isSystemAiDisabledMsg =
        incoming.type === "add-remove" && text.includes("ai message disabled");

      if (isSystemAiDisabledMsg) {
        scheduleAiReenableIfNeeded();
        return;
      }

      // If a message arrives from owner/team member while timer active -> cancel
      if (aiReenableTimerRef.current) {
        const sender = incoming.senderId?._id || incoming.senderId;
        if (isOwnerOrTeamMemberByUserId(sender)) {
          clearAiReenableTimer();
        }
      }
    },
    [
      scheduleAiReenableIfNeeded,
      isOwnerOrTeamMemberByUserId,
      clearAiReenableTimer,
    ]
  );

  // Socket context
  const {
    isConnected,
    sendMessage,
    sendMessageOnSubConversation,
    joinConversation,
    leaveConversation,
    emitTyping,
    addEventListener,
    getMessages,
    getConversations,
    markAsRead,
    emit,
    socketService,
    getListenerStats,
    socket,
  } = useSocketContext();
  emitRef.current = emit;

  // Load more messages for pagination
  const loadMoreMessages = useCallback(() => {
    if (
      !selectedContact ||
      chatState.isLoadingMore ||
      !chatState.hasMoreMessages ||
      paginationInFlightRef.current // FIXED: Double-check to prevent race conditions
    ) {
      return;
    }

    const conversationId = selectedContact.isNewConversation
      ? null
      : selectedContact.id;

    // Don't load more messages for new conversations
    if (!conversationId) {
      return;
    }

    // For pagination, take the TOP visible message (oldest currently loaded)
    const topVisibleMessage = chatState.messages[0];

    if (topVisibleMessage) {
      paginationInFlightRef.current = true;
      setChatState((prev) => ({ ...prev, isLoadingMore: true }));

      // Emit getMessages with lastMessageId for pagination
      const success = getMessages(conversationId, topVisibleMessage.id);

      if (!success) {
        paginationInFlightRef.current = false;
        setChatState((prev) => ({ ...prev, isLoadingMore: false }));
      }
    } else {
    }
  }, [
    selectedContact,
    chatState.isLoadingMore,
    chatState.hasMoreMessages,
    chatState.messages,
    getMessages,
  ]);

  // Handle scroll to top for pagination
  const handleScroll = useCallback(() => {
    if (
      !messageContainerRef.current ||
      chatState.isLoadingMore ||
      !chatState.hasMoreMessages ||
      paginationInFlightRef.current // FIXED: Prevent multiple simultaneous pagination requests
    ) {
      return;
    }

    const { scrollTop } = messageContainerRef.current;
    if (scrollTop < 100) {
      // FIXED: Add debouncing to prevent rapid pagination requests

      loadMoreMessages();
    }
  }, [chatState.isLoadingMore, chatState.hasMoreMessages, loadMoreMessages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messageContainerRef.current && !chatState.isLoadingMore) {
      // FIXED: Don't auto-scroll during pagination to prevent interference
      messageContainerRef.current.scrollTop =
        messageContainerRef.current.scrollHeight;
    }
  }, [chatState.messages, chatState.isLoadingMore]);

  // Join/leave conversation when contact changes
  useEffect(() => {
    if (selectedContact) {
      const conversationId = selectedContact.isNewConversation
        ? null
        : selectedContact.id;

      // Update current conversation ref
      currentConversationRef.current = conversationId;

      // Clear pending temporary messages when switching conversations
      pendingTempMessages.current.clear();

      // Also clean up any temporary messages that might be left in state
      // BUT be more conservative and only remove very old temporary messages
      setChatState((prev) => {
        const now = Date.now();
        const cleanedMessages = prev.messages.filter((msg) => {
          if (msg.isTemp) {
            // Only remove temporary messages older than 30 seconds during conversation change
            const messageTime = new Date(msg.timestamp).getTime();
            const isOld = now - messageTime > 30000; // 30 seconds

            if (isOld) {
              return false; // Remove this old temporary message
            }
            return true; // Keep newer temporary messages
          }
          return true; // Keep non-temporary messages
        });

        return { ...prev, messages: cleanedMessages };
      });

      // Check if component is still mounted
      if (!isMountedRef.current) {
        return;
      }

      // Ensure socket is connected before proceeding
      if (!isConnected) {
        // Check if we're handling a page refresh
        const isHandlingReconnection =
          sessionStorage.getItem("handlingReconnection") === "true";

        if (isHandlingReconnection) {
          return;
        }

        return;
      }

      // Clear existing messages when switching conversations
      setChatState((prev) => ({
        ...prev,
        messages: [],
        hasMoreMessages: true,
        isLoadingMore: false,
      }));

      // Clear processed message IDs to prevent memory leaks
      processedMessageIds.current.clear();

      // Clear conversation data when switching conversations
      // Use cached conversation data if available to avoid null state
      setConversationInfo(selectedContact?.rawConversation || null);

      // FIXED: Clear sub-conversations when switching conversations to prevent showing old ones
      if (subConversations.length > 0) {
        setSubConversations([]);
      }

      // Clear reply preview when switching conversations
      setReplyPreview({
        isActive: false,
        message: null,
      });

      // Clean up any existing listeners for this conversation
      if (activeListenersRef.current.has(conversationId)) {
        const existingListeners =
          activeListenersRef.current.get(conversationId);
        existingListeners.forEach((unsubscribe) => unsubscribe());
        activeListenersRef.current.delete(conversationId);
      }

      // Additional safety: clear all messageReceived listeners to prevent duplicates
      // Removed clearEventListeners usage as it's not available

      // DEFENSIVE GUARD: Prevent duplicate listener setup if listeners already exist for this conversation
      // This can happen if the effect runs multiple times with the same conversationId
      // due to dependency changes (e.g., socket functions getting new references)
      if (conversationId && activeListenersRef.current.has(conversationId)) {
        // Listeners already exist for this conversation, skip setup to prevent duplicates
        return;
      }

      if (conversationId) {
        // Only join conversation and get messages if we have a real conversation ID
        joinConversation(conversationId);
        markAsRead(conversationId);
        paginationInFlightRef.current = false;

        // Emit getMessages with conversation ID to fetch messages
        getMessages(conversationId);

        emit("getConversationsInfo", { conversationId });
        
        // Initialize opposite user online status from selectedContact
        if (selectedContact && !selectedContact.isGroup && selectedContact.oppositeUserOnline !== undefined) {
          setOppositeUserOnline(selectedContact.oppositeUserOnline);
        }
      } else {
        // This is a new conversation - don't try to get messages yet
        setChatState((prev) => ({ ...prev, messages: [] }));
        
        // Initialize opposite user online status for new conversations
        if (selectedContact && !selectedContact.isGroup && selectedContact.oppositeUserOnline !== undefined) {
          setOppositeUserOnline(selectedContact.oppositeUserOnline);
        }
      }

      // verify currentUserId is available
      if (!currentUserId) {
      }

      // Create a unique listener ID for this conversation
      const listenerId = conversationId
        ? `conversation-${conversationId}-${Date.now()}`
        : `new-conversation-${Date.now()}`;

      // Store all unsubscribe functions for this conversation
      const conversationListeners = [];

      const messageReceivedUnsubscribe = addEventListener(
        "messageReceived",
        (data) => {
          // Debug logging for all messages
          // Check if this listener is still valid
          if (
            !isMountedRef.current ||
            currentConversationRef.current !== conversationId
          ) {
            return;
          }

          // Safety check: ensure this listener is still for the current conversation
          // For new conversations, we need to check if the message is for the current user
          const currentConversationId = currentConversationRef.current;

          if (currentConversationId) {
            // Existing conversation - check conversation ID
            // FIXED: Use ref to avoid stale closures
            const isInSubConversation =
              selectedSubConversationIdRef.current !== null;
            const messageConversationId = data.message?.conversationId;

            if (messageConversationId) {
              // FIXED: If we're in a sub-conversation, skip sub-conversation messages in main handler
              // Sub-conversation messages should only be processed by messageReceivedSubConversation
              if (isInSubConversation) {
                const currentSubConvId = selectedSubConversationIdRef.current;
                // Skip if this is a sub-conversation message (let messageReceivedSubConversation handle it)
                if (messageConversationId === currentSubConvId) {
                  return; // Skip this message, let messageReceivedSubConversation handle it
                }
              }

              // Check if message is for main conversation
              const isForMainConversation =
                messageConversationId === currentConversationId;

              if (!isForMainConversation) {
                return;
              }
            }
          } else {
            // New conversation - check if message is for the current user
            if (
              selectedContact?.id !== data.message?.receiverId &&
              selectedContact?.id !== data.message?.senderId
            ) {
              return;
            }
          }

          // handle batch messages structure (primary format from your log)
          if (data.batch && Array.isArray(data.batch)) {
            for (const batchItem of data.batch) {
              if (batchItem.message) {
                const incoming = batchItem.message;

                const attachments = Array.isArray(incoming.attachments)
                  ? incoming.attachments
                  : [];

                // ensure both values are strings for comparison to avoid type mismatch issues
                const senderIdStr = String(incoming.senderId?._id || "");
                const currentUserIdStr = String(currentUserId || "");
                const isOwnIncoming = senderIdStr === currentUserIdStr;

                // check if this message is for the current conversation
                let isCurrentConversation;
                const currentConversationId = currentConversationRef.current;

                if (currentConversationId) {
                  // Existing conversation - compare conversation IDs
                  // FIXED: Use ref to avoid stale closures
                  const isInSubConversation =
                    selectedSubConversationIdRef.current !== null;

                  // FIXED: If we're in a sub-conversation, skip sub-conversation messages in main handler
                  // Sub-conversation messages should only be processed by messageReceivedSubConversation
                  if (isInSubConversation) {
                    const currentSubConvId = selectedSubConversationIdRef.current;
                    // Skip if this is a sub-conversation message (let messageReceivedSubConversation handle it)
                    if (batchItem.conversationId === currentSubConvId) {
                      continue; // Skip this message, let messageReceivedSubConversation handle it
                    }
                  }

                  // Check if message is for main conversation
                  isCurrentConversation = compareConversationIds(
                    batchItem.conversationId,
                    currentConversationId
                  );

                  // Additional check: if conversation ID doesn't match, check if message is for current user
                  // But only for main conversation messages (sub-conversation messages are handled separately)
                  if (!isCurrentConversation && !isInSubConversation) {
                    const senderId =
                      incoming.senderId?._id || incoming.senderId;
                    const receiverId =
                      batchItem.receiverId || batchItem.senderId;

                    // Check if message involves current user (either as sender or receiver)
                    isCurrentConversation =
                      String(senderId) === String(currentUserId) ||
                      String(receiverId) === String(currentUserId);
                  }
                } else {
                  // New conversation - check if message is for the current user
                  isCurrentConversation =
                    batchItem.conversationId === selectedContact?.id ||
                    batchItem.receiverId === selectedContact?.id ||
                    batchItem.senderId === selectedContact?.id ||
                    String(incoming.senderId?._id) === String(currentUserId) ||
                    String(batchItem.receiverId) === String(currentUserId);
                }

                if (isCurrentConversation) {
                  // AI re-enable flow handling
                  handleAiReenableOnIncoming(incoming);
                  // Message is for current conversation - add to chat

                  // Enhanced duplicate checking using multiple methods
                  const messageId = incoming._id || incoming.id;

                  // IMPROVED: Check if message with this _id already exists to prevent duplicates
                  // But allow replacement of temporary messages for own messages
                  const isOwnMessage = isOwnIncoming;

                  // Check for true duplicates (same message ID or exact same content at same time)
                  if (isMessageDuplicate(messageId, chatState.messages)) {
                    return; // Skip this message entirely
                  }

                  // Check for true duplicates (same text sent at exact same time - prevents network duplicates)
                  if (isTrueDuplicate(incoming, chatState.messages)) {
                    return; // Skip this message entirely
                  }

                  // Track this message as processed to prevent multiple processing
                  if (processedMessageIds.current.has(messageId)) {
                    return;
                  }
                  processedMessageIds.current.add(messageId);

                  // FIXED: Check if this is an AI message that should replace a temporary AI message
                  const aiUserId = getBeemzAiUserId();
                  const incomingSenderId = incoming.senderId?._id || incoming.senderId;
                  const isAiMessage =
                    incomingSenderId && String(incomingSenderId) === String(aiUserId);

                  // Check if this is a message we just sent (to replace temporary message)
                  let tempMessageIndex = -1;

                  if (isOwnMessage) {
                    // Match by exact _id to replace temporary message
                    const incomingId = incoming._id || incoming.id;
                    const incomingText = incoming.message;
                    const incomingType = incoming.type;

                    // Find temporary message by exact _id match
                    tempMessageIndex = chatState.messages.findIndex(
                      (msg) => msg.isTemp && msg.isOwn && msg._id === incomingId
                    );

                    if (tempMessageIndex !== -1) {
                    } else {
                      // Fallback: Try to find by content and recent timestamp (within 5 seconds)
                      const incomingTime = new Date(
                        incoming.createdAt
                      ).getTime();
                      const timeThreshold = 5000; // 5 seconds threshold for quick messages

                      tempMessageIndex = chatState.messages.findIndex((msg) => {
                        if (!msg.isTemp || !msg.isOwn) return false;

                        // Match by content and very recent time
                        const msgTime = new Date(msg.timestamp).getTime();
                        const timeDiff = Math.abs(incomingTime - msgTime);

                        const matches =
                          msg.text === incomingText &&
                          msg.type === incomingType &&
                          timeDiff < timeThreshold;

                        return matches;
                      });
                    }

                    // Strategy 4: Check pending queue for exact match
                    if (tempMessageIndex === -1) {
                      // Try to find in pending queue by matching content and recent time
                      const incomingTime = new Date(
                        incoming.createdAt
                      ).getTime();
                      const timeThreshold = 3000; // 3 seconds for quick messages

                      for (const [
                        messageId,
                        pendingMsg,
                      ] of pendingTempMessages.current.entries()) {
                        const msgTime = new Date(pendingMsg.sentAt).getTime();
                        const timeDiff = Math.abs(incomingTime - msgTime);

                        if (
                          pendingMsg.text === incoming.message &&
                          pendingMsg.type === incoming.type &&
                          timeDiff < timeThreshold
                        ) {
                          // Remove from pending queue since it's now confirmed
                          pendingTempMessages.current.delete(messageId);

                          // Find the temporary message in state to replace it
                          tempMessageIndex = chatState.messages.findIndex(
                            (msg) => msg._id === messageId
                          );
                          if (tempMessageIndex !== -1) {
                          }
                          break;
                        }
                      }
                    }
                  }

                  // Additional check: prevent duplicate temporary messages only if they're very recent (within 1 second)
                  if (
                    isOwnMessage &&
                    chatState.messages.some((msg) => {
                      if (
                        !msg.isTemp ||
                        msg.text !== incoming.message ||
                        msg.type !== incoming.type
                      ) {
                        return false;
                      }

                      // Only prevent if the temporary message is very recent (within 1 second)
                      const msgTime = new Date(msg.timestamp).getTime();
                      const incomingTime = new Date(
                        incoming.createdAt
                      ).getTime();
                      const timeDiff = Math.abs(incomingTime - msgTime);

                      return timeDiff < 1000; // 1 second threshold
                    })
                  ) {
                    return; // skip if we already have a very recent temporary version
                  }

                  // Mark this message as processed
                  processedMessageIds.current.add(messageId);

                  const newMessage = {
                    id: incoming._id || Date.now(),
                    _id: incoming._id, // Ensure _id is preserved for future matching
                    type: incoming.type || "text",
                    text: incoming.message || incoming.text || "",
                    description: incoming.description || "",
                    attachments,
                    location: incoming.location || null,
                    latLong: incoming.latLong || null,
                    senderId: incoming.senderId, // Store the full senderId object, not just the ID
                    sender:
                      incoming.senderId?.fullName ||
                      incoming.senderId?.userName ||
                      "unknown",
                    timestamp: new Date(incoming.createdAt).toLocaleTimeString(
                      [],
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    ),
                    isOwn: isOwnIncoming, // preserve the actual ownership
                    isTemp: false, // ensure it's not marked as temporary
                    conversationId: batchItem.conversationId || incoming.conversationId || data.conversationId,
                    conversationType: incoming.conversationType || batchItem.conversationType || data.conversationType || "Conversation",
                    miniConversationId: incoming.miniConversationId || null,
                    reactions: incoming.reactions || [], // Preserve reactions from backend
                    // FIXED: Only set replyTo fields if replyTo exists and has a valid ID
                    // This prevents messages without replies from incorrectly displaying as replies
                    // Ensure replyTo has an actual ID value (string or object with _id), not just empty object/null/undefined
                    ...(function() {
                      // extract replyTo ID - can be either an object with _id property or a direct ID value
                      const replyToId = incoming.replyTo?._id || incoming.replyTo;
                      // check if we have a valid replyTo ID (not null, undefined, empty string, or empty object)
                      const hasValidReplyToId = replyToId && 
                        replyToId !== null && 
                        replyToId !== undefined && 
                        replyToId !== "" &&
                        !(typeof replyToId === "object" && Object.keys(replyToId).length === 0);
                      
                      if (hasValidReplyToId) {
                        return {
                          replyTo: incoming.replyTo._id || incoming.replyTo || null,
                          replyToText: incoming.replyTo?.message || incoming.replyToText || null,
                          replyToSender:
                            incoming.replyTo?.senderId?.fullName ||
                            incoming.replyTo?.senderId?.userName ||
                            incoming.replyToSender ||
                            null,
                          replyToAttachments: Array.isArray(
                            incoming.replyTo?.attachments
                          )
                            ? incoming.replyTo.attachments
                            : (incoming.replyToAttachments || []),
                        };
                      } else {
                        // no valid replyTo - explicitly set all reply fields to null/empty
                        return {
                          replyTo: null,
                          replyToText: null,
                          replyToSender: null,
                          replyToAttachments: [],
                        };
                      }
                    })(),
                    // Preserve important message status fields from backend
                    isDeletedForMe: incoming.isDeletedForMe || [],
                    isDeletedForEveryone:
                      incoming.isDeletedForEveryone || false,
                    isEdited: incoming.isEdited || false,
                    isForwarded: incoming.isForwarded || false,
                    forwardedFrom: incoming.forwardedFrom || null,
                    forwardedBy: incoming.forwardedBy || [],
                    editedBy: incoming.editedBy || [],
                    // FIXED: Ensure deliveredTo and readBy are always arrays with proper defensive checks
                    deliveredTo: Array.isArray(incoming.deliveredTo) ? incoming.deliveredTo : [],
                    readBy: Array.isArray(incoming.readBy) ? incoming.readBy : [],
                    // Preserve orderId for order messages
                    orderId: incoming.orderId || null,
                    amount: incoming.amount || null,
                    createdAt: incoming.createdAt || null,
                    // Preserve initiationFor field for payment_request_upfront messages
                    initiationFor: incoming.initiationFor || null,
                    // Preserve messageFor field for payment_request_individual messages
                    // If not present, derive from order data (user who initiated individual payment)
                    messageFor: (() => {
                      if (incoming.messageFor) {
                        return incoming.messageFor;
                      }
                      // For payment_request_individual messages, derive from order data
                      if (incoming.type === "payment_request_individual" && incoming.orderId) {
                        const orderData = incoming.orderId;
                        const userOrders = orderData?.groupOrder?.userOrders || [];
                        const messageAmount = incoming.amount || 0;
                        // Find user order with individual payment type, approved payment flow, and matching amount
                        const individualUserOrder = userOrders.find((userOrder) => {
                          const paymentFlow = userOrder.paymentFlow;
                          const matchesPaymentType = userOrder.paymentType === "individual" &&
                            paymentFlow?.type === "individual" &&
                            paymentFlow?.status === "approved";
                          // If message has amount, match by amountToPay; otherwise just match payment type
                          if (messageAmount > 0) {
                            return matchesPaymentType && userOrder.amountToPay === messageAmount;
                          }
                          return matchesPaymentType;
                        });
                        if (individualUserOrder) {
                          return individualUserOrder.userId?._id || individualUserOrder.userId || null;
                        }
                      }
                      return null;
                    })(),
                    // Restore AI menu images from ref if available
                    aiMenuImages:
                      aiMenuImagesRef.current.get(incoming._id || incoming.id) ||
                      incoming.aiMenuImages ||
                      null,
                  };

                  setChatState((prev) => {
                    let newMessages;
                    // Use outer tempMessageIndex variable (don't redeclare)
                    let aiTempMessageIndex = -1;

                    // FIXED: Check for temporary AI messages that need to be replaced (using latest state)
                    if (isAiMessage && !isOwnMessage) {
                      const incomingText = incoming.message || "";
                      const incomingTime = new Date(incoming.createdAt).getTime();
                      const timeThreshold = 60000; // 60 seconds threshold for AI messages

                      // Find temporary AI message by matching content and recent timestamp
                      aiTempMessageIndex = prev.messages.findIndex((msg) => {
                        // Check if it's a temporary AI message (either isTemp OR isAiStreaming)
                        if (!msg.isTemp && !msg.isAiStreaming) return false;

                        // Check if it's from AI
                        const msgSenderId = msg.senderId?._id || msg.senderId;
                        const isMsgFromAi =
                          msgSenderId && String(msgSenderId) === String(aiUserId);
                        if (!isMsgFromAi) return false;

                        // For temp AI messages with "..." text, match any recent AI message
                        // For temp messages with actual text, match by content
                        const msgText = msg.text || "";
                        const isTempWithDots = msgText === "..." || msgText.trim() === "";
                        
                        let contentMatches = false;
                        if (isTempWithDots) {
                          // If temp message is just "...", match any recent AI message
                          contentMatches = true;
                        } else {
                          // Match by content similarity
                          const cleanedMsgText = msgText.replace("...", "").trim();
                          const cleanedIncomingText = incomingText.trim();
                          
                          contentMatches =
                            cleanedIncomingText === cleanedMsgText ||
                            cleanedIncomingText.includes(cleanedMsgText) ||
                            cleanedMsgText.includes(cleanedIncomingText);
                        }

                        // Match by recent timestamp (more lenient for temp messages)
                        const msgTime = new Date(msg.timestamp || msg.createdAt || Date.now()).getTime();
                        const timeDiff = Math.abs(incomingTime - msgTime);
                        // Use longer threshold for temp messages with dots
                        const effectiveThreshold = isTempWithDots ? timeThreshold * 2 : timeThreshold;

                        return contentMatches && timeDiff < effectiveThreshold;
                      });

                    }

                    // FIXED: Replace temporary message for both own messages and AI messages
                    // Use tempMessageIndex for own messages, aiTempMessageIndex for AI messages
                    const finalTempIndex = isOwnMessage ? tempMessageIndex : aiTempMessageIndex;
                    if (finalTempIndex !== -1 && (isOwnMessage || isAiMessage)) {
                      // Replace the temporary message with the real message
                      newMessages = [...prev.messages];
                      newMessages[finalTempIndex] = {
                        ...newMessage,
                        isTemp: false, // Mark as permanent
                        isAiStreaming: false, // Ensure AI streaming flag is cleared
                        // Preserve aiMenuImages if it was set in temp message
                        aiMenuImages: prev.messages[finalTempIndex]?.aiMenuImages || newMessage.aiMenuImages || null,
                        // FIXED: Explicitly preserve deliveredTo and readBy from newMessage to ensure they're not lost
                        deliveredTo: Array.isArray(newMessage.deliveredTo) ? newMessage.deliveredTo : [],
                        readBy: Array.isArray(newMessage.readBy) ? newMessage.readBy : [],
                      };

                      // Also remove from pending messages since it's no longer temporary
                      if (newMessage._id) {
                        pendingTempMessages.current.delete(newMessage._id);
                      }
                    } else {
                      // No temp message found - check for duplicates before adding
                      let isDuplicate = false;

                      if (isOwnMessage) {
                        // For own messages, check if we already have a temporary message with same content
                        isDuplicate = prev.messages.some(
                          (msg) =>
                            msg.isTemp &&
                            msg.isOwn &&
                            msg.text === newMessage.text &&
                            msg.type === newMessage.type
                        );
                      } else if (isAiMessage) {
                        // For AI messages, check for duplicates by ID or content
                        isDuplicate = prev.messages.some((msg) => {
                          const msgSenderId = msg.senderId?._id || msg.senderId;
                          const isMsgFromAi =
                            msgSenderId && String(msgSenderId) === String(aiUserId);
                          if (!isMsgFromAi) return false;

                          // Check by ID first
                          if (msg._id === newMessage._id || msg.id === newMessage._id) {
                            return true;
                          }

                          // Check by content if no ID match (for temp messages that might have been updated)
                          const msgText = (msg.text || "").trim();
                          const incomingTextTrimmed = (incoming.message || "").trim();
                          
                          // Only consider it a duplicate if content matches AND it's not a temp message
                          // (temp messages should be replaced, not treated as duplicates)
                          return msgText === incomingTextTrimmed && !msg.isTemp && !msg.isAiStreaming;
                        });
                      } else {
                        // For other users' messages, use ID-based duplicate checking only
                        isDuplicate = prev.messages.some(
                          (msg) =>
                            msg.id === newMessage.id ||
                            msg._id === newMessage._id
                        );
                      }

                      if (!isDuplicate) {
                        newMessages = [...prev.messages, newMessage];
                      } else {
                        // FIXED: If duplicate found, update existing message with new deliveredTo and readBy data
                        newMessages = prev.messages.map((msg) => {
                          // Check if this is the duplicate message by ID
                          if (msg._id === newMessage._id || msg.id === newMessage._id) {
                            return {
                              ...msg,
                              // Update deliveredTo and readBy with latest data from server
                              deliveredTo: Array.isArray(newMessage.deliveredTo) && newMessage.deliveredTo.length > 0
                                ? newMessage.deliveredTo
                                : (Array.isArray(msg.deliveredTo) ? msg.deliveredTo : []),
                              readBy: Array.isArray(newMessage.readBy) && newMessage.readBy.length > 0
                                ? newMessage.readBy
                                : (Array.isArray(msg.readBy) ? msg.readBy : []),
                            };
                          }
                          return msg;
                        });
                      }
                    }

                    return { ...prev, messages: newMessages };
                  });

                  cleanupOrphanedTempMessages();

                  // Additional cleanup: Remove any remaining temp AI messages after real message is added
                  // Since temp message is removed immediately in handleAiStreaming, this is a safety cleanup
                  if (isAiMessage && !isOwnMessage) {
                    // Use setTimeout to avoid nested state updates
                    setTimeout(() => {
                      setChatState((prev) => {
                        const incomingText = (incoming.message || "").trim();
                        const cleanedMessages = prev.messages.filter((msg) => {
                          // Remove temp AI messages with same content or "..." animation
                          if (msg.isTemp || msg.isAiStreaming) {
                            const msgSenderId = msg.senderId?._id || msg.senderId;
                            const isMsgFromAi =
                              msgSenderId && String(msgSenderId) === String(aiUserId);
                            if (isMsgFromAi) {
                              const msgText = (msg.text || "").trim();
                              // Remove if content matches or if it's just "..." animation
                              if (msgText === "..." || msgText === incomingText) {
                                return false; // Remove this temp message
                              }
                            }
                          }
                          return true; // Keep other messages
                        });
                        return { ...prev, messages: cleanedMessages };
                      });
                    }, 100); // Small delay to avoid nested state updates
                  }

                  // Additional cleanup: Remove any temporary messages with the same content to prevent duplicates
                  // BUT only if we haven't already replaced them AND they're very recent
                  if (isOwnMessage && tempMessageIndex === -1) {
                    // Only clean up if we didn't find a temporary message to replace
                    // This prevents removing messages that were just added
                    // Only clean up very recent temporary messages (within 2 seconds)
                    setTimeout(() => {
                      setChatState((prev) => {
                        const now = Date.now();
                        const cleanedMessages = prev.messages.filter((msg) => {
                          if (
                            msg.isTemp &&
                            msg.isOwn &&
                            msg.text === incoming.message
                          ) {
                            // Only remove if the temporary message is very recent (within 2 seconds)
                            const msgTime = new Date(msg.timestamp).getTime();
                            const timeDiff = now - msgTime;

                            if (timeDiff < 2000) {
                              return false; // Remove this recent temporary message
                            }
                          }
                          return true; // Keep other messages
                        });

                        return { ...prev, messages: cleanedMessages };
                      });
                    }, 2000); // 2 second delay to prevent immediate removal
                  }
                  // Immediate check for any matching temporary messages
                  // BUT with a small delay to prevent race conditions
                  setTimeout(() => {
                    const immediateTempMessages = chatState.messages.filter(
                      (msg) =>
                        msg.isTemp &&
                        msg.isOwn &&
                        msg.text === incoming.message &&
                        msg.type === incoming.type
                    );

                    if (immediateTempMessages.length > 0) {
                      setChatState((prev) => {
                        const newMessages = prev.messages.map((msg) => {
                          if (
                            immediateTempMessages.some(
                              (tempMsg) => tempMsg.id === msg.id
                            )
                          ) {
                            return {
                              ...msg,
                              isTemp: false,
                              id: incoming._id || incoming.id,
                              _id: incoming._id || incoming.id,
                            };
                          }
                          return msg;
                        });
                        return { ...prev, messages: newMessages };
                      });
                    }
                  }, 500); // 500ms delay to prevent race conditions

                  // Try immediate replacement for quick messages
                  if (isOwnMessage) {
                    const replaced = immediateReplaceTemporaryMessage(incoming);
                    if (replaced) {
                    }
                  }

                  // Additional check: try to replace any remaining temporary messages by content (immediate)
                  const remainingTempMessages = chatState.messages.filter(
                    (msg) => msg.isTemp && msg.isOwn
                  );
                  if (remainingTempMessages.length > 0) {
                    remainingTempMessages.forEach((tempMsg) => {
                      if (
                        tempMsg.text === incoming.message &&
                        tempMsg.type === incoming.type
                      ) {
                        setChatState((prev) => {
                          const newMessages = prev.messages.map((msg) => {
                            if (msg.id === tempMsg.id) {
                              return {
                                ...msg,
                                isTemp: false,
                                id: incoming._id || incoming.id,
                                _id: incoming._id || incoming.id,
                              };
                            }
                            return msg;
                          });
                          return { ...prev, messages: newMessages };
                        });
                      }
                    });
                  }

                  // For rapid messages, try additional immediate replacement strategies
                  if (rapidMessageTracker.current.isRapidMode && isOwnMessage) {
                    // Try to find any temporary message with matching content and very recent timestamp
                    const rapidTempMessages = chatState.messages.filter(
                      (msg) =>
                        msg.isTemp &&
                        msg.isOwn &&
                        msg.text === incoming.message &&
                        Date.now() - new Date(msg.timestamp).getTime() < 3000 // Within 3 seconds
                    );

                    if (rapidTempMessages.length > 0) {
                      rapidTempMessages.forEach((tempMsg) => {
                        setChatState((prev) => {
                          const newMessages = prev.messages.map((msg) => {
                            if (msg.id === tempMsg.id) {
                              return {
                                ...msg,
                                isTemp: false,
                                id: incoming._id || incoming.id,
                                _id: incoming._id || incoming.id,
                              };
                            }
                            return msg;
                          });
                          return { ...prev, messages: newMessages };
                        });
                      });
                    }
                  }

                  // For own messages that couldn't be replaced immediately, retry after a short delay
                  if (isOwnMessage && tempMessageIndex === -1) {
                    const incomingId = incoming._id || incoming.id;

                    // Try multiple retry strategies with shorter delays for quick messages
                    const retryStrategies = [
                      // Strategy 1: Immediate retry (0ms delay)
                      () => {
                        const retryIndex = chatState.messages.findIndex(
                          (msg) =>
                            msg.isTemp && msg.isOwn && msg._id === incomingId
                        );

                        if (retryIndex !== -1) {
                          setChatState((prev) => {
                            const newMessages = [...prev.messages];
                            newMessages[retryIndex] = {
                              ...newMessages[retryIndex],
                              isTemp: false,
                              id: incomingId,
                              _id: incomingId,
                            };

                            // Remove from pending messages
                            pendingTempMessages.current.delete(incomingId);

                            return { ...prev, messages: newMessages };
                          });
                        }
                      },

                      // Strategy 2: Micro delay (5ms) and try content matching
                      () => {
                        setTimeout(() => {
                          const retryIndex = chatState.messages.findIndex(
                            (msg) => {
                              if (!msg.isTemp || !msg.isOwn) return false;
                              return (
                                msg.text === incoming.message &&
                                msg.type === incoming.type
                              );
                            }
                          );

                          if (retryIndex !== -1) {
                            setChatState((prev) => {
                              const newMessages = [...prev.messages];
                              newMessages[retryIndex] = {
                                ...newMessages[retryIndex],
                                isTemp: false,
                                id: incomingId,
                                _id: incomingId,
                              };

                              // Remove from pending messages
                              pendingTempMessages.current.delete(incomingId);

                              return { ...prev, messages: newMessages };
                            });
                          }
                        }, 5); // Reduced delay for quick messages
                      },

                      // Strategy 3: Minimal delay (20ms) and try any temporary message
                      () => {
                        setTimeout(() => {
                          const retryIndex = chatState.messages.findIndex(
                            (msg) => {
                              if (!msg.isTemp || !msg.isOwn) return false;
                              return msg.text === incoming.message;
                            }
                          );

                          if (retryIndex !== -1) {
                            setChatState((prev) => {
                              const newMessages = [...prev.messages];
                              newMessages[retryIndex] = {
                                ...newMessages[retryIndex],
                                isTemp: false,
                                id: incomingId,
                                _id: incomingId,
                              };

                              // Remove from pending messages
                              pendingTempMessages.current.delete(incomingId);

                              return { ...prev, messages: newMessages };
                            });
                          }
                        }, 20); // Reduced delay for quick messages
                      },
                    ];

                    // Execute all retry strategies
                    retryStrategies.forEach((strategy, index) => {
                      strategy();
                    });

                    replaceTemporaryMessageByContent(incoming);
                  }

                  // mark message as read if it's from another user
                  if (!isOwnIncoming) {
                    markAsRead(conversationId);
                  }
                } else {
                  // Message is for different conversation - show notification

                  // only show notification for messages from other users
                  if (!isOwnIncoming) {
                    const senderName =
                      incoming.senderId?.fullName || "unknown user";
                    const messagePreview =
                      incoming.message?.length > 30
                        ? incoming.message.substring(0, 30) + "..."
                        : incoming.message || "sent a message";

                    toast(`${senderName}: ${messagePreview}`, {
                      duration: 4000,
                      icon: "💬",
                      style: {
                        background: "#7C3AED",
                        color: "#FFFFFF",
                      },
                    });
                  }
                }
              }
            }
          } else if (data.conversationId && data.message) {
            // handle single message structure (fallback format)

            // Safety check: ensure this listener is still for the current conversation
            const currentConversationId = selectedContact?.id;
            if (
              currentConversationId &&
              data.conversationId !== currentConversationId
            ) {
              // Additional check: if conversation ID doesn't match, check if message is for current user
              const senderId =
                data.message?.senderId?._id || data.message?.senderId;
              const receiverId = data.message?.receiverId;

              const isForCurrentUser =
                String(senderId) === String(currentUserId) ||
                String(receiverId) === String(currentUserId);

              if (!isForCurrentUser) {
                return;
              } else {
              }
            }

            const incoming = data.message;
            // AI re-enable flow handling
            handleAiReenableOnIncoming(incoming);
            const attachments = Array.isArray(incoming.attachments)
              ? incoming.attachments
              : [];

            const senderIdStr = String(incoming.senderId?._id || "");
            const currentUserIdStr = String(currentUserId || "");
            const isOwnIncoming = senderIdStr === currentUserIdStr;

            // check if this message is for the current conversation
            // FIXED: Use ref to avoid stale closures
            const isInSubConversation =
              selectedSubConversationIdRef.current !== null;

            // FIXED: If we're in a sub-conversation, skip sub-conversation messages in main handler
            // Sub-conversation messages should only be processed by messageReceivedSubConversation
            if (isInSubConversation && currentConversationId) {
              const currentSubConvId = selectedSubConversationIdRef.current;
              // Skip if this is a sub-conversation message (let messageReceivedSubConversation handle it)
              if (data.conversationId === currentSubConvId) {
                return; // Skip this message, let messageReceivedSubConversation handle it
              }
            }

            let isCurrentConversation = currentConversationId
              ? compareConversationIds(
                  data.conversationId,
                  currentConversationId
                )
              : true;

            if (isCurrentConversation) {
              // Message is for current conversation - add to chat

              // Enhanced duplicate checking using multiple methods
              const messageId = incoming._id || incoming.id;

              // IMPROVED: Check if message with this _id already exists to prevent duplicates
              // But allow replacement of temporary messages for own messages
              const isOwnMessage = isOwnIncoming;

              // Check for true duplicates (same message ID)
              if (isMessageDuplicate(messageId, chatState.messages)) {
                return; // Skip this message entirely
              }

              // Check for true duplicates (same text sent at exact same time - prevents network duplicates)
              if (isTrueDuplicate(incoming, chatState.messages)) {
                return; // Skip this message entirely
              }

              // Track this message as processed to prevent multiple processing
              if (processedMessageIds.current.has(messageId)) {
                return;
              }
              processedMessageIds.current.add(messageId);

              // Check if this is a message we just sent (to replace temporary message)
              let tempMessageIndex = -1;

              if (isOwnMessage) {
                // Match by exact _id to replace temporary message
                const incomingId = incoming._id || incoming.id;
                const incomingText = incoming.message;
                const incomingType = incoming.type;

                // Find temporary message by exact _id match
                tempMessageIndex = chatState.messages.findIndex(
                  (msg) => msg.isTemp && msg.isOwn && msg._id === incomingId
                );

                if (tempMessageIndex !== -1) {
                } else {
                  // Fallback: Try to find by content and recent timestamp (within 5 seconds)
                  const incomingTime = new Date(incoming.createdAt).getTime();
                  const timeThreshold = 5000; // 5 seconds threshold for quick messages

                  tempMessageIndex = chatState.messages.findIndex((msg) => {
                    if (!msg.isTemp || !msg.isOwn) return false;

                    // Match by content and very recent time
                    const msgTime = new Date(msg.timestamp).getTime();
                    const timeDiff = Math.abs(incomingTime - msgTime);

                    const matches =
                      msg.text === incomingText &&
                      msg.type === incomingType &&
                      timeDiff < timeThreshold;

                    return matches;
                  });
                }

                // Strategy 4: Check pending queue for exact match
                if (tempMessageIndex === -1) {
                  // Try to find in pending queue by matching content and recent time
                  const incomingTime = new Date(incoming.createdAt).getTime();
                  const timeThreshold = 3000; // 3 seconds for quick messages

                  for (const [
                    messageId,
                    pendingMsg,
                  ] of pendingTempMessages.current.entries()) {
                    const msgTime = new Date(pendingMsg.sentAt).getTime();
                    const timeDiff = Math.abs(incomingTime - msgTime);

                    if (
                      pendingMsg.text === incoming.message &&
                      pendingMsg.type === incoming.type &&
                      timeDiff < timeThreshold
                    ) {
                      // Remove from pending queue since it's now confirmed
                      pendingTempMessages.current.delete(messageId);

                      // Find the temporary message in state to replace it
                      tempMessageIndex = chatState.messages.findIndex(
                        (msg) => msg._id === messageId
                      );
                      break;
                    }
                  }
                }
              }

              // Additional check: prevent duplicate temporary messages only if they're very recent (within 1 second)
              if (
                isOwnMessage &&
                chatState.messages.some((msg) => {
                  if (
                    !msg.isTemp ||
                    msg.text !== incoming.message ||
                    msg.type !== incoming.type
                  ) {
                    return false;
                  }

                  // Only prevent if the temporary message is very recent (within 1 second)
                  const msgTime = new Date(msg.timestamp).getTime();
                  const incomingTime = new Date(incoming.createdAt).getTime();
                  const timeDiff = Math.abs(incomingTime - msgTime);

                  return timeDiff < 1000; // 1 second threshold
                })
              ) {
                return; // skip if we already have a very recent temporary version
              }

              // Mark this message as processed
              processedMessageIds.current.add(messageId);

              const newMessage = {
                id: incoming._id || Date.now(),
                _id: incoming._id, // Ensure _id is preserved for future matching
                type: incoming.type || "text",
                text: incoming.message || incoming.text || "",
                attachments,
                location: incoming.location || null,
                latLong: incoming.latLong || null,
                senderId: incoming.senderId,
                sender:
                  incoming.senderId?.fullName ||
                  incoming.senderId?.userName ||
                  "unknown",
                timestamp: new Date(incoming.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                isOwn: isOwnIncoming, // preserve the actual ownership
                isTemp: false, // ensure it's not marked as temporary
                conversationId: incoming.conversationId || data.conversationId,
                conversationType: incoming.conversationType || data.conversationType || "Conversation",
                miniConversationId: incoming.miniConversationId || null,
                reactions: incoming.reactions || [], // Preserve reactions from backend
                // FIXED: Only set replyTo fields if replyTo exists and has a valid ID
                // This prevents messages without replies from incorrectly displaying as replies
                // Ensure replyTo has an actual ID value (string or object with _id), not just empty object/null/undefined
                ...(function() {
                  // extract replyTo ID - can be either an object with _id property or a direct ID value
                  const replyToId = incoming.replyTo?._id || incoming.replyTo;
                  // check if we have a valid replyTo ID (not null, undefined, empty string, or empty object)
                  const hasValidReplyToId = replyToId && 
                    replyToId !== null && 
                    replyToId !== undefined && 
                    replyToId !== "" &&
                    !(typeof replyToId === "object" && Object.keys(replyToId).length === 0);
                  
                  if (hasValidReplyToId) {
                    return {
                      replyTo: incoming.replyTo._id || incoming.replyTo || null,
                      replyToText: incoming.replyTo?.message || incoming.replyToText || null,
                      replyToSender:
                        incoming.replyTo?.senderId?.fullName ||
                        incoming.replyTo?.senderId?.userName ||
                        incoming.replyToSender ||
                        null,
                      replyToAttachments: Array.isArray(
                        incoming.replyTo?.attachments
                      )
                        ? incoming.replyTo.attachments
                        : (incoming.replyToAttachments || []),
                    };
                  } else {
                    // no valid replyTo - explicitly set all reply fields to null/empty
                    return {
                      replyTo: null,
                      replyToText: null,
                      replyToSender: null,
                      replyToAttachments: [],
                    };
                  }
                })(),
                // Preserve important message status fields from backend
                isDeletedForMe: incoming.isDeletedForMe || [],
                isDeletedForEveryone: incoming.isDeletedForEveryone || false,
                isEdited: incoming.isEdited || false,
                isForwarded: incoming.isForwarded || false,
                forwardedFrom: incoming.forwardedFrom || null,
                forwardedBy: incoming.forwardedBy || [],
                editedBy: incoming.editedBy || [],
                // FIXED: Ensure deliveredTo and readBy are always arrays with proper defensive checks
                deliveredTo: Array.isArray(incoming.deliveredTo) ? incoming.deliveredTo : [],
                readBy: Array.isArray(incoming.readBy) ? incoming.readBy : [],
                orderId: incoming.orderId || null,
                amount: incoming.amount || null,
                createdAt: incoming.createdAt || null,
                // Preserve initiationFor field for payment_request_upfront messages
                initiationFor: incoming.initiationFor || null,
                // Preserve messageFor field for payment_request_individual messages
                // If not present, derive from order data (user who initiated individual payment)
                messageFor: (() => {
                  if (incoming.messageFor) {
                    return incoming.messageFor;
                  }
                  // For payment_request_individual messages, derive from order data
                  if (incoming.type === "payment_request_individual" && incoming.orderId) {
                    const orderData = incoming.orderId;
                    const userOrders = orderData?.groupOrder?.userOrders || [];
                    const messageAmount = incoming.amount || 0;
                    // Find user order with individual payment type, approved payment flow, and matching amount
                    const individualUserOrder = userOrders.find((userOrder) => {
                      const paymentFlow = userOrder.paymentFlow;
                      const matchesPaymentType = userOrder.paymentType === "individual" &&
                        paymentFlow?.type === "individual" &&
                        paymentFlow?.status === "approved";
                      // If message has amount, match by amountToPay; otherwise just match payment type
                      if (messageAmount > 0) {
                        return matchesPaymentType && userOrder.amountToPay === messageAmount;
                      }
                      return matchesPaymentType;
                    });
                    if (individualUserOrder) {
                      return individualUserOrder.userId?._id || individualUserOrder.userId || null;
                    }
                  }
                  return null;
                })(),
                // Restore AI menu images from ref if available
                aiMenuImages:
                  aiMenuImagesRef.current.get(incoming._id || incoming.id) ||
                  incoming.aiMenuImages ||
                  null,
              };

              // Log if we found AI menu images for this message
              if (newMessage.aiMenuImages) {
                console.log(
                  "Restored AI menu images for message:",
                  newMessage._id,
                  newMessage.aiMenuImages
                );
              }

              setChatState((prev) => {
                let newMessages;
                // Check if this is an AI message that should replace a temporary AI message
                const aiUserId = getBeemzAiUserId();
                const incomingSenderId = incoming.senderId?._id || incoming.senderId;
                const isAiMessage =
                  incomingSenderId && String(incomingSenderId) === String(aiUserId);
                
                let aiTempMessageIndex = -1;

                // FIXED: Check for temporary AI messages that need to be replaced
                if (isAiMessage && !isOwnMessage) {
                  const incomingText = incoming.message || "";
                  const incomingTime = new Date(incoming.createdAt).getTime();
                  const timeThreshold = 60000; // 60 seconds threshold for AI messages

                  // Find temporary AI message by matching content and recent timestamp
                  aiTempMessageIndex = prev.messages.findIndex((msg) => {
                    // Check if it's a temporary AI message (either isTemp OR isAiStreaming)
                    if (!msg.isTemp && !msg.isAiStreaming) return false;

                    // Check if it's from AI
                    const msgSenderId = msg.senderId?._id || msg.senderId;
                    const isMsgFromAi =
                      msgSenderId && String(msgSenderId) === String(aiUserId);
                    if (!isMsgFromAi) return false;

                    // For temp AI messages with "..." text, match any recent AI message
                    // For temp messages with actual text, match by content
                    const msgText = msg.text || "";
                    const isTempWithDots = msgText === "..." || msgText.trim() === "";
                    
                    let contentMatches = false;
                    if (isTempWithDots) {
                      // If temp message is just "...", match any recent AI message
                      contentMatches = true;
                    } else {
                      // Match by content similarity
                      const cleanedMsgText = msgText.replace("...", "").trim();
                      const cleanedIncomingText = incomingText.trim();
                      
                      contentMatches =
                        cleanedIncomingText === cleanedMsgText ||
                        cleanedIncomingText.includes(cleanedMsgText) ||
                        cleanedMsgText.includes(cleanedIncomingText);
                    }

                    // Match by recent timestamp (more lenient for temp messages)
                    const msgTime = new Date(msg.timestamp || msg.createdAt || Date.now()).getTime();
                    const timeDiff = Math.abs(incomingTime - msgTime);
                    // Use longer threshold for temp messages with dots
                    const effectiveThreshold = isTempWithDots ? timeThreshold * 2 : timeThreshold;

                    return contentMatches && timeDiff < effectiveThreshold;
                  });
                }

                // FIXED: Replace temporary message for both own messages and AI messages
                const finalTempIndex = isOwnMessage ? tempMessageIndex : aiTempMessageIndex;
                if (finalTempIndex !== -1 && (isOwnMessage || isAiMessage)) {
                  // Replace the temporary message with the real message
                  newMessages = [...prev.messages];
                  newMessages[finalTempIndex] = {
                    ...newMessage,
                    isTemp: false, // Mark as permanent
                    isAiStreaming: false, // Ensure AI streaming flag is cleared
                    // Preserve aiMenuImages if it was set in temp message
                    aiMenuImages: prev.messages[finalTempIndex]?.aiMenuImages || newMessage.aiMenuImages || null,
                    // FIXED: Explicitly preserve deliveredTo and readBy from newMessage to ensure they're not lost
                    deliveredTo: Array.isArray(newMessage.deliveredTo) ? newMessage.deliveredTo : [],
                    readBy: Array.isArray(newMessage.readBy) ? newMessage.readBy : [],
                  };

                  // Also remove from pending messages since it's no longer temporary
                  if (newMessage._id) {
                    pendingTempMessages.current.delete(newMessage._id);
                  }
                } else {
                  // No temp message found - check for duplicates before adding
                  let isDuplicate = false;

                  if (isOwnMessage) {
                    // For own messages, check if we already have a temporary message with same content
                    isDuplicate = prev.messages.some(
                      (msg) =>
                        msg.isTemp &&
                        msg.isOwn &&
                        msg.text === newMessage.text &&
                        msg.type === newMessage.type
                    );
                  } else if (isAiMessage) {
                    // For AI messages, check for duplicates by ID or content
                    isDuplicate = prev.messages.some((msg) => {
                      const msgSenderId = msg.senderId?._id || msg.senderId;
                      const isMsgFromAi =
                        msgSenderId && String(msgSenderId) === String(aiUserId);
                      if (!isMsgFromAi) return false;

                      // Check by ID first
                      if (msg._id === newMessage._id || msg.id === newMessage._id) {
                        return true;
                      }

                      // Check by content if no ID match (for temp messages that might have been updated)
                      const msgText = (msg.text || "").trim();
                      const incomingTextTrimmed = (incoming.message || "").trim();
                      
                      // Only consider it a duplicate if content matches AND it's not a temp message
                      // (temp messages should be replaced, not treated as duplicates)
                      return msgText === incomingTextTrimmed && !msg.isTemp && !msg.isAiStreaming;
                    });
                  } else {
                    // For other users' messages, use ID-based duplicate checking only
                    isDuplicate = prev.messages.some(
                      (msg) =>
                        msg.id === newMessage.id || msg._id === newMessage._id
                    );
                  }

                  if (!isDuplicate) {
                    newMessages = [...prev.messages, newMessage];
                  } else {
                    // FIXED: If duplicate found, update existing message with new deliveredTo and readBy data
                    newMessages = prev.messages.map((msg) => {
                      // Check if this is the duplicate message by ID
                      if (msg._id === newMessage._id || msg.id === newMessage._id) {
                        return {
                          ...msg,
                          // Update deliveredTo and readBy with latest data from server
                          deliveredTo: Array.isArray(newMessage.deliveredTo) && newMessage.deliveredTo.length > 0
                            ? newMessage.deliveredTo
                            : (Array.isArray(msg.deliveredTo) ? msg.deliveredTo : []),
                          readBy: Array.isArray(newMessage.readBy) && newMessage.readBy.length > 0
                            ? newMessage.readBy
                            : (Array.isArray(msg.readBy) ? msg.readBy : []),
                        };
                      }
                      return msg;
                    });
                  }
                }

                return { ...prev, messages: newMessages };
              });

              cleanupOrphanedTempMessages();

              // Additional cleanup: Remove any temporary messages with the same content to prevent duplicates
              // BUT only if we haven't already replaced them AND they're very recent
              if (isOwnMessage && tempMessageIndex === -1) {
                // Only clean up if we didn't find a temporary message to replace
                // This prevents removing messages that were just added
                // Only clean up very recent temporary messages (within 2 seconds)
                setTimeout(() => {
                  setChatState((prev) => {
                    const now = Date.now();
                    const cleanedMessages = prev.messages.filter((msg) => {
                      if (
                        msg.isTemp &&
                        msg.isOwn &&
                        msg.text === incoming.message
                      ) {
                        // Only remove if the temporary message is very recent (within 2 seconds)
                        const msgTime = new Date(msg.timestamp).getTime();
                        const timeDiff = now - msgTime;

                        if (timeDiff < 2000) {
                          // 2 seconds threshold
                          return false; // Remove this recent temporary message
                        }
                      }
                      return true; // Keep other messages
                    });

                    if (cleanedMessages.length !== prev.messages.length) {
                    }

                    return { ...prev, messages: cleanedMessages };
                  });
                }, 2000); // 2 second delay to prevent immediate removal
              }
              // Immediate check for any matching temporary messages
              // BUT with a small delay to prevent race conditions
              setTimeout(() => {
                const immediateTempMessages = chatState.messages.filter(
                  (msg) =>
                    msg.isTemp &&
                    msg.isOwn &&
                    msg.text === incoming.message &&
                    msg.type === incoming.type
                );

                if (immediateTempMessages.length > 0) {
                  setChatState((prev) => {
                    const newMessages = prev.messages.map((msg) => {
                      if (
                        immediateTempMessages.some(
                          (tempMsg) => tempMsg.id === msg.id
                        )
                      ) {
                        return {
                          ...msg,
                          isTemp: false,
                          id: incoming._id || incoming.id,
                          _id: incoming._id || incoming.id,
                        };
                      }
                      return msg;
                    });
                    return { ...prev, messages: newMessages };
                  });
                }
              }, 500); // 500ms delay to prevent race conditions

              // Try immediate replacement for quick messages (single)
              if (isOwnMessage) {
                immediateReplaceTemporaryMessage(incoming);
              }

              // Additional check: try to replace any remaining temporary messages by content (immediate)
              const remainingTempMessages = chatState.messages.filter(
                (msg) => msg.isTemp && msg.isOwn
              );
              if (remainingTempMessages.length > 0) {
                remainingTempMessages.forEach((tempMsg) => {
                  if (
                    tempMsg.text === incoming.message &&
                    tempMsg.type === incoming.type
                  ) {
                    setChatState((prev) => {
                      const newMessages = prev.messages.map((msg) => {
                        if (msg.id === tempMsg.id) {
                          return {
                            ...msg,
                            isTemp: false,
                            id: incoming._id || incoming.id,
                            _id: incoming._id || incoming.id,
                          };
                        }
                        return msg;
                      });
                      return { ...prev, messages: newMessages };
                    });
                  }
                });
              }

              // For rapid messages, try additional immediate replacement strategies (single)
              if (rapidMessageTracker.current.isRapidMode && isOwnMessage) {
                // Try to find any temporary message with matching content and very recent timestamp
                const rapidTempMessages = chatState.messages.filter(
                  (msg) =>
                    msg.isTemp &&
                    msg.isOwn &&
                    msg.text === incoming.message &&
                    Date.now() - new Date(msg.timestamp).getTime() < 3000 // Within 3 seconds
                );

                if (rapidTempMessages.length > 0) {
                  rapidTempMessages.forEach((tempMsg) => {
                    setChatState((prev) => {
                      const newMessages = prev.messages.map((msg) => {
                        if (msg.id === tempMsg.id) {
                          return {
                            ...msg,
                            isTemp: false,
                            id: incoming._id || incoming.id,
                            _id: incoming._id || incoming.id,
                          };
                        }
                        return msg;
                      });
                      return { ...prev, messages: newMessages };
                    });
                  });
                }
              }

              // For own messages that couldn't be replaced immediately, retry after a short delay
              if (isOwnMessage && tempMessageIndex === -1) {
                const incomingId = incoming._id || incoming.id;

                // Try multiple retry strategies with shorter delays for quick messages
                const retryStrategies = [
                  // Strategy 1: Immediate retry (0ms delay)
                  () => {
                    const retryIndex = chatState.messages.findIndex(
                      (msg) => msg.isTemp && msg.isOwn && msg._id === incomingId
                    );

                    if (retryIndex !== -1) {
                      setChatState((prev) => {
                        const newMessages = [...prev.messages];
                        newMessages[retryIndex] = {
                          ...newMessages[retryIndex],
                          isTemp: false,
                          id: incomingId,
                          _id: incomingId,
                        };

                        // Remove from pending messages
                        pendingTempMessages.current.delete(incomingId);

                        return { ...prev, messages: newMessages };
                      });
                    }
                  },

                  // Strategy 2: Micro delay (5ms) and try content matching
                  () => {
                    setTimeout(() => {
                      const retryIndex = chatState.messages.findIndex((msg) => {
                        if (!msg.isTemp || !msg.isOwn) return false;
                        return (
                          msg.text === incoming.message &&
                          msg.type === incoming.type
                        );
                      });

                      if (retryIndex !== -1) {
                        setChatState((prev) => {
                          const newMessages = [...prev.messages];
                          newMessages[retryIndex] = {
                            ...newMessages[retryIndex],
                            isTemp: false,
                            id: incomingId,
                            _id: incomingId,
                          };

                          // Remove from pending messages
                          pendingTempMessages.current.delete(incomingId);

                          return { ...prev, messages: newMessages };
                        });
                      }
                    }, 5); // Reduced delay for quick messages
                  },

                  // Strategy 3: Minimal delay (20ms) and try any temporary message
                  () => {
                    setTimeout(() => {
                      const retryIndex = chatState.messages.findIndex((msg) => {
                        if (!msg.isTemp || !msg.isOwn) return false;
                        return msg.text === incoming.message;
                      });

                      if (retryIndex !== -1) {
                        setChatState((prev) => {
                          const newMessages = [...prev.messages];
                          newMessages[retryIndex] = {
                            ...newMessages[retryIndex],
                            isTemp: false,
                            id: incomingId,
                            _id: incomingId,
                          };

                          // Remove from pending messages
                          pendingTempMessages.current.delete(incomingId);

                          return { ...prev, messages: newMessages };
                        });
                      }
                    }, 20); // Reduced delay for quick messages
                  },
                ];

                // Execute all retry strategies
                retryStrategies.forEach((strategy, index) => {
                  strategy();
                });

                // Final fallback: try manual replacement by content (immediate)
                replaceTemporaryMessageByContent(incoming);
              }

              // mark message as read if it's from another user
              if (!isOwnIncoming) {
                markAsRead(conversationId);
              }
            } else {
              // Message is for different conversation - show notification

              // only show notification for messages from other users
              if (!isOwnIncoming) {
                const senderName =
                  incoming.senderId?.fullName || "unknown user";
                const messagePreview =
                  incoming.message?.length > 30
                    ? incoming.message.substring(0, 30) + "..."
                    : incoming.message || "sent a message";

                toast(`${senderName}: ${messagePreview}`, {
                  duration: 4000,
                  icon: "💬",
                  style: {
                    background: "#7C3AED",
                    color: "#FFFFFF",
                  },
                });
              }
            }
          } else {
            // Try to extract message information from various possible structures
            const messageData = data.message || data;
            if (messageData && (messageData._id || messageData.id)) {
              // Process as a single message
              const incoming = messageData;
              // AI re-enable flow handling
              handleAiReenableOnIncoming(incoming);
              const attachments = Array.isArray(incoming.attachments)
                ? incoming.attachments
                : [];

              const senderIdStr = String(
                incoming.senderId?._id || incoming.senderId || ""
              );
              const currentUserIdStr = String(currentUserId || "");
              const isOwnIncoming = senderIdStr === currentUserIdStr;

              // For fallback, assume it's for the current conversation if we can't determine otherwise
              const isCurrentConversation = true;

              if (isCurrentConversation) {
                // Enhanced duplicate checking using multiple methods
                const messageId = incoming._id || incoming.id;

                // IMPROVED: Check if message with this _id already exists to prevent duplicates
                // But allow replacement of temporary messages for own messages
                const isOwnMessage = isOwnIncoming;

                // Check for true duplicates (same message ID)
                if (isMessageDuplicate(messageId, chatState.messages)) {
                  return; // Skip this message entirely
                }

                // Check for true duplicates (same text sent at exact same time - prevents network duplicates)
                if (isTrueDuplicate(incoming, chatState.messages)) {
                  return; // Skip this message entirely
                }

                // Mark this message as processed
                processedMessageIds.current.add(messageId);

                const newMessage = {
                  id: incoming._id || Date.now(),
                  _id: incoming._id, // Ensure _id is preserved for future matching
                  type: incoming.type || "text",
                  text: incoming.message || incoming.text || "",
                  attachments,
                  location: incoming.location || null,
                  latLong: incoming.latLong || null,
                  senderId: incoming.senderId,
                  timestamp: new Date(
                    incoming.createdAt || Date.now()
                  ).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                  isOwn: isOwnIncoming,
                  isTemp: false,
                  reactions: incoming.reactions || [], // Preserve reactions from backend
                  // Preserve important message status fields from backend
                  isDeletedForMe: incoming.isDeletedForMe || [],
                  isDeletedForEveryone: incoming.isDeletedForEveryone || false,
                  isEdited: incoming.isEdited || false,
                  isForwarded: incoming.isForwarded || false,
                  forwardedFrom: incoming.forwardedFrom || null,
                  forwardedBy: incoming.forwardedBy || [],
                  editedBy: incoming.editedBy || [],
                  // FIXED: Ensure deliveredTo and readBy are always arrays with proper defensive checks
                  deliveredTo: Array.isArray(incoming.deliveredTo) ? incoming.deliveredTo : [],
                  readBy: Array.isArray(incoming.readBy) ? incoming.readBy : [],
                  orderId: incoming.orderId || null,
                  amount: incoming.amount || null,
                  createdAt: incoming.createdAt || null,
                  // Preserve initiationFor field for payment_request_upfront messages
                  initiationFor: incoming.initiationFor || null,
                  // Preserve messageFor field for payment_request_individual messages
                  // If not present, derive from order data (user who initiated individual payment)
                  messageFor: (() => {
                    if (incoming.messageFor) {
                      return incoming.messageFor;
                    }
                    // For payment_request_individual messages, derive from order data
                    if (incoming.type === "payment_request_individual" && incoming.orderId) {
                      const orderData = incoming.orderId;
                      const userOrders = orderData?.groupOrder?.userOrders || [];
                      const messageAmount = incoming.amount || 0;
                      // Find user order with individual payment type, approved payment flow, and matching amount
                      const individualUserOrder = userOrders.find((userOrder) => {
                        const paymentFlow = userOrder.paymentFlow;
                        const matchesPaymentType = userOrder.paymentType === "individual" &&
                          paymentFlow?.type === "individual" &&
                          paymentFlow?.status === "approved";
                        // If message has amount, match by amountToPay; otherwise just match payment type
                        if (messageAmount > 0) {
                          return matchesPaymentType && userOrder.amountToPay === messageAmount;
                        }
                        return matchesPaymentType;
                      });
                      if (individualUserOrder) {
                        return individualUserOrder.userId?._id || individualUserOrder.userId || null;
                      }
                    }
                    return null;
                  })(),
                };

                setChatState((prev) => {
                  const updatedMessages = [...prev.messages, newMessage];
                  return { ...prev, messages: updatedMessages };
                });

                // mark message as read if it's from another user
                if (!isOwnIncoming && conversationId) {
                  markAsRead(conversationId);
                }

                // Additional cleanup: Remove any temporary messages with the same content to prevent duplicates
                // BUT only if we haven't already replaced them AND they're very recent
                if (isOwnIncoming) {
                  // For fallback, we don't have tempMessageIndex, so we need to be more careful
                  // Only clean up if we're sure the message was processed AND it's very recent
                  setChatState((prev) => {
                    const now = Date.now();
                    const cleanedMessages = prev.messages.filter((msg) => {
                      if (
                        msg.isTemp &&
                        msg.isOwn &&
                        msg.text === incoming.message
                      ) {
                        // Only remove if the temporary message is very recent (within 2 seconds)
                        const msgTime = new Date(msg.timestamp).getTime();
                        const timeDiff = now - msgTime;

                        if (timeDiff < 2000) {
                          // 2 seconds threshold
                          return false; // Remove this recent temporary message
                        }
                      }
                      return true; // Keep other messages
                    });

                    if (cleanedMessages.length !== prev.messages.length) {
                    }

                    return { ...prev, messages: cleanedMessages };
                  });
                }
              }
            } else {
            }
          }
        }
      );

      // Add listener for messageReceivedSubConversation events (handles sub-conversation messages received on opposite side)
      const messageReceivedSubConversationUnsubscribe = addEventListener(
        "messageReceivedSubConversation",
        (data) => {
          // Check if this listener is still valid
          if (!isMountedRef.current) {
            return;
          }

          // FIXED: Only process messages if a sub-conversation is currently selected
          const currentSubConversationId = selectedSubConversationIdRef.current;
          if (!currentSubConversationId) {
            return;
          }

          // Process the message if we have batch data
          if (data.batch && Array.isArray(data.batch)) {
            for (const batchItem of data.batch) {
              if (batchItem.message) {
                const incoming = batchItem.message;

                // FIXED: Only process messages for the currently selected sub-conversation
                if (batchItem.conversationId !== currentSubConversationId) {
                  continue;
                }

                // Check if this message is from the current user (to avoid showing own messages as received)
                const senderIdStr = String(incoming.senderId?._id || "");
                const currentUserIdStr = String(currentUserId || "");
                const isOwnMessage = senderIdStr === currentUserIdStr;

                // Special message types (order, payment, request) should be processed even if from current user
                // This ensures order messages and other important messages are displayed in sub-conversations
                const isSpecialMessageType =
                  incoming.type === "order" ||
                  incoming.type === "payment" ||
                  incoming.type === "payment-request" ||
                  incoming.type === "payment_request_individual" ||
                  incoming.type === "payment_request_individual_remaining_amount" ||
                  incoming.type === "request";

                if (isOwnMessage && !isSpecialMessageType) {
                  continue; // Use continue instead of return to process other messages
                }

                // FIXED: Check if this is an AI message that should replace a temporary AI message
                const aiUserId = getBeemzAiUserId();
                const incomingSenderId = incoming.senderId?._id || incoming.senderId;
                const isAiMessage =
                  incomingSenderId && String(incomingSenderId) === String(aiUserId);

                // Check for duplicates using functional update to get latest state
                const messageId = incoming._id || incoming.id;

                // Track this message as processed to prevent multiple processing
                if (processedMessageIds.current.has(messageId)) {
                  continue;
                }

                // Use functional update to check duplicates with latest state
                setChatState((prev) => {
                  // FIXED: Check for temporary AI messages that need to be replaced
                  let tempMessageIndex = -1;
                  if (isAiMessage && !isOwnMessage) {
                    const incomingText = incoming.message || "";
                    const incomingTime = new Date(incoming.createdAt).getTime();
                    const timeThreshold = 60000; // 60 seconds threshold for AI messages

                    // Find temporary AI message by matching content and recent timestamp
                    tempMessageIndex = prev.messages.findIndex((msg) => {
                      // Check if it's a temporary AI message (either isTemp OR isAiStreaming)
                      if (!msg.isTemp && !msg.isAiStreaming) return false;

                      // Check if it's from AI
                      const msgSenderId = msg.senderId?._id || msg.senderId;
                      const isMsgFromAi =
                        msgSenderId && String(msgSenderId) === String(aiUserId);
                      if (!isMsgFromAi) return false;

                      // For temp AI messages with "..." text, match any recent AI message
                      // For temp messages with actual text, match by content
                      const msgText = msg.text || "";
                      const isTempWithDots = msgText === "..." || msgText.trim() === "";
                      
                      let contentMatches = false;
                      if (isTempWithDots) {
                        // If temp message is just "...", match any recent AI message
                        contentMatches = true;
                      } else {
                        // Match by content similarity
                        const cleanedMsgText = msgText.replace("...", "").trim();
                        const cleanedIncomingText = incomingText.trim();
                        
                        contentMatches =
                          cleanedIncomingText === cleanedMsgText ||
                          cleanedIncomingText.includes(cleanedMsgText) ||
                          cleanedMsgText.includes(cleanedIncomingText);
                      }

                      // Match by recent timestamp (more lenient for temp messages)
                      const msgTime = new Date(msg.timestamp || msg.createdAt || Date.now()).getTime();
                      const timeDiff = Math.abs(incomingTime - msgTime);
                      // Use longer threshold for temp messages with dots
                      const effectiveThreshold = isTempWithDots ? timeThreshold * 2 : timeThreshold;

                      return contentMatches && timeDiff < effectiveThreshold;
                    });

                    // FIXED: Also check for duplicate AI messages with same content (not just temp ones)
                    if (tempMessageIndex === -1) {
                      const hasDuplicateAiMessage = prev.messages.some((msg) => {
                        const msgSenderId = msg.senderId?._id || msg.senderId;
                        const isMsgFromAi =
                          msgSenderId && String(msgSenderId) === String(aiUserId);
                        if (!isMsgFromAi) return false;

                        // Check if content matches
                        const msgText = (msg.text || "").trim();
                        const incomingTextTrimmed = incomingText.trim();
                        
                        return msgText === incomingTextTrimmed && !msg.isTemp;
                      });

                      if (hasDuplicateAiMessage) {
                        // Skip adding duplicate AI message
                        return prev;
                      }
                    }
                  }

                  // Check for duplicates using current state
                  const isDuplicate = isMessageDuplicate(messageId, prev.messages);

                  if (isDuplicate) {
                    return prev; // No changes if duplicate
                  }

                  // Mark as processed
                  processedMessageIds.current.add(messageId);

                  // Add the message to chat state
                  const newMessage = {
                    id: messageId,
                    _id: messageId,
                    text: incoming.message || incoming.text || "",
                    sender:
                      incoming.senderId?.fullName ||
                      incoming.senderId?.userName ||
                      "unknown",
                    senderId: incoming.senderId,
                    timestamp: new Date(
                      incoming.createdAt || Date.now()
                    ).toISOString(), // Convert to ISO string
                    isOwn: isOwnMessage,
                    isTemp: false,
                    type: incoming.type || "text",
                    attachments: Array.isArray(incoming.attachments)
                      ? incoming.attachments
                      : [],
                    conversationId: batchItem.conversationId,
                    conversationType: batchItem.conversationType,
                    mainConversationId: batchItem.mainConversationId,
                    miniConversationId: incoming.miniConversationId || null,
                    reactions: incoming.reactions || [], // Preserve reactions from backend
                    // include reply preview fields for received sub-conversation messages
                    // FIXED: Only set replyTo fields if replyTo exists and has a valid ID
                    // This prevents messages without replies from incorrectly displaying as replies in sub-conversations
                    // Ensure replyTo has an actual ID value (string or object with _id), not just empty object/null/undefined
                    ...(function() {
                      // extract replyTo ID - can be either an object with _id property or a direct ID value
                      const replyToId = incoming.replyTo?._id || incoming.replyTo;
                      // check if we have a valid replyTo ID (not null, undefined, empty string, or empty object)
                      const hasValidReplyToId = replyToId && 
                        replyToId !== null && 
                        replyToId !== undefined && 
                        replyToId !== "" &&
                        !(typeof replyToId === "object" && Object.keys(replyToId).length === 0);
                      
                      if (hasValidReplyToId) {
                        return {
                          replyTo: incoming.replyTo._id || incoming.replyTo || null,
                          replyToText: incoming.replyTo?.message || null,
                          replyToSender:
                            incoming.replyTo?.senderId?.fullName ||
                            incoming.replyTo?.senderId?.userName ||
                            null,
                          replyToAttachments: Array.isArray(
                            incoming.replyTo?.attachments
                          )
                            ? incoming.replyTo.attachments
                            : [],
                        };
                      } else {
                        // no valid replyTo - explicitly set all reply fields to null/empty
                        return {
                          replyTo: null,
                          replyToText: null,
                          replyToSender: null,
                          replyToAttachments: [],
                        };
                      }
                    })(),
                    // Preserve important message status fields from backend
                    isDeletedForMe: incoming.isDeletedForMe || [],
                    isDeletedForEveryone: incoming.isDeletedForEveryone || false,
                    isEdited: incoming.isEdited || false,
                    isForwarded: incoming.isForwarded || false,
                    forwardedFrom: incoming.forwardedFrom || null,
                    forwardedBy: incoming.forwardedBy || [],
                    editedBy: incoming.editedBy || [],
                    deliveredTo: incoming.deliveredTo || [],
                    readBy: incoming.readBy || [],
                    orderId: incoming.orderId || null,
                    amount: incoming.amount || null,
                    createdAt: incoming.createdAt || null,
                    // Preserve initiationFor field for payment_request_upfront messages
                    initiationFor: incoming.initiationFor || null,
                    // Preserve messageFor field for payment_request_individual messages
                    // If not present, derive from order data (user who initiated individual payment)
                    messageFor: (() => {
                      if (incoming.messageFor) {
                        return incoming.messageFor;
                      }
                      // For payment_request_individual messages, derive from order data
                      if (incoming.type === "payment_request_individual" && incoming.orderId) {
                        const orderData = incoming.orderId;
                        const userOrders = orderData?.groupOrder?.userOrders || [];
                        const messageAmount = incoming.amount || 0;
                        // Find user order with individual payment type, approved payment flow, and matching amount
                        const individualUserOrder = userOrders.find((userOrder) => {
                          const paymentFlow = userOrder.paymentFlow;
                          const matchesPaymentType = userOrder.paymentType === "individual" &&
                            paymentFlow?.type === "individual" &&
                            paymentFlow?.status === "approved";
                          // If message has amount, match by amountToPay; otherwise just match payment type
                          if (messageAmount > 0) {
                            return matchesPaymentType && userOrder.amountToPay === messageAmount;
                          }
                          return matchesPaymentType;
                        });
                        if (individualUserOrder) {
                          return individualUserOrder.userId?._id || individualUserOrder.userId || null;
                        }
                      }
                      return null;
                    })(),
                  };

                  // FIXED: Replace temporary AI message if found, otherwise add new message
                  if (tempMessageIndex !== -1 && isAiMessage) {
                    const updatedMessages = [...prev.messages];
                    updatedMessages[tempMessageIndex] = {
                      ...newMessage,
                      isAiStreaming: false, // Ensure AI streaming flag is cleared
                    };
                    return {
                      ...prev,
                      messages: updatedMessages,
                    };
                  }

                  return {
                    ...prev,
                    messages: [...prev.messages, newMessage],
                  };
                });

                // Update sub-conversations with the new last message
                setSubConversations((prevSubConversations) => {
                  const updatedSubConversations = prevSubConversations.map(
                    (subConv) => {
                      if (subConv._id === batchItem.conversationId) {
                        return {
                          ...subConv,
                          lastMessage: {
                            _id: incoming._id,
                            message: incoming.message,
                            updatedAt: incoming.updatedAt || incoming.createdAt,
                            senderId: incoming.senderId,
                            type: incoming.type || "text",
                          },
                          updatedAt: incoming.updatedAt || incoming.createdAt,
                        };
                      }
                      return subConv;
                    }
                  );

                  return updatedSubConversations;
                });

                // Mark message as read if we're actively viewing this sub-conversation
                if (selectedSubConversationId === batchItem.conversationId) {
                }
              }
            }
          } else {
            // Handle non-batch message format (single message)
            const incoming = data.message || data;
            
            // Check if this message is for the currently selected sub-conversation
            const messageConversationId = incoming.conversationId || data.conversationId;
            if (messageConversationId !== currentSubConversationId) {
              return;
            }
            
            // Check if this message is from the current user (to avoid showing own messages as received)
            const senderIdStr = String(incoming.senderId?._id || incoming.senderId || "");
            const currentUserIdStr = String(currentUserId || "");
            const isOwnMessage = senderIdStr === currentUserIdStr;
            
            // Special message types (order, payment, request, add-remove) should be processed even if from current user
            // add-remove type messages are notifications about notes/mini conversations
            const isSpecialMessageType =
              incoming.type === "order" ||
              incoming.type === "payment" ||
              incoming.type === "payment-request" ||
              incoming.type === "payment_request_individual" ||
              incoming.type === "payment_request_individual_remaining_amount" ||
              incoming.type === "request" ||
              incoming.type === "add-remove";
            
            if (isOwnMessage && !isSpecialMessageType) {
              return;
            }
            
            // Check for duplicates
            const messageId = incoming._id || incoming.id;
            
            // Track this message as processed to prevent multiple processing
            if (processedMessageIds.current.has(messageId)) {
              return;
            }
            
            // Use functional update to check duplicates with latest state
            setChatState((prev) => {
              // Check for duplicates using current state
              const isDuplicate = isMessageDuplicate(messageId, prev.messages);
              
              if (isDuplicate) {
                return prev; // No changes if duplicate
              }
              
              // Mark as processed
              processedMessageIds.current.add(messageId);
              
              // Add the message to chat state
              const newMessage = {
                id: messageId,
                _id: messageId,
                text: incoming.message || incoming.text || "",
                sender:
                  incoming.senderId?.fullName ||
                  incoming.senderId?.userName ||
                  "unknown",
                senderId: incoming.senderId,
                timestamp: new Date(
                  incoming.createdAt || Date.now()
                ).toISOString(),
                isOwn: isOwnMessage,
                isTemp: false,
                type: incoming.type || "text",
                attachments: Array.isArray(incoming.attachments)
                  ? incoming.attachments
                  : [],
                conversationId: messageConversationId,
                conversationType: incoming.conversationType || data.conversationType || "SubConversation",
                mainConversationId: incoming.mainConversationId || data.mainConversationId,
                miniConversationId: incoming.miniConversationId || null,
                reactions: incoming.reactions || [],
                // FIXED: Only set replyTo fields if replyTo exists and has a valid ID
                // This prevents messages without replies from incorrectly displaying as replies in sub-conversations
                // Ensure replyTo has an actual ID value (string or object with _id), not just empty object/null/undefined
                ...(function() {
                  // extract replyTo ID - can be either an object with _id property or a direct ID value
                  const replyToId = incoming.replyTo?._id || incoming.replyTo;
                  // check if we have a valid replyTo ID (not null, undefined, empty string, or empty object)
                  const hasValidReplyToId = replyToId && 
                    replyToId !== null && 
                    replyToId !== undefined && 
                    replyToId !== "" &&
                    !(typeof replyToId === "object" && Object.keys(replyToId).length === 0);
                  
                  if (hasValidReplyToId) {
                    return {
                      replyTo: incoming.replyTo._id || incoming.replyTo || null,
                      replyToText: incoming.replyTo?.message || null,
                      replyToSender:
                        incoming.replyTo?.senderId?.fullName ||
                        incoming.replyTo?.senderId?.userName ||
                        null,
                      replyToAttachments: Array.isArray(
                        incoming.replyTo?.attachments
                      )
                        ? incoming.replyTo.attachments
                        : [],
                    };
                  } else {
                    // no valid replyTo - explicitly set all reply fields to null/empty
                    return {
                      replyTo: null,
                      replyToText: null,
                      replyToSender: null,
                      replyToAttachments: [],
                    };
                  }
                })(),
                isDeletedForMe: incoming.isDeletedForMe || [],
                isDeletedForEveryone: incoming.isDeletedForEveryone || false,
                isEdited: incoming.isEdited || false,
                isForwarded: incoming.isForwarded || false,
                forwardedFrom: incoming.forwardedFrom || null,
                forwardedBy: incoming.forwardedBy || [],
                editedBy: incoming.editedBy || [],
                deliveredTo: incoming.deliveredTo || [],
                readBy: incoming.readBy || [],
                orderId: incoming.orderId || null,
                amount: incoming.amount || null,
                createdAt: incoming.createdAt || null,
                // Preserve initiationFor field for payment_request_upfront messages
                initiationFor: incoming.initiationFor || null,
                // Preserve messageFor field for payment_request_individual messages
                // If not present, derive from order data (user who initiated individual payment)
                messageFor: (() => {
                  if (incoming.messageFor) {
                    return incoming.messageFor;
                  }
                  // For payment_request_individual messages, derive from order data
                  if (incoming.type === "payment_request_individual" && incoming.orderId) {
                    const orderData = incoming.orderId;
                    const userOrders = orderData?.groupOrder?.userOrders || [];
                    const messageAmount = incoming.amount || 0;
                    // Find user order with individual payment type, approved payment flow, and matching amount
                    const individualUserOrder = userOrders.find((userOrder) => {
                      const paymentFlow = userOrder.paymentFlow;
                      const matchesPaymentType = userOrder.paymentType === "individual" &&
                        paymentFlow?.type === "individual" &&
                        paymentFlow?.status === "approved";
                      // If message has amount, match by amountToPay; otherwise just match payment type
                      if (messageAmount > 0) {
                        return matchesPaymentType && userOrder.amountToPay === messageAmount;
                      }
                      return matchesPaymentType;
                    });
                    if (individualUserOrder) {
                      return individualUserOrder.userId?._id || individualUserOrder.userId || null;
                    }
                  }
                  return null;
                })(),
              };
              
              return {
                ...prev,
                messages: [...prev.messages, newMessage],
              };
            });
            
            // Update sub-conversations with the new last message
            setSubConversations((prevSubConversations) => {
              const updatedSubConversations = prevSubConversations.map(
                (subConv) => {
                  if (subConv._id === messageConversationId) {
                    return {
                      ...subConv,
                      lastMessage: {
                        _id: incoming._id,
                        message: incoming.message || incoming.text || "",
                        updatedAt: incoming.updatedAt || incoming.createdAt,
                        senderId: incoming.senderId,
                        type: incoming.type || "text",
                      },
                      updatedAt: incoming.updatedAt || incoming.createdAt,
                    };
                  }
                  return subConv;
                }
              );
              
              return updatedSubConversations;
            });
          }
        }
      );

      // Add listener for conversationUpdate events (handles updateConversation socket events)
      const conversationUpdateUnsubscribe = addEventListener(
        "conversationUpdate",
        (data) => {
          // Check if this listener is still valid
          if (!isMountedRef.current) {
            return;
          }

          const currentConversationId = selectedContact?.id;

          // Handle direct updateConversation format (when order status changes)
          if (data.updateConversation) {
            const updatedConversation = data.updateConversation;
            // Check if this update is for the current conversation
            if (
              currentConversationId &&
              String(updatedConversation._id) === String(currentConversationId)
            ) {
              // Update conversationInfo state with the new conversation data
              // This ensures showMenu and other properties are updated in real-time
              setConversationInfo((prev) => {
                if (prev && prev._id === updatedConversation._id) {
                  // Merge the updated conversation data with existing state
                  return { ...prev, ...updatedConversation };
                }
                return updatedConversation;
              });
            }
          }

          // Process conversationUpdate data (same logic as messageReceived)
          if (data.batch && Array.isArray(data.batch)) {
            for (const batchItem of data.batch) {
              // Handle updateConversation in batch format
              if (batchItem.updateConversation) {
                const updatedConversation = batchItem.updateConversation;
                // Check if this update is for the current conversation
                if (
                  currentConversationId &&
                  String(updatedConversation._id) === String(currentConversationId)
                ) {
                  // Update conversationInfo state with the new conversation data
                  // This ensures showMenu and other properties are updated in real-time
                  setConversationInfo((prev) => {
                    if (prev && prev._id === updatedConversation._id) {
                      // Merge the updated conversation data with existing state
                      return { ...prev, ...updatedConversation };
                    }
                    return updatedConversation;
                  });
                }
              }

              if (batchItem.message) {
                const incoming = batchItem.message;

                const attachments = Array.isArray(incoming.attachments)
                  ? incoming.attachments
                  : [];

                // ensure both values are strings for comparison to avoid type mismatch issues
                const senderIdStr = String(incoming.senderId?._id || "");
                const currentUserIdStr = String(currentUserId || "");
                const isOwnIncoming = senderIdStr === currentUserIdStr;

                // check if this message is for the current conversation
                let isCurrentConversation;
                // Always check if message is for current user first (more reliable)
                const senderId = incoming.senderId?._id || incoming.senderId;
                const receiverId = batchItem.receiverId || batchItem.senderId;

                // Check if message involves current user (either as sender or receiver)
                isCurrentConversation =
                  String(senderId) === String(currentUserId) ||
                  String(receiverId) === String(currentUserId);

                // Additional check: if we have a conversation ID, also verify it matches
                if (currentConversationId && batchItem.conversationId) {
                  compareConversationIds(
                    batchItem.conversationId,
                    currentConversationId
                  );
                }

                if (isCurrentConversation) {
                  // Process the message (same logic as messageReceived)
                  const messageId = incoming._id || incoming.id;

                  // Check for true duplicates (same message ID)
                  if (isMessageDuplicate(messageId, chatState.messages)) {
                    continue; // Skip this message entirely
                  }

                  // Check for true duplicates (same text sent at exact same time - prevents network duplicates)
                  if (isTrueDuplicate(incoming, chatState.messages)) {
                    continue; // Skip this message entirely
                  }

                  // Debug: Log the incoming conversationUpdate message data for read receipt debugging

                  const newMessage = {
                    id: incoming._id || Date.now(),
                    _id: incoming._id,
                    type: incoming.type || "text",
                    text: incoming.message || "",
                    attachments,
                    location: incoming.location || null,
                    latLong: incoming.latLong || null,
                    senderId: incoming.senderId,
                    timestamp: new Date(incoming.createdAt).toLocaleTimeString(
                      [],
                      {
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    ),
                    isOwn: isOwnIncoming,
                    isTemp: false,
                    reactions: incoming.reactions || [], // Preserve reactions from backend
                    // Preserve important message status fields from backend
                    isDeletedForMe: incoming.isDeletedForMe || [],
                    isDeletedForEveryone:
                      incoming.isDeletedForEveryone || false,
                    isEdited: incoming.isEdited || false,
                    isForwarded: incoming.isForwarded || false,
                    forwardedFrom: incoming.forwardedFrom || null,
                    forwardedBy: incoming.forwardedBy || [],
                    editedBy: incoming.editedBy || [],
                    deliveredTo: incoming.deliveredTo || [],
                    readBy: incoming.readBy || [],
                    orderId: incoming.orderId || null,
                    amount: incoming.amount || null,
                    createdAt: incoming.createdAt || null,
                  };

                  setChatState((prev) => {
                    const updatedMessages = [...prev.messages, newMessage];
                    return { ...prev, messages: updatedMessages };
                  });

                  // mark message as read if it's from another user
                  if (!isOwnIncoming && conversationId) {
                    markAsRead(conversationId);
                  }

                  // Additional cleanup: Remove any temporary messages with the same content to prevent duplicates
                  // BUT only if we haven't already replaced them AND they're very recent
                  if (isOwnIncoming) {
                    // For conversationUpdate, we don't have tempMessageIndex, so we need to be more careful
                    // Only clean up if we're sure the message was processed AND it's very recent
                    setChatState((prev) => {
                      const now = Date.now();
                      const cleanedMessages = prev.messages.filter((msg) => {
                        if (
                          msg.isTemp &&
                          msg.isOwn &&
                          msg.text === incoming.message
                        ) {
                          // Only remove if the temporary message is very recent (within 2 seconds)
                          const msgTime = new Date(msg.timestamp).getTime();
                          const timeDiff = now - msgTime;

                          if (timeDiff < 2000) {
                            // 2 seconds threshold
                            return false; // Remove this recent temporary message
                          }
                        }
                        return true; // Keep other messages
                      });

                      if (cleanedMessages.length !== prev.messages.length) {
                      }

                      return { ...prev, messages: cleanedMessages };
                    });
                  }
                } else {
                }
              }
            }
          }
        }
      );

      const typingUnsubscribe = addEventListener("typing", (data) => {
        if (data.conversationId === conversationId) {
          if (data.isTyping === 1) {
            setChatState((prev) => ({
              ...prev,
              typingUsers: [
                ...prev.typingUsers.filter((id) => id !== data.userId),
                data.userId,
              ],
            }));
          } else {
            setChatState((prev) => ({
              ...prev,
              typingUsers: prev.typingUsers.filter((id) => id !== data.userId),
            }));
          }
        }
      });

      const messagesUnsubscribe = addEventListener(
        "messagesFetched",
        (data) => {
          if (data && data.messages) {
            const transformedMessages = data.messages.map((msg) => {
              // ensure both values are strings for comparison to avoid type mismatch issues
              const senderIdStr = String(msg.senderId?._id || "");
              const currentUserIdStr = String(currentUserId || "");
              const isOwnMessage = senderIdStr === currentUserIdStr;

              // Pass all backend fields directly to MessageBubble without filtering
              return {
                ...msg, // Spread all backend fields
                id: msg._id, // Add id field for React key
                isOwn: isOwnMessage, // Add isOwn field for message alignment
                isTemp: false, // Ensure fetched messages are not marked as temporary
                // Add timestamp for display (keep original createdAt for backend compatibility)
                timestamp: new Date(msg.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                // Map message field to text for backward compatibility
                text: msg.message || msg.text || "",
              };
            });

            // Check if this is pagination (has more messages) or initial load
            if (data.hasMore !== undefined) {
              setChatState((prev) => ({
                ...prev,
                hasMoreMessages: data.hasMore,
              }));
            }

            // Use ref to avoid stale closures from hooks
            const isPagination = paginationInFlightRef.current;

            if (isPagination) {
              // Backend returns oldest-first; since these are older than current top, put them before current list
              // FIXED: Don't filter messages - just add them to the beginning to prevent losing messages
              setChatState((prev) => {
                // Simply prepend new messages to existing ones without filtering
                // This prevents the flickering and message loss issues
                const newMessages = [...transformedMessages, ...prev.messages];

                // FIXED: Preserve scroll position after pagination
                setTimeout(() => {
                  if (messageContainerRef.current) {
                    const newScrollHeight =
                      messageContainerRef.current.scrollHeight;
                    const oldScrollHeight = prev.messages.length * 100; // Approximate height per message
                    const scrollDiff = newScrollHeight - oldScrollHeight;
                    messageContainerRef.current.scrollTop = scrollDiff;
                  }
                }, 100);

                return { ...prev, messages: newMessages };
              });
            } else {
              // This is initial load - replace all messages
              setChatState((prev) => ({
                ...prev,
                messages: transformedMessages,
              }));
            }

            // Reset loading state after processing
            paginationInFlightRef.current = false;
            setChatState((prev) => ({ ...prev, isLoadingMore: false }));
          }
        }
      );

      // Add listener for message delivery confirmation
      const messagesDeliveredUnsubscribe = addEventListener(
        "messagesDelivered",
        (data) => {
          if (data && data.messageIds && Array.isArray(data.messageIds)) {
            // Update delivered messages to show delivery confirmation
            setChatState((prev) => {
              const updatedMessages = prev.messages.map((msg) => {
                if (
                  data.messageIds.includes(msg._id) ||
                  data.messageIds.includes(msg.id)
                ) {
                  // Add current user to deliveredTo array if not already present
                  const currentUserId =
                    typeof window !== "undefined"
                      ? window.localStorage.getItem(
                          authConfig.storageUserIdKeyName
                        )
                      : null;

                  const updatedDeliveredTo = Array.isArray(msg.deliveredTo)
                    ? [...msg.deliveredTo]
                    : [];

                  // Add current user to deliveredTo if not already there
                  if (
                    currentUserId &&
                    !updatedDeliveredTo.some(
                      (delivery) => delivery.userId === currentUserId
                    )
                  ) {
                    updatedDeliveredTo.push({
                      userId: currentUserId,
                      timestamp: new Date().toISOString(),
                      _id: Date.now().toString(), // temporary ID
                    });
                  }

                  return {
                    ...msg,
                    isDelivered: true,
                    deliveredTo: updatedDeliveredTo,
                    // Remove temporary status if it was a temporary message
                    isTemp: false,
                  };
                }
                return msg;
              });

              return { ...prev, messages: updatedMessages };
            });
          }
        }
      );

      // Add listener for message read confirmation
      const messagesReadUnsubscribe = addEventListener(
        "messagesRead",
        (data) => {
          if (data && data.messageIds && Array.isArray(data.messageIds)) {
            // Update read messages to show read confirmation
            setChatState((prev) => {
              const updatedMessages = prev.messages.map((msg) => {
                if (
                  data.messageIds.includes(msg._id) ||
                  data.messageIds.includes(msg.id)
                ) {
                  // Add current user to readBy array if not already present
                  const currentUserId =
                    typeof window !== "undefined"
                      ? window.localStorage.getItem(
                          authConfig.storageUserIdKeyName
                        )
                      : null;

                  const updatedReadBy = Array.isArray(msg.readBy)
                    ? [...msg.readBy]
                    : [];

                  // Add current user to readBy if not already there
                  if (
                    currentUserId &&
                    !updatedReadBy.some((read) => read.userId === currentUserId)
                  ) {
                    updatedReadBy.push({
                      userId: currentUserId,
                      timestamp: new Date().toISOString(),
                      _id: Date.now().toString(), // temporary ID
                    });
                  }

                  // Also ensure deliveredTo is updated for read messages
                  const updatedDeliveredTo = Array.isArray(msg.deliveredTo)
                    ? [...msg.deliveredTo]
                    : [];

                  if (
                    currentUserId &&
                    !updatedDeliveredTo.some(
                      (delivery) => delivery.userId === currentUserId
                    )
                  ) {
                    updatedDeliveredTo.push({
                      userId: currentUserId,
                      timestamp: new Date().toISOString(),
                      _id: Date.now().toString(), // temporary ID
                    });
                  }

                  return {
                    ...msg,
                    isRead: true,
                    isDelivered: true, // If read, it must be delivered
                    readBy: updatedReadBy,
                    deliveredTo: updatedDeliveredTo,
                    isTemp: false, // Remove temporary status
                  };
                }
                return msg;
              });

              return { ...prev, messages: updatedMessages };
            });
          }
        }
      );

      // Add listener for message edited events
      const messageEditedUnsubscribe = addEventListener(
        "messageEdited",
        (data) => {
          // console.log("messageEditedUnsubscribe----->", data);
          // Check if this listener is still valid
          if (
            !isMountedRef.current ||
            currentConversationRef.current !== conversationId
          ) {
            return;
          }

          if (data && data.message) {
            const editedMessage = data.message;

            // Find and replace the message based on _id
            setChatState((prev) => {
              const messageIndex = prev.messages.findIndex((msg) => {
                const matches =
                  msg._id === editedMessage._id || msg.id === editedMessage._id;
                if (matches) {
                }
                return matches;
              });

              if (messageIndex !== -1) {
                const oldMessage = prev.messages[messageIndex];

                // FIXED: Check if this is actually a text edit or just a reaction update
                // If the text content hasn't changed, it's likely just a reaction update
                const isTextEdit = oldMessage.text !== editedMessage.message;
                const hasReactions =
                  editedMessage.reactions && editedMessage.reactions.length > 0;

                // Create updated message object with enhanced data from server
                const updatedMessage = {
                  ...oldMessage, // Preserve all existing properties
                  // Update core message content only if it's actually a text edit
                  text: isTextEdit
                    ? editedMessage.message ||
                      editedMessage.text ||
                      oldMessage.text
                    : oldMessage.text,
                  type: editedMessage.type || oldMessage.type,
                  // FIXED: Only set isEdited to true if it's actually a text edit, not a reaction update
                  isEdited: isTextEdit ? true : oldMessage.isEdited,
                  editedAt: isTextEdit
                    ? editedMessage.updatedAt || new Date().toISOString()
                    : oldMessage.editedAt,
                  editedBy: isTextEdit
                    ? editedMessage.editedBy || []
                    : oldMessage.editedBy,
                  // Update server-generated properties
                  _id: editedMessage._id,
                  id: editedMessage._id,
                  // Update attachments if provided
                  attachments:
                    editedMessage.attachments || oldMessage.attachments,
                  // Update location data if provided
                  location: editedMessage.location || oldMessage.location,
                  latLong: editedMessage.latLong || oldMessage.latLong,
                  // Update sender information if provided
                  senderId: editedMessage.senderId || oldMessage.senderId,
                  sender:
                    editedMessage.senderId?.fullName ||
                    editedMessage.senderId?.userName ||
                    oldMessage.sender,
                  // Update reactions from server
                  reactions: editedMessage.reactions || oldMessage.reactions,
                  // Update read/delivery status from server
                  readBy: editedMessage.readBy || oldMessage.readBy,
                  deliveredTo:
                    editedMessage.deliveredTo || oldMessage.deliveredTo,
                  // Update other server properties
                  replyTo: editedMessage.replyTo || oldMessage.replyTo,
                  repliedBy: editedMessage.repliedBy || oldMessage.repliedBy,
                  forwardedBy:
                    editedMessage.forwardedBy || oldMessage.forwardedBy,
                  isForwarded:
                    editedMessage.isForwarded || oldMessage.isForwarded,
                  isDeletedForMe:
                    editedMessage.isDeletedForMe || oldMessage.isDeletedForMe,
                  isDeletedForEveryone:
                    editedMessage.isDeletedForEveryone ||
                    oldMessage.isDeletedForEveryone,
                  // Keep original timestamp and ownership
                  timestamp: oldMessage.timestamp,
                  // Update buttonDisabled - server sends it at root level (data.buttonDisabled) or inside message
                  buttonDisabled: data.buttonDisabled !== undefined 
                    ? data.buttonDisabled 
                    : (editedMessage.buttonDisabled !== undefined 
                      ? editedMessage.buttonDisabled 
                      : oldMessage.buttonDisabled),
                  isOwn: oldMessage.isOwn,
                  isTemp: false, // Ensure it's not marked as temporary
                  orderId: editedMessage.orderId || oldMessage.orderId,
                  createdAt: editedMessage.createdAt || oldMessage.createdAt,
                };

                // Update the message in the array
                const newMessages = [...prev.messages];
                newMessages[messageIndex] = updatedMessage;

                return { ...prev, messages: newMessages };
              } else {
                return prev; // No changes if message not found
              }
            });
          } else {
          }
        }
      );

      const conversationInfoUnsubscribe = addEventListener(
        "conversationsInfo",
        (data) => {
          // Check if this listener is still valid
          if (
            !isMountedRef.current ||
            currentConversationRef.current !== conversationId
          ) {
            return;
          }

          if (data && data.conversations) {
            setConversationInfo(data.conversations);

            // Initialize opposite user online status for one-to-one conversations
            if (!data.conversations.isGroup && data.conversations.users) {
              const oppositeUser = data.conversations.users.find(
                (user) => user?.userId?._id && user.userId._id !== currentUserId
              );
              if (oppositeUser && oppositeUser.userId) {
                setOppositeUserOnline(oppositeUser.userId.isOnline || false);
              }
            }

            // Check if it's a business group and emit getSubConversations
            if (
              data.conversations.groupType === "business" &&
              data.conversations._id
            ) {
              emit("getSubConversations", {
                conversationId: data.conversations._id,
              });
            }
          }
        }
      );
      
      // Listen for user status updates (online/offline)
      const userStatusUnsubscribe = addEventListener(
        "userStatusUpdate",
        (data) => {
          // Check if this listener is still valid
          if (
            !isMountedRef.current ||
            currentConversationRef.current !== conversationId
          ) {
            return;
          }

          // Only update if this is a one-to-one conversation
          if (selectedContact && !selectedContact.isGroup && data && data.userId) {
            // Check if the status update is for the opposite user in this conversation
            if (selectedContact.oppositeUserId === data.userId) {
              setOppositeUserOnline(data.isOnline || false);
            }
          }
        }
      );

      // Listen for sub-conversations if it's a business group
      const subConversationsUnsubscribe = addEventListener(
        "subConversations",
        (data) => {
          if (data && data.subConversations) {
            if (data.mainConversationId == selectedContact.id) {
              setSubConversations(data.subConversations);
            }
          }
        }
      );

      const subConversationMessagesUnsubscribe = addEventListener(
        "messagesFetchedSubConversation",
        (data) => {
          console.log("messagesFetchedSubConversation received:", data);
          
          // FIXED: Only process messages if a sub-conversation is currently selected
          const currentSubConversationId = selectedSubConversationIdRef.current;
          console.log("Current sub-conversation ID:", currentSubConversationId);
          
          if (!currentSubConversationId) {
            console.log("No sub-conversation selected, ignoring messages");
            return;
          }

          // Handle both data structures: array directly or data.messages
          let messagesArray = null;
          let conversationId = null;
          let hasMore = false;

          // Check if data is an array directly
          if (Array.isArray(data)) {
            messagesArray = data;
            // Extract conversationId from first message if available
            if (messagesArray.length > 0 && messagesArray[0].conversationId) {
              conversationId = messagesArray[0].conversationId;
            }
            // If no conversationId in messages, use currentSubConversationId as fallback
            if (!conversationId && currentSubConversationId) {
              conversationId = currentSubConversationId;
            }
          } else if (data && data.messages) {
            // Data structure: { messages: [], conversationId: "...", hasMore: false }
            messagesArray = data.messages;
            conversationId = data.conversationId;
            hasMore = data.hasMore || false;
          } else if (data && Array.isArray(data.data)) {
            // Alternative structure: { data: [] }
            messagesArray = data.data;
            conversationId = data.conversationId;
            hasMore = data.hasMore || false;
          }
          
          // If still no conversationId, try to extract from first message
          if (!conversationId && messagesArray && messagesArray.length > 0) {
            conversationId = messagesArray[0].conversationId || currentSubConversationId;
          }

          // FIXED: Validate that the response is for the currently selected sub-conversation
          // Only validate if we have a conversationId to compare
          // If data is an array, we'll validate per-message in the filter below
          if (conversationId && conversationId !== currentSubConversationId && !Array.isArray(data)) {
            console.log("Conversation ID mismatch, ignoring:", {
              received: conversationId,
              expected: currentSubConversationId
            });
            return;
          }

          if (messagesArray && messagesArray.length > 0) {
            console.log("Processing messages:", messagesArray.length);
            
            // FIXED: Filter messages to only include those for the current sub-conversation
            // This prevents processing messages from other sub-conversations
            const filteredMessages = messagesArray.filter((msg) => {
              // If conversationId is in the message, validate it matches
              if (msg.conversationId) {
                return msg.conversationId === currentSubConversationId;
              }
              // If no conversationId in message, include it (might be from older format)
              return true;
            });
            
            console.log("Filtered messages:", filteredMessages.length);

            // Transform the incoming message format to match our local message structure
            // This ensures consistency between main conversation and sub-conversation messages
            const transformedMessages = filteredMessages.map((msg) => {
              const attachments = Array.isArray(msg.attachments)
                ? msg.attachments
                : [];
              // ensure both values are strings for comparison to avoid type mismatch issues
              const senderIdStr = String(msg.senderId?._id || "");
              const currentUserIdStr = String(currentUserId || "");
              const isOwnMessage = senderIdStr === currentUserIdStr;

              return {
                id: msg._id,
                _id: msg._id, // Ensure _id is preserved for consistent matching
                type:
                  msg.type ||
                  (attachments.length > 0 ? attachments[0].type : "text"),
                text: msg.message || msg.text || "",
                attachments,
                location: msg.location || null,
                latLong: msg.latLong || null,
                senderId: msg.senderId,
                sender:
                  msg.senderId?.fullName || msg.senderId?.userName || "unknown",
                timestamp: new Date(msg.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                }),
                isOwn: isOwnMessage,
                isTemp: false, // Ensure fetched messages are not marked as temporary
                conversationId: msg.conversationId || conversationId,
                conversationType: msg.conversationType || (data && !Array.isArray(data) ? data.conversationType : null) || "SubConversation",
                mainConversationId: msg.mainConversationId || (data && !Array.isArray(data) ? data.mainConversationId : null),
                miniConversationId: msg.miniConversationId || null,
                reactions: msg.reactions || [], // Preserve reactions from backend
                // FIXED: Only set replyTo fields if replyTo exists and has a valid ID
                // This prevents messages without replies from incorrectly displaying as replies
                // Ensure replyTo has an actual ID value (string or object with _id), not just empty object/null/undefined
                ...(function() {
                  // extract replyTo ID - can be either an object with _id property or a direct ID value
                  const replyToId = msg.replyTo?._id || msg.replyTo;
                  // check if we have a valid replyTo ID (not null, undefined, empty string, or empty object)
                  const hasValidReplyToId = replyToId && 
                    replyToId !== null && 
                    replyToId !== undefined && 
                    replyToId !== "" &&
                    !(typeof replyToId === "object" && Object.keys(replyToId).length === 0);
                  
                  if (hasValidReplyToId) {
                    return {
                      replyTo: msg.replyTo._id || msg.replyTo || null,
                      replyToText: msg.replyTo?.message || msg.replyToText || null,
                      replyToSender:
                        msg.replyTo?.senderId?.fullName ||
                        msg.replyTo?.senderId?.userName ||
                        msg.replyToSender ||
                        null,
                      replyToAttachments: Array.isArray(
                        msg.replyTo?.attachments
                      )
                        ? msg.replyTo.attachments
                        : (msg.replyToAttachments || []),
                    };
                  } else {
                    // no valid replyTo - explicitly set all reply fields to null/empty
                    return {
                      replyTo: null,
                      replyToText: null,
                      replyToSender: null,
                      replyToAttachments: [],
                    };
                  }
                })(),
                // Preserve important message status fields from backend
                isDeletedForMe: msg.isDeletedForMe || [],
                isDeletedForEveryone: msg.isDeletedForEveryone || false,
                isEdited: msg.isEdited || false,
                isForwarded: msg.isForwarded || false,
                forwardedFrom: msg.forwardedFrom || null,
                forwardedBy: msg.forwardedBy || [],
                editedBy: msg.editedBy || [],
                deliveredTo: msg.deliveredTo || [],
                readBy: msg.readBy || [],
                orderId: msg.orderId || null,
                amount: msg.amount || null,
                createdAt: msg.createdAt || null,
                // Additional fields for payment/order messages
                buttonDisabled: msg.buttonDisabled || false,
                initiationFor: msg.initiationFor || [],
                messageFor: msg.messageFor || null,
                description: msg.description || null,
              };
            });

            // FIXED: Replace all messages with sub-conversation messages
            // This completely switches the chat view to show sub-conversation messages
            // Also mark all fetched messages as processed to prevent duplicates from other handlers
            transformedMessages.forEach((msg) => {
              if (msg._id) {
                processedMessageIds.current.add(msg._id);
              }
            });

            console.log("Setting chat state with", transformedMessages.length, "messages");
            
            setChatState((prev) => ({
              ...prev,
              messages: transformedMessages,
              hasMoreMessages: hasMore,
            }));
          } else {
            console.log("No messages found in data");
          }
        }
      );

      // Debug: Check current listener stats
      if (typeof getListenerStats === "function") {
        const stats = getListenerStats();
      }

      // FIXED: Listen for sub-conversation message sent confirmation
      // This fixes the duplicate message issue where messages appeared twice (waiting + received)
      // The issue was caused by server generating different _id than the temporary message
      // Now using content-based matching instead of ID-based matching for sub-conversations
      const subConversationMessageSentUnsubscribe = addEventListener(
        "messageSentOnSubConversation",
        (data) => {
          if (data && data.success && data.message) {
            // Transform the sent message to match our local message structure
            const sentMessage = data.message;

            // Log all current temporary messages for debugging
            const currentTempMessages = chatState.messages.filter(
              (msg) => msg.isTemp && msg.isOwn
            );

            const attachments = Array.isArray(sentMessage.attachments)
              ? sentMessage.attachments
              : [];

            const senderIdStr = String(sentMessage.senderId?._id || "");
            const currentUserIdStr = String(currentUserId || "");
            const isOwnMessage = senderIdStr === currentUserIdStr;

            const transformedMessage = {
              id: sentMessage._id,
              _id: sentMessage._id,
              type:
                sentMessage.type ||
                (attachments.length > 0 ? attachments[0].type : "text"),
              text: sentMessage.message || "",
              attachments,
              location: sentMessage.location || null,
              latLong: sentMessage.latLong || null,
              senderId: sentMessage.senderId,
              timestamp: new Date(sentMessage.createdAt).toLocaleTimeString(
                [],
                {
                  hour: "2-digit",
                  minute: "2-digit",
                }
              ),
              isOwn: isOwnMessage,
              isTemp: false,
              reactions: sentMessage.reactions || [], // Preserve reactions from backend
              // include reply preview fields for sub-conversations
              // FIXED: Only set replyTo fields if replyTo exists and has a valid ID
              // This prevents messages without replies from incorrectly displaying as replies
              // Ensure replyTo has an actual ID value (string or object with _id), not just empty object/null/undefined
              ...(function() {
                // extract replyTo ID - can be either an object with _id property or a direct ID value
                const replyToId = sentMessage.replyTo?._id || sentMessage.replyTo;
                // check if we have a valid replyTo ID (not null, undefined, empty string, or empty object)
                const hasValidReplyToId = replyToId && 
                  replyToId !== null && 
                  replyToId !== undefined && 
                  replyToId !== "" &&
                  !(typeof replyToId === "object" && Object.keys(replyToId).length === 0);
                
                if (hasValidReplyToId) {
                  return {
                    replyTo: sentMessage.replyTo._id || sentMessage.replyTo || null,
                    replyToText: sentMessage.replyTo?.message || null,
                    replyToSender:
                      sentMessage.replyTo?.senderId?.fullName ||
                      sentMessage.replyTo?.senderId?.userName ||
                      null,
                    replyToAttachments: Array.isArray(
                      sentMessage.replyTo?.attachments
                    )
                      ? sentMessage.replyTo.attachments
                      : [],
                  };
                } else {
                  // no valid replyTo - explicitly set all reply fields to null/empty
                  return {
                    replyTo: null,
                    replyToText: null,
                    replyToSender: null,
                    replyToAttachments: [],
                  };
                }
              })(),
              orderId: sentMessage.orderId || null,
              createdAt: sentMessage.createdAt || null,
            };

            // Check if this is a message we just sent (to replace temporary message)
            if (isOwnMessage) {
              // FIXED: For sub-conversations, use content-based duplicate detection since server generates different _id
              const messageId = sentMessage._id || sentMessage.id;

              // Check for content-based duplicates (same text and type) to prevent network duplicates
              const isContentDuplicate = chatState.messages.some((msg) => {
                if (!msg.isOwn || msg.isTemp) return false;
                return (
                  msg.text === sentMessage.message &&
                  msg.type === sentMessage.type
                );
              });

              if (isContentDuplicate) {
                return; // Skip this message entirely
              }

              // Track this message as processed to prevent multiple processing
              if (processedMessageIds.current.has(messageId)) {
                return;
              }
              processedMessageIds.current.add(messageId);

              // Find temporary message to replace using the same logic as main conversation
              let tempMessageIndex = -1;
              const incomingId = sentMessage._id || sentMessage.id;
              const incomingText = sentMessage.message;
              const incomingType = sentMessage.type;

              // FIXED: For sub-conversations, use content-based matching since server generates different _id
              // Strategy 1: Try to find by content and recent timestamp (within 3 seconds)
              const incomingTime = new Date(sentMessage.createdAt).getTime();
              const timeThreshold = 3000; // 3 seconds threshold

              tempMessageIndex = chatState.messages.findIndex((msg) => {
                if (!msg.isTemp || !msg.isOwn) return false;

                const msgTime = new Date(msg.timestamp).getTime();
                const timeDiff = Math.abs(incomingTime - msgTime);

                return (
                  msg.text === incomingText &&
                  msg.type === incomingType &&
                  timeDiff < timeThreshold
                );
              });

              // Strategy 3: Check pending queue for exact match
              if (tempMessageIndex === -1) {
                const incomingTime = new Date(sentMessage.createdAt).getTime();
                const timeThreshold = 3000; // 3 seconds

                for (const [
                  messageId,
                  pendingMsg,
                ] of pendingTempMessages.current.entries()) {
                  const msgTime = new Date(pendingMsg.sentAt).getTime();
                  const timeDiff = Math.abs(incomingTime - msgTime);

                  if (
                    pendingMsg.text === incomingText &&
                    pendingMsg.type === incomingType &&
                    timeDiff < timeThreshold
                  ) {
                    // Remove from pending queue since it's now confirmed
                    pendingTempMessages.current.delete(messageId);

                    // Find the temporary message in state to replace it
                    tempMessageIndex = chatState.messages.findIndex(
                      (msg) => msg._id === messageId
                    );
                    break;
                  }
                }
              }

              // Replace the temporary message or add new message
              setChatState((prev) => {
                let newMessages;

                if (tempMessageIndex !== -1) {
                  // Replace temporary message with real server message

                  newMessages = [...prev.messages];
                  newMessages[tempMessageIndex] = transformedMessage;

                  // Remove from pending messages since it's no longer temporary
                  if (transformedMessage._id) {
                    pendingTempMessages.current.delete(transformedMessage._id);
                  }

                  return { ...prev, messages: newMessages };
                } else {
                  // If no temporary message found, add the new message

                  // FIXED: More aggressive cleanup for sub-conversations to prevent duplicates
                  const now = Date.now();
                  const cleanedMessages = prev.messages.filter((msg) => {
                    if (msg.isTemp && msg.isOwn && msg.text === incomingText) {
                      const msgTime = new Date(msg.timestamp).getTime();
                      const timeDiff = now - msgTime;

                      // Remove any temporary message with same content, regardless of age
                      return false; // Remove this temporary message
                    }
                    return true; // Keep other messages
                  });

                  return {
                    ...prev,
                    messages: [...cleanedMessages, transformedMessage],
                  };
                }
              });
            } else {
              // Message from another user, just add it
              setChatState((prev) => ({
                ...prev,
                messages: [...prev.messages, transformedMessage],
              }));
            }
          }
        }
      );

      // Add listener for messageDeleted events (handles delete for everyone)
      const messageDeletedUnsubscribe = addEventListener(
        "messageDeleted",
        (data) => {
          if (!isMountedRef.current) {
            return;
          }

          const { messageId, conversationId: deletedConversationId, isDeletedForEveryone } = data;
          const currentConversationId = selectedContact?.id;

          // Check if this deletion is for the current conversation
          if (
            currentConversationId &&
            String(deletedConversationId) === String(currentConversationId)
          ) {
            // Update the message in the state to mark it as deleted
            setChatState((prev) => {
              const updatedMessages = prev.messages.map((msg) => {
                if (String(msg._id || msg.id) === String(messageId)) {
                  return {
                    ...msg,
                    isDeletedForEveryone: isDeletedForEveryone,
                  };
                }
                return msg;
              });
              return { ...prev, messages: updatedMessages };
            });
          }
        }
      );

      // Store all unsubscribe functions for this conversation
      conversationListeners.push(
        messageReceivedUnsubscribe,
        messageReceivedSubConversationUnsubscribe,
        conversationUpdateUnsubscribe,
        typingUnsubscribe,
        messagesUnsubscribe,
        messagesDeliveredUnsubscribe,
        messagesReadUnsubscribe,
        messageEditedUnsubscribe,
        messageDeletedUnsubscribe,
        conversationInfoUnsubscribe,
        userStatusUnsubscribe,
        subConversationsUnsubscribe,
        subConversationMessagesUnsubscribe,
        subConversationMessageSentUnsubscribe
      );
      activeListenersRef.current.set(conversationId, conversationListeners);

      return () => {
        // Clean up all listeners for this conversation
        if (activeListenersRef.current.has(conversationId)) {
          const listeners = activeListenersRef.current.get(conversationId);
          listeners.forEach((unsubscribe) => unsubscribe());
          activeListenersRef.current.delete(conversationId);
        }

        leaveConversation(conversationId);
        // Clear any pending AI re-enable timers when leaving conversation
        clearAiReenableTimer();
      };
    }
  }, [
    selectedContact?.id,
    selectedContact?.isNewConversation,
    isConnected,
    joinConversation,
    leaveConversation,
    addEventListener,
    getMessages,
    markAsRead,
    emit,
    currentUserId,
    clearAiReenableTimer
  ]);

  // Add scroll event listener for pagination
  useEffect(() => {
    const messageContainer = messageContainerRef.current;
    if (messageContainer) {
      messageContainer.addEventListener("scroll", handleScroll);
      return () => {
        messageContainer.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]);

  // Cleanup all listeners when component unmounts
  useEffect(() => {
    return () => {
      // Clean up all active listeners for all conversations
      activeListenersRef.current.forEach((listeners, conversationId) => {
        listeners.forEach((unsubscribe) => unsubscribe());
      });

      // Clear the active listeners map
      activeListenersRef.current.clear();

      // Clear processed message IDs
      processedMessageIds.current.clear();

      // Clean up any remaining temporary messages
      pendingTempMessages.current.clear();

      // Clear reply preview on unmount
      setReplyPreview({
        isActive: false,
        message: null,
      });

      // Clear AI re-enable timer on unmount
      clearAiReenableTimer();
    };
  }, []);

  // Handle page refresh and automatic reconnection
  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        // Force reconnection when page is restored from cache
        // Removed forceReconnect call as it's not available
      }
    };

    const handleBeforeUnload = () => {
      // Set a flag that we can check when the page loads again
      sessionStorage.setItem("needsReconnection", "true");
    };

    window.addEventListener("pageshow", handlePageShow);
    window.addEventListener("beforeunload", handleBeforeUnload);

    // Check if we need to reconnect after page load
    if (sessionStorage.getItem("needsReconnection") === "true") {
      sessionStorage.removeItem("needsReconnection");

      // Don't auto-reconnect immediately, let the context handle it
    }

    return () => {
      window.removeEventListener("pageshow", handlePageShow);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  // Monitor connection status and handle reconnection if needed
  useEffect(() => {
    if (isConnected) {
      sessionStorage.removeItem("handlingReconnection");
      sessionStorage.removeItem("needsReconnection");
    } else {
      // Only attempt reconnection if we're not already handling it
      const isHandlingReconnection =
        sessionStorage.getItem("handlingReconnection") === "true";

      if (!isHandlingReconnection) {
        setTimeout(() => {
          // forceReconnect(); // Removed forceReconnect call
        }, 2000); // Wait 2 seconds before attempting reconnection
      }
    }
  }, [isConnected]);

  // Cleanup old temporary messages periodically
  useEffect(() => {
    const cleanupInterval = setInterval(() => {
      setChatState((prev) => {
        const now = Date.now();
        let cleanedCount = 0;

        const filteredMessages = prev.messages.filter((msg) => {
          if (msg.isTemp) {
            // Remove temporary messages older than 60 seconds (increased to prevent premature removal)
            const messageTime = new Date(msg.timestamp).getTime();
            const isOld = now - messageTime > 60000; // 60 seconds

            if (isOld) {
              cleanedCount++;

              // Also clean up from pending messages if it exists
              if (msg._id) {
                pendingTempMessages.current.delete(msg._id);
              }
            }

            return !isOld;
          }
          return true;
        });

        // Only update state if messages were actually filtered
        if (filteredMessages.length !== prev.messages.length) {
          return { ...prev, messages: filteredMessages };
        }

        // Return same state if no changes (prevents unnecessary re-renders)
        return prev;
      });
    }, 30000); // Check every 30 seconds (increased to reduce interference)

    return () => {
      clearInterval(cleanupInterval);
    };
  }, []); // Remove dependency on chatState.messages to prevent infinite re-renders

  // Handle typing with debounce - optimized to prevent excessive re-renders
  useEffect(() => {
    if (!selectedContact) return;

    const conversationId = selectedContact.isNewConversation
      ? null
      : selectedContact.id;
    let typingTimer;

    // Debounce typing indicator to prevent spam
    if (chatState.newMessage.length > 0) {
      clearTimeout(typingTimer);
      typingTimer = setTimeout(() => {
        setChatState((prev) => ({ ...prev, isTyping: false }));
        emitTyping(conversationId, false);
      }, 1500); // Increased delay to reduce spam
    }

    return () => {
      if (typingTimer) {
        clearTimeout(typingTimer);
      }
    };
  }, [selectedContact, emitTyping, chatState.newMessage.length]); // Use length to prevent excessive re-renders

  // Separate effect for typing state changes to prevent excessive re-renders
  useEffect(() => {
    if (!selectedContact) return;

    const conversationId = selectedContact.isNewConversation
      ? null
      : selectedContact.id;

    // Only emit typing if message length changes and we're not already typing
    if (chatState.newMessage.length > 0 && !chatState.isTyping) {
      setChatState((prev) => ({ ...prev, isTyping: true }));
      emitTyping(conversationId, true);
    } else if (chatState.newMessage.length === 0 && chatState.isTyping) {
      setChatState((prev) => ({ ...prev, isTyping: false }));
      emitTyping(conversationId, false);
    }
  }, [
    chatState.newMessage.length,
    chatState.isTyping,
    selectedContact,
    emitTyping,
  ]); // Use length instead of full string

  // Cleanup message queue on unmount
  useEffect(() => {
    return () => {
      // Clear any pending messages
      messageQueue.current = [];
      isProcessingQueue.current = false;
    };
  }, []);

  // Message sending queue to prevent race conditions
  const messageQueue = useRef([]);
  const isProcessingQueue = useRef(false);

  const processMessageQueue = async () => {
    if (isProcessingQueue.current || messageQueue.current.length === 0) return;

    isProcessingQueue.current = true;

    while (messageQueue.current.length > 0) {
      const messageData = messageQueue.current.shift();
      try {
        await sendSingleMessage(messageData);
        // Minimal delay to prevent overwhelming the socket but allow quick processing
        await new Promise((resolve) => setTimeout(resolve, 10));
      } catch (error) {
        // Re-add failed message to front of queue
        messageQueue.current.unshift(messageData);
        break;
      }
    }

    isProcessingQueue.current = false;
  };

  const sendSingleMessage = async (messageData) => {
    const { messageText, attachments, messageType, orderId, selectedContact, latLong } =
      messageData;

    // Generate a valid ObjectId for the database _id field
    // This _id will be unique for every message and stored in database
    const messageId = new ObjectId().toString();

    // create payload based on whether this is a new or existing conversation
    let messagePayload;

    if (selectedContact.isNewConversation) {
      // new conversation - send with receiverId (backend will create conversation)
      messagePayload = {
        _id: messageId, // Use valid ObjectId for database
        senderId: currentUserId,
        receiverId: selectedContact.id, // this is the user ID
        type: messageType,
        message: messageText,
        attachments: attachments,
        mentions: messageMentions.map((m) => ({
          userId: m.userId,
          name: m.name,
        })), // Add mentions
        reactions: [],
        replyTo:
          replyPreview.isActive && replyPreview.message
            ? replyPreview.message._id || replyPreview.message.id
            : null, // Add replyTo field
        repliedBy: [],
        forwardedFrom: null,
        forwardedBy: [],
        isDeletedForMe: [],
        isDeletedForEveryone: false,
        isEdited: false,
        editedBy: [],
        deliveredTo: [],
        readBy: [],
        orderId: orderId,
        location: messageType === "location" ? messageText : null,
        latLong: messageType === "location" && latLong ? latLong : null,
      };
    } else {
      // existing conversation - send with conversationId
      messagePayload = {
        _id: messageId, // Use valid ObjectId for database
        senderId: currentUserId,
        conversationId: selectedContact.id, // this is the conversation ID
        type: messageType,
        message: messageText,
        attachments: attachments,
        mentions: messageMentions.map((m) => ({
          userId: m.userId,
          name: m.name,
        })), // Add mentions
        reactions: [],
        replyTo:
          replyPreview.isActive && replyPreview.message
            ? replyPreview.message._id || replyPreview.message.id
            : null, // Add replyTo field
        repliedBy: [],
        forwardedBy: [],
        isDeletedForMe: [],
        isEdited: false,
        editedBy: [],
        deliveredTo: [],
        readBy: [],
        orderId: orderId,
        location: messageType === "location" ? messageText : null,
        latLong: messageType === "location" && latLong ? latLong : null,
      };
    }

    try {
      // emit sendMessage socket event
      let success = false;

      try {
        // FIXED: Check if we're in a sub-conversation and use appropriate sending method
        // Note: Server generates different _id for sub-conversation messages
        // This is why we use content-based matching in the response handler
        if (selectedSubConversationId) {
          // Prepare sub-conversation message payload
          const subConversationPayload = {
            _id: messageId,
            senderId: currentUserId,
            conversationId: selectedSubConversationId,
            conversationType: "SubConversation",
            type: messageType,
            message: messageText,
            attachments: attachments,
            mentions: messageMentions.map((m) => ({
              userId: m.userId,
              name: m.name,
            })), // Add mentions
            reactions: [],
            replyTo:
              replyPreview.isActive && replyPreview.message
                ? replyPreview.message._id || replyPreview.message.id
                : null, // Add replyTo field for sub-conversations
            repliedBy: [],
            forwardedFrom: null,
            forwardedBy: [],
            isDeletedForMe: [],
            isDeletedForEveryone: false,
            isEdited: false,
            editedBy: [],
            deliveredTo: [],
            readBy: [],
            location: messageType === "location" ? messageText : null,
            latLong: messageType === "location" && latLong ? latLong : null,
          };
          success = sendMessageOnSubConversation(subConversationPayload);
        } else {
          // Regular conversation message

          success = sendMessage(messagePayload);
        }
      } catch (socketError) {
        success = false;
      }

      if (success) {
        // create local message for immediate display with unique _id
        const newMsg = {
          id: messageId, // Use ObjectId for local tracking
          _id: messageId, // Use ObjectId for consistent matching with server messages
          text: messageText,
          type: messageType,
          senderId: currentUserId,
          timestamp: new Date().toLocaleString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
          }),
          isOwn: true,
          attachments: attachments,
          location: messageType === "location" ? messageText : null,
          latLong: messageType === "location" && latLong ? latLong : null,
          isTemp: true, // mark as temporary message until server confirms
          reactions: [], // Initialize with empty reactions for temporary messages
          // Store the exact text and type for content matching
          exactText: messageText,
          exactType: messageType,
          // Add replyTo information for temporary messages
          replyTo:
            replyPreview.isActive && replyPreview.message
              ? replyPreview.message._id || replyPreview.message.id
              : null,
          // Add reply preview information for display
          replyToText:
            replyPreview.isActive && replyPreview.message
              ? replyPreview.message.text
              : null,
          replyToSender:
            replyPreview.isActive && replyPreview.message
              ? replyPreview.message.isOwn
                ? "yourself"
                : replyPreview.message.sender || "user"
              : null,
          // include reply attachments for preview if replying to media
          replyToAttachments:
            replyPreview.isActive &&
            replyPreview.message &&
            Array.isArray(replyPreview.message.attachments)
              ? replyPreview.message.attachments
              : [],
          // Add delivery and read tracking fields for temporary messages
          deliveredTo: [],
          readBy: [],
          orderId: orderId,
          isDeletedForMe: [],
          isDeletedForEveryone: false,
          isEdited: false,
          isForwarded: false,
          forwardedFrom: null,
          forwardedBy: [],
          editedBy: [],
        };

        // Log the complete temporary message object

        // Add to pending queue to track temporary messages
        pendingTempMessages.current.set(messageId, {
          ...newMsg,
          originalText: messageText,
          sentAt: Date.now(),
        });

        // Update state synchronously to prevent race conditions
        setChatState((prev) => {
          const updatedMessages = [...prev.messages, newMsg];
          return {
            ...prev,
            messages: updatedMessages,
          };
        });
      } else {
        toast.error("failed to send message. please try again.");
        throw new Error("Message sending failed");
      }
    } catch (error) {
      throw error; // Re-throw to be handled by queue processor
    }
  };

  // Handle sending message with formatted mentions
  const handleSendMessageWithFormattedText = async (
    formattedMessageText,
    mentionsArray
  ) => {
    if (
      (!formattedMessageText.trim() && mediaState.selectedFiles.length === 0) ||
      !selectedContact
    ) {
      return;
    }

    // Use formatted message text for socket transmission
    const messageText = formattedMessageText.trim() || "";
    let messageType = "text";

    // Update mentions state with the provided mentions
    setMessageMentions(mentionsArray);

    // If there are files, start background upload
    if (mediaState.selectedFiles.length > 0) {
      messageType = "media";

      // Clear input immediately for better UX
      setChatState((prev) => ({
        ...prev,
        newMessage: "",
        isTyping: false,
      }));

      // Clear reply preview when sending message
      if (replyPreview.isActive) {
        setReplyPreview({
          isActive: false,
          message: null,
        });
      }

      // Start upload in background with callback to send message when complete
      setMediaState((prev) => ({
        ...prev,
        isUploading: true,
        uploadProgress: {},
      }));

      uploadMedia(mediaState.selectedFiles)
        .then((attachments) => {
          if (attachments.length === 0) {
            toast.error("failed to upload media. please try again.");
            setMediaState((prev) => ({
              ...prev,
              isUploading: false,
              uploadProgress: {},
              selectedFiles: [],
            }));
            return;
          }

          // validate attachments structure
          const validAttachments = attachments.filter(
            (att) =>
              att &&
              att.type &&
              att.url &&
              ["image", "video", "audio", "document", "file"].includes(att.type)
          );

          if (validAttachments.length !== attachments.length) {
            toast.error("some media files are invalid. please try again.");
            setMediaState((prev) => ({
              ...prev,
              isUploading: false,
              uploadProgress: {},
              selectedFiles: [],
            }));
            return;
          }

          // Clear selected files
          setMediaState((prev) => ({
            ...prev,
            selectedFiles: [],
            isUploading: false,
            uploadProgress: {},
          }));

          // Automatically send message with attachments when upload completes
          processMessageSending(messageText, validAttachments, messageType);
        })
        .catch((error) => {
          console.error("Upload failed:", error);
          toast.error("failed to upload media. please try again.");
          setMediaState((prev) => ({
            ...prev,
            isUploading: false,
            uploadProgress: {},
            selectedFiles: [],
          }));
        });
    } else {
      // No files, send text message immediately
      await processMessageSending(messageText, [], messageType);
    }
  };

  // Extract common message sending logic
  const processMessageSending = async (
    messageText,
    attachments,
    messageType,
    latLong = null
  ) => {
    // Check if message contains beemzAi mention
    const hasAiMention = hasBeemzAiMention(messageText, messageMentions);
    const isAiPresent = isAiUserPresentInGroup();
    const customerCount = getCustomerMemberCount();
    // Check AI permission: conversation-level aiEnabled (from API) or business location enableAi
    // conversationInfo.aiEnabled is set when conversation has AI enabled; businessLocationId may be a string ID
    const aiEnabled =
      conversationInfo?.aiEnabled === true ||
      conversationInfo?.businessLocationId?.enableAi === true;
    
    // AI trigger conditions:
    // 0) AI must be enabled (conversation aiEnabled or businessLocationId.enableAi)
    // 1) If message mentions @beemz.ai -> always trigger (when AI enabled)
    // 2) Owner/team member: only trigger when @tagged (never auto-trigger)
    // 3) Single user (userType "user"): auto-trigger without tag when sender is that customer
    // 4) Multiple users (userType "user"): only trigger when @tagged

    const isSenderCustomer = !isCurrentUserOwnerOrTeamMember();
    let shouldTriggerAI = false;

    if (aiEnabled && isAiPresent) {
      if (hasAiMention) {
        shouldTriggerAI = true;
      } else if (customerCount === 1 && isSenderCustomer) {
        // Single customer: auto-trigger only when sender is the customer (userType "user")
        shouldTriggerAI = true;
      }
    }

    // Remove mentions from message text for AI processing
    const cleanedMessageText = removeMentionsFromText(messageText);

    // Clear input and media state immediately for better UX
    setChatState((prev) => ({
      ...prev,
      newMessage: "",
      isTyping: false,
    }));
    setMediaState((prev) => ({ ...prev, selectedFiles: [] }));

    // Clear reply preview when sending message
    if (replyPreview.isActive) {
      setReplyPreview({
        isActive: false,
        message: null,
      });
    }

    // Clear mentions when sending message
    setMessageMentions([]);

    // Track rapid message sending
    const now = Date.now();
    const timeSinceLastMessage =
      now - rapidMessageTracker.current.lastMessageTime;

    if (timeSinceLastMessage < 1000) {
      // Less than 1 second
      rapidMessageTracker.current.messageCount++;
      if (rapidMessageTracker.current.messageCount >= 3) {
        rapidMessageTracker.current.isRapidMode = true;
      }
    } else {
      rapidMessageTracker.current.messageCount = 1;
      rapidMessageTracker.current.isRapidMode = false;
    }
    rapidMessageTracker.current.lastMessageTime = now;

    // For quick message sending, process immediately without queue delay
    if (
      messageQueue.current.length === 0 ||
      rapidMessageTracker.current.isRapidMode
    ) {
      // No queue or rapid mode - send immediately
      const messageData = {
        messageText,
        attachments,
        messageType,
        selectedContact,
        latLong,
      };

      // Send immediately for better responsiveness
      sendSingleMessage(messageData);

      // Schedule AI trigger; recompute from conversationInfoRef when timeout runs (fixes stale closure)
      if (cleanedMessageText.trim()) {
        const timeout = hasAiMention ? 100 : 800;
        setTimeout(() => {
          const info = conversationInfoRef.current;
          const aiEnabledNow =
            info?.aiEnabled === true || info?.businessLocationId?.enableAi === true;
          const isAiPresentNow = isAiUserPresentInGroup();
          const customerCountNow = getCustomerMemberCount();
          const isSenderCustomerNow = !isCurrentUserOwnerOrTeamMember();
          const shouldTriggerNow =
            aiEnabledNow &&
            isAiPresentNow &&
            (hasAiMention || (customerCountNow === 1 && isSenderCustomerNow));
          const businessLocationId =
            info?.businessLocationId?._id ?? info?.businessLocationId;
          if (shouldTriggerNow && businessLocationId) {
            handleAiStreaming(
              cleanedMessageText,
              getConversationCase(),
              businessLocationId
            );
          } else if (shouldTriggerNow && !businessLocationId) {
            toast.error("Conversation not loaded yet. Try again.");
          }
        }, timeout);
      }
    } else {
      // Add message to queue for batch processing
      const messageData = {
        messageText,
        attachments,
        messageType,
        selectedContact,
        latLong,
      };

      messageQueue.current.push(messageData);

      // Process queue if not already processing
      if (!isProcessingQueue.current) {
        processMessageQueue();
      }

      // Schedule AI trigger for queued messages; recompute from conversationInfoRef when timeout runs
      if (cleanedMessageText.trim()) {
        setTimeout(() => {
          const info = conversationInfoRef.current;
          const aiEnabledNow =
            info?.aiEnabled === true || info?.businessLocationId?.enableAi === true;
          const isAiPresentNow = isAiUserPresentInGroup();
          const customerCountNow = getCustomerMemberCount();
          const isSenderCustomerNow = !isCurrentUserOwnerOrTeamMember();
          const shouldTriggerNow =
            aiEnabledNow &&
            isAiPresentNow &&
            (hasAiMention || (customerCountNow === 1 && isSenderCustomerNow));
          const businessLocationId =
            info?.businessLocationId?._id ?? info?.businessLocationId;
          if (shouldTriggerNow && businessLocationId) {
            handleAiStreaming(
              cleanedMessageText,
              getConversationCase(),
              businessLocationId
            );
          } else if (shouldTriggerNow && !businessLocationId) {
            toast.error("Conversation not loaded yet. Try again.");
          }
        }, 1000);
      }
    }
  };

  const handleSendMessage = async () => {
    if (
      (!chatState.newMessage.trim() && mediaState.selectedFiles.length === 0) ||
      !selectedContact
    ) {
      // toast.error("please enter a message or select files to send");
      return;
    }

    // Store message text and files before clearing input
    const messageText = chatState.newMessage.trim() || "";
    const filesToUpload = [...mediaState.selectedFiles];
    let messageType = "text";

    // If there are files, start background upload
    if (filesToUpload.length > 0) {
      messageType = "media";

      // Generate temporary message ID for tracking
      const tempMessageId = new ObjectId().toString();
      
      // Store reply info before clearing
      const replyToMessage = replyPreview.isActive ? replyPreview.message : null;
      const mentionsToSend = [...messageMentions];
      
      // Store conversation context for later use
      const conversationId = selectedContact.id;
      const conversationContext = {
        conversationId,
        messageText,
        messageType,
        replyToMessage,
        mentionsToSend,
        tempMessageId,
      };

      // Clear input and files immediately for better UX
      setChatState((prev) => ({
        ...prev,
        newMessage: "",
        isTyping: false,
      }));

      setMediaState((prev) => ({
        ...prev,
        selectedFiles: [],
        isUploading: false,
        uploadProgress: {},
      }));

      // Clear reply preview when sending message
      if (replyPreview.isActive) {
        setReplyPreview({
          isActive: false,
          message: null,
        });
      }

      // Clear mentions when sending message
      setMessageMentions([]);

      // Create temporary "uploading" message to show in chat
      const tempUploadingMessage = {
        _id: tempMessageId,
        id: tempMessageId,
        message: messageText || `Uploading ${filesToUpload.length} file${filesToUpload.length > 1 ? 's' : ''}...`,
        text: messageText || `Uploading ${filesToUpload.length} file${filesToUpload.length > 1 ? 's' : ''}...`,
        type: "media",
        senderId: {
          _id: currentUserId,
          fullName: user?.fullName || "You",
        },
        timestamp: new Date().toISOString(),
        isTemp: true,
        isOwn: true,
        isUploading: true,
        uploadProgress: 0,
        attachments: filesToUpload.map(file => ({
          type: file.type.startsWith('image/') ? 'image' : 
                file.type.startsWith('video/') ? 'video' : 
                file.type.startsWith('audio/') ? 'audio' : 'document',
          fileName: file.name,
          size: file.size,
          isUploading: true,
        })),
        replyTo: replyToMessage,
        mentions: mentionsToSend,
      };

      // Add temporary uploading message to chat
      setChatState((prev) => ({
        ...prev,
        messages: [...prev.messages, tempUploadingMessage],
      }));

      // Scroll to bottom to show the uploading message
      setTimeout(() => {
        if (messageContainerRef.current) {
          messageContainerRef.current.scrollTop =
            messageContainerRef.current.scrollHeight;
        }
      }, 100);

      // Start upload in background - DON'T AWAIT, let it run asynchronously
      // Store conversation context in metadata so upload persists across navigation
      uploadFiles(filesToUpload, {
        conversationId: conversationContext.conversationId,
        messageContext: conversationContext,
      })
        .then((uploadResults) => {
          if (uploadResults.length === 0) {
            toast.error("failed to upload media. please try again.");
            
            // Remove the temporary uploading message only if still in same conversation
            setChatState((prev) => ({
              ...prev,
              messages: prev.messages.filter(msg => msg._id !== tempMessageId),
            }));
            return;
          }

          // Transform upload results to attachments
          const attachments = uploadResults.map((result) => {
            let fileType = "document";
            if (result.fileType) {
              if (result.fileType.startsWith("image/")) fileType = "image";
              else if (result.fileType.startsWith("video/")) fileType = "video";
              else if (result.fileType.startsWith("audio/")) fileType = "audio";
              else fileType = "document";
            } else if (result.type) {
              fileType = result.type;
            }

            return {
              type: fileType,
              url: result.fileUrl || result.fileKey,
              fileName: result.fileName || null,
              size: result.fileSize || null,
              thumbnail: null,
              duration: null,
            };
          });

          // Validate attachments structure
          const validAttachments = attachments.filter(
            (att) =>
              att &&
              att.type &&
              att.url &&
              ["image", "video", "audio", "document", "file"].includes(att.type)
          );

          if (validAttachments.length !== attachments.length) {
            toast.error("some media files are invalid. please try again.");
            
            // Remove the temporary uploading message only if still in same conversation
            setChatState((prev) => ({
              ...prev,
              messages: prev.messages.filter(msg => msg._id !== tempMessageId),
            }));
            return;
          }

          // Remove the temporary uploading message only if still in same conversation
          setChatState((prev) => ({
            ...prev,
            messages: prev.messages.filter(msg => msg._id !== tempMessageId),
          }));

          // Send the actual message with uploaded attachments
          // Use the stored conversation context to ensure message goes to correct conversation
          const { messageText: storedText, messageType: storedType, replyToMessage, mentionsToSend } = conversationContext;
          
          // Create proper message payload matching sendSingleMessage format
          const messagePayload = {
            _id: new ObjectId().toString(),
            senderId: currentUserId,
            conversationId: conversationContext.conversationId,
            type: storedType,
            message: storedText,
            attachments: validAttachments,
            mentions: mentionsToSend || [],
            reactions: [],
            replyTo: replyToMessage?._id || replyToMessage?.id || null,
            repliedBy: [],
            forwardedBy: [],
            isDeletedForMe: [],
            isEdited: false,
            editedBy: [],
            deliveredTo: [],
            readBy: [],
          };

          // Send message using the correct socket function
          try {
            const success = sendMessage(messagePayload);
            
            if (success) {
              // Create local message for immediate display
              const newMsg = {
                id: messagePayload._id,
                _id: messagePayload._id,
                text: storedText,
                type: storedType,
                senderId: currentUserId,
                timestamp: new Date().toLocaleString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                }),
                isOwn: true,
                attachments: validAttachments,
                isTemp: true,
                reactions: [],
                replyTo: replyToMessage?._id || replyToMessage?.id || null,
                replyToText: replyToMessage?.text || null,
                replyToSender: replyToMessage?.isOwn ? "yourself" : replyToMessage?.sender || "user",
                replyToAttachments: replyToMessage?.attachments || [],
                deliveredTo: [],
                readBy: [],
                isDeletedForMe: [],
                isDeletedForEveryone: false,
                isEdited: false,
                isForwarded: false,
              };

              // Add to messages
              setChatState((prev) => ({
                ...prev,
                messages: [...prev.messages, newMsg],
              }));

              // Scroll to bottom
              setTimeout(() => {
                if (messageContainerRef.current) {
                  messageContainerRef.current.scrollTop =
                    messageContainerRef.current.scrollHeight;
                }
              }, 100);

              toast.success("media uploaded and message sent");
            } else {
              toast.error("Unable to send message. Please check your connection.");
            }
          } catch (error) {
            console.error("Error sending message:", error);
            toast.error("Unable to send message. Please try again.");
          }
        })
        .catch((error) => {
          console.error("Upload failed:", error);
          toast.error("failed to upload media. please try again.");
          
          // Remove the temporary uploading message only if still in same conversation
          setChatState((prev) => ({
            ...prev,
            messages: prev.messages.filter(msg => msg._id !== tempMessageId),
          }));
        });

      // Return immediately - don't wait for upload to complete
      return;
    } else {
      // No files, send text message immediately
      await processMessageSending(messageText, [], messageType);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleInputChange = (e) => {
    setChatState((prev) => ({ ...prev, newMessage: e.target.value }));
  };

  const handleQuickReplySelect = useCallback(
    (suggestionText) => {
      if (!suggestionText?.trim()) return;
      processMessageSending(suggestionText.trim(), [], "text");
    },
    [processMessageSending]
  );

  // media viewer functions
  const handleMediaClick = (message) => {
    if (message.attachments && message.attachments.length > 0) {
      setMediaState((prev) => ({
        ...prev,
        selectedMedia: message,
        currentMediaIndex: 0,
        mediaViewerOpen: true,
      }));
    }
  };

  // emoji reaction handler
  const handleEmojiReaction = (messageId, emoji, side) => {
    console.log("😀 ChatView - Emoji Reaction Handler:", {
      messageId,
      emoji,
      side,
      isConnected,
      conversationId: conversationInfo?.conversationId,
      conversationInfo: conversationInfo ? "✅ Present" : "❌ Missing",
    });
    
    // Emit socket event for emoji reaction
    if (isConnected) {
      if (!conversationInfo?.conversationId) {
        console.warn("⚠️ conversationId is missing, reaction may not be saved properly");
      }
      emit("addReaction", {
        messageId,
        emoji,
        side, // 'left' or 'right' to indicate which side of the message
        conversationId: conversationInfo?.conversationId,
      });
    } else {
      console.error("❌ Socket not connected, cannot add reaction");
    }
  };

  const handleCloseMediaViewer = () => {
    setMediaState((prev) => ({
      ...prev,
      mediaViewerOpen: false,
      selectedMedia: null,
      currentMediaIndex: 0,
    }));
  };

  const handleNextMedia = () => {
    if (
      mediaState.selectedMedia &&
      mediaState.currentMediaIndex <
        mediaState.selectedMedia.attachments.length - 1
    ) {
      setMediaState((prev) => ({
        ...prev,
        currentMediaIndex: prev.currentMediaIndex + 1,
      }));
    }
  };

  const handlePrevMedia = () => {
    if (mediaState.selectedMedia && mediaState.currentMediaIndex > 0) {
      setMediaState((prev) => ({
        ...prev,
        currentMediaIndex: prev.currentMediaIndex - 1,
      }));
    }
  };

  const handleDownloadMedia = async () => {
    if (
      mediaState.selectedMedia &&
      mediaState.selectedMedia.attachments[mediaState.currentMediaIndex]
    ) {
      const media =
        mediaState.selectedMedia.attachments[mediaState.currentMediaIndex];
      
      try {
        // Fetch the file as a blob to force download
        const response = await fetch(media.url, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
        });
        
        if (!response.ok) throw new Error('Network response was not ok');
        
        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = media.fileName || `media_${Date.now()}`;
        document.body.appendChild(link);
        link.click();
        
        // Cleanup
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (error) {
        console.error("Download failed, using fallback:", error);
        // Fallback: Use the direct URL but WITHOUT target="_blank"
        // This avoids the new tab, though some browsers might still open it if they can't download
        const link = document.createElement("a");
        link.href = media.url;
        link.download = media.fileName || `media_${Date.now()}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  };

  const handleDownloadAllMedia = () => {
    if (mediaState.selectedMedia && mediaState.selectedMedia.attachments) {
      mediaState.selectedMedia.attachments.forEach(async (media, index) => {
        setTimeout(async () => {
          try {
            const response = await fetch(media.url, {
              method: 'GET',
              mode: 'cors',
              cache: 'no-cache',
            });
            
            if (!response.ok) throw new Error('Fetch failed');
            
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = media.fileName || `media_${Date.now()}_${index}`;
            document.body.appendChild(link);
            link.click();
            
            document.body.removeChild(link);
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          } catch (error) {
            console.error("Download failed for file " + index, error);
            const link = document.createElement("a");
            link.href = media.url;
            link.download = media.fileName || `media_${Date.now()}_${index}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }
        }, index * 500); // More staggering for multiple fetches
      });
    }
  };

  // Chat options menu handlers
  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
    setIsMenuOpen(true);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setIsMenuOpen(false);
  };

  // Handle close conversation
  const handleCloseConversation = async () => {
    try {
      if (!selectedContact?.id) {
        toast.error("Conversation not found");
        return;
      }

      // Make POST request to close the conversation
      await axiosInstance.post(
        ApiEndPoints.CHAT.closeConvertion(selectedContact.id),
        {
          isOpen: false,
        }
      );

      toast.success("Conversation closed  ");

      // Optionally refresh the conversation data or navigate away
      // You can add additional logic here if needed
    } catch (error) {
      console.error("Error closing conversation:", error);
      toast.error(
        error.response?.data?.message || "Failed to close conversation"
      );
    }
  };

  // Handle toggle mute/unmute conversation
  const handleToggleMute = async () => {
    try {
      const conversationId = selectedContact?.id;
      if (!conversationId) {
        toast.error("No conversation selected");
        return;
      }

      // Check current mute status from conversationInfo
      const isMuted = conversationInfo?.isMuteUser?.some(
        (muteUserId) => String(muteUserId) === String(currentUserId)
      );

      // Emit socket event to toggle mute status
      emit("isMute", { conversationId });

      // Show feedback to user
      toast.success(isMuted ? "conversation unmuted" : "conversation muted");
    } catch (error) {
      console.error("Error toggling mute:", error);
      toast.error("Failed to toggle mute status");
    }
  };

  const handleMenuActionInternal = (action) => {
    // Handle different menu actions here
    switch (action) {
      case "addMember":
        // Clear sub-conversation context for main conversation
        setAddMemberSubConversation(null);
        // Check group type:
        // - Business groups use AddMemberDialog (sends request)
        // - Regular groups (Group type) use ParticipantSelectionDialog (directly adds members)
        if (conversationInfo?.groupType === "business") {
          // Open add member dialog for business groups (request-based flow)
          setAddMemberDialogOpen(true);
        } else {
          // For regular groups (Group type), use participant selection dialog (direct add)
          setParticipantSelectionDialogOpen(true);
        }
        break;
      case "viewOrder":
        // View order logic
        break;
      case "hide":
        // Hide logic
        break;
      case "rate":
        // Rate logic
        break;
      case "mute":
        // Toggle mute/unmute for this conversation
        handleToggleMute();
        break;
      case "report":
        // Report logic
        break;
      case "budgetSettings":
        // Budget settings logic
        break;
      case "closeConversation":
        // Close conversation logic
        handleCloseConversation();
        break;
      case "exitGroup":
        // Open exit group confirmation dialog
        setExitGroupDialogOpen(true);
        break;
      case "deleteGroup":
        // Remove conversation from list (user already exited or was removed)
        handleDeleteGroup();
        break;
      case "exitAndDeleteForMe":
        // Exit and remove from list – same delete-conversation API
        handleDeleteGroup();
        break;
      case "clearChat":
        // Open clear chat confirmation dialog
        setClearChatDialogOpen(true);
        break;
      default:
        break;
    }
    handleMenuClose();
  };
  
  // Keep the original name for internal use
  const handleMenuAction = handleMenuActionInternal;

  // Chat categories handlers
  const handleOpenChatCategories = () => {
    // Only allow opening chat categories for business groups
    if (conversationInfo?.groupType !== "business") {
      toast.error("chat categories are only available for business groups");
      return;
    }

    const businessLocationId = conversationInfo?.businessLocationId?._id ?? conversationInfo?.businessLocationId;
    if (!businessLocationId) {
      toast.error("business location not available");
      return;
    }

    setChatCategoriesOpen(true);
    // Trigger the API call when opening the popup
    refetchChatCategories();
  };

  const handleCloseChatCategories = () => {
    setChatCategoriesOpen(false);
  };

  // Handle menu dialog open/close
  const handleOpenMenuDialog = () => {
    setMenuDialogOpen(true);
    
    // If businessLocationId is not available, request conversationInfo update
    // This handles cases where conversationInfo might not be fully loaded yet
    if (!conversationInfo?.businessLocationId?._id) {
      const conversationId = currentConversationRef.current;
      if (conversationId && emit) {
        emit("getConversationsInfo", { conversationId });
      }
    }
  };

  const handleCloseMenuDialog = () => {
    setMenuDialogOpen(false);
    menuRefetchRef.current = null; // Clear refetch ref when dialog closes
  };

  // Refetch menu data when conversationInfo updates (e.g., after payment completion)
  // This handles the case where businessLocationId becomes available after dialog opens
  const prevBusinessLocationIdRef = useRef(conversationInfo?.businessLocationId?._id);
  useEffect(() => {
    const currentBusinessLocationId = conversationInfo?.businessLocationId?._id;
    
    // Only refetch if:
    // 1. Menu dialog is open
    // 2. businessLocationId is now available
    // 3. businessLocationId changed from undefined/null to a value (or changed to a different value)
    if (menuDialogOpen && menuRefetchRef.current && currentBusinessLocationId) {
      const prevLocationId = prevBusinessLocationIdRef.current;
      
      // If businessLocationId became available (was null/undefined, now has value) or changed
      if (prevLocationId !== currentBusinessLocationId) {
        prevBusinessLocationIdRef.current = currentBusinessLocationId;
        
        // Small delay to ensure conversationInfo has fully updated
        const timeoutId = setTimeout(() => {
          if (menuRefetchRef.current && menuDialogOpen) {
            menuRefetchRef.current();
          }
        }, 300);
        return () => clearTimeout(timeoutId);
      }
    } else if (currentBusinessLocationId) {
      // Update ref even if dialog is not open (to track current value)
      prevBusinessLocationIdRef.current = currentBusinessLocationId;
    }
  }, [conversationInfo?.businessLocationId?._id, menuDialogOpen]);

  // Listen for payment completion messages from popup windows (e.g., OrderDetailsDialog)
  useEffect(() => {
    const handleMessage = (event) => {
      // Handle payment success from order payment popup
      if (event.data === "order-payment-success" || 
          (typeof event.data === "string" && event.data.includes("order-payment-success"))) {
        
        // Refetch conversationInfo to get updated businessLocationId after payment
        const conversationId = currentConversationRef.current;
        if (conversationId && emit) {
          emit("getConversationsInfo", { conversationId });
        }
        
        // Note: The effect watching conversationInfo.businessLocationId will handle menu refetch
        // when businessLocationId becomes available. No need for retry here as the effect will trigger.
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [menuDialogOpen, conversationInfo?.businessLocationId?._id, emit]);

  // Fetch cart data
  const fetchCartData = async (conversationId) => {
    if (!conversationId) return;

    setCartLoading(true);
    try {
      const locationId = conversationInfo?.businessLocationId?._id;
      const url = locationId 
        ? `${ApiEndPoints.CART.get}/${conversationId}?locationId=${locationId}`
        : `${ApiEndPoints.CART.get}/${conversationId}`;
      const response = await axiosInstance.get(url);
      setCartData(response.data);
    } catch (error) {
      setCartData(null);
    } finally {
      setCartLoading(false);
    }
  };

  // Cart dialog handlers
  const handleOpenCartDialog = async () => {
    setMenuDialogOpen(false); // Close menu dialog first
    setCartDialogOpen(true);
    // Fetch cart data when opening cart dialog - use sub-conversation ID if available
    const conversationIdToUse = selectedSubConversationId || currentConversationRef.current;
    if (conversationIdToUse) {
      await fetchCartData(conversationIdToUse);
    }
  };

  const handleCloseCartDialog = () => {
    setCartDialogOpen(false);
  };

  const handleBackToMenuFromCart = () => {
    setCartDialogOpen(false); // Close cart dialog
    setMenuDialogOpen(true); // Open menu dialog
  };

  // Cart action handlers
  const handleEditCartItem = (cartItem) => {
    // Open item detail dialog for editing cart item
    handleOpenItemOrderDetail(cartItem.menuItemId, cartItem);
  };

  const handleRemoveCartItem = (cartItem) => {
    // TODO: Implement remove item from cart

    toast.success("item removed from cart");
    // Refresh cart data after removal - use sub-conversation ID if available
    const conversationIdToUse = selectedSubConversationId || currentConversationRef.current;
    if (conversationIdToUse) {
      fetchCartData(conversationIdToUse);
    }
  };

  const handleUpdateCartQuantity = (cartItem, newQuantity) => {
    // TODO: Implement update quantity in cart

    toast.success("quantity updated");
    // Refresh cart data after update - use sub-conversation ID if available
    const conversationIdToUse = selectedSubConversationId || currentConversationRef.current;
    if (conversationIdToUse) {
      fetchCartData(conversationIdToUse);
    }
  };

  const handlePlaceOrder = (cartData) => {
   setCartDialogOpen(false);
  };

  // Handle menu item search by name (for AI-generated menu lists)
  const handleSearchMenuItemByName = async (itemName) => {
    try {
      if (!conversationInfo?.businessLocationId?._id) {
        console.error("Business location ID not available");
        return;
      }

      // Get menu data from cache or fetch if not available
      let currentMenuData = menuData;
      
      if (!currentMenuData) {
        // Fetch menu data if not in cache
        const result = await refetchMenuData();
        currentMenuData = result.data;
      }

      // Search for menu item by name (case-insensitive, partial match)
      let foundItem = null;
      if (currentMenuData?.data?.menus) {
        const searchName = itemName.toLowerCase().trim();

        for (const menu of currentMenuData.data.menus) {
          for (const category of menu.categories || []) {
            foundItem = category.menuItemIds?.find((item) => {
              const itemNameLower = (item.name || "").toLowerCase().trim();
              // Check for exact match or if the item name contains the search term
              return (
                itemNameLower === searchName ||
                itemNameLower.includes(searchName)
              );
            });
            if (foundItem) break;
          }
          if (foundItem) break;
        }
      }

      if (foundItem) {
        // Open the item order detail dialog
        handleOpenItemOrderDetail(foundItem);
      } else {
        console.warn(`Menu item not found: ${itemName}`);
        // Optionally show a toast/notification that item wasn't found
      }
    } catch (error) {
      console.error("Error searching for menu item:", error);
      toast.error("Failed to search menu item");
    }
  };

  // Item order detail dialog handlers
  const handleOpenItemOrderDetail = async (itemData, cartItemData = null) => {
    // If this is coming from cart and we have simplified menu item data, fetch full menu item data
    let fullMenuItemData = itemData;
    if (
      cartItemData &&
      itemData &&
      !itemData.variants &&
      !itemData.ingredients
    ) {
      try {
        // Get menu data from cache or fetch if not available
        let currentMenuData = menuData;
        
        if (!currentMenuData) {
          // Fetch menu data if not in cache
          const result = await refetchMenuData();
          currentMenuData = result.data;
        }

        // Find the full menu item data
        if (currentMenuData?.data?.menus) {
          for (const menu of currentMenuData.data.menus) {
            for (const category of menu.categories || []) {
              const fullItem = category.menuItemIds?.find(
                (item) => item._id === itemData._id
              );
              if (fullItem) {
                fullMenuItemData = fullItem;
                break;
              }
            }
            if (fullMenuItemData !== itemData) break;
          }
        }
      } catch (error) {
        console.error("Error fetching full menu item data:", error);
        // Fallback to the simplified data
      }
    }

    setSelectedMenuItem(fullMenuItemData);
    setSelectedCartItem(cartItemData); // Set cart item if provided
    setMenuDialogOpen(false); // Close menu dialog first

    // Check if this item exists in cart
    try {
      const locationId = conversationInfo?.businessLocationId?._id;
      const url = locationId 
        ? `${ApiEndPoints.CART.get}/${currentConversationRef.current}?locationId=${locationId}`
        : `${ApiEndPoints.CART.get}/${currentConversationRef.current}`;
      const response = await axiosInstance.get(url);
      const cartData = response.data;

      if (cartData?.data?.cart?.cart) {
        // Find existing cart item for this menu item
        const existingCartItem = cartData.data.cart.cart.find(
          (cartItem) => cartItem.menuItemId._id === itemData._id
        );

        if (existingCartItem) {
          setSelectedCartItem(existingCartItem);
        }
      }
    } catch (error) {
      // Continue with normal flow even if cart fetch fails
    }

    setItemOrderDetailOpen(true);
  };

  const handleCloseItemOrderDetail = () => {
    setItemOrderDetailOpen(false);
    setSelectedMenuItem(null);
    setSelectedCartItem(null);
  };

  const handleBackToMenu = () => {
    setItemOrderDetailOpen(false); // Close item detail dialog first
    setMenuDialogOpen(true);
  };

  // Handle sub-conversation menu open
  const handleSubConversationMenuOpen = (event, subConversation) => {
    setSubConversationMenuAnchor(event.currentTarget);
    setSelectedSubConversation(subConversation);
  };

  // Handle sub-conversation menu close
  const handleSubConversationMenuClose = () => {
    setSubConversationMenuAnchor(null);
    setSelectedSubConversation(null);
  };

  // Handle sub-conversation menu action
  const handleSubConversationMenuAction = (action) => {
    if (selectedSubConversation) {
      if (action === "view") {
        setInfoSubConversation(selectedSubConversation);

        handleOpenSubConversationInfo();
      } else if (action === "addMember") {
        setAddMemberSubConversation(selectedSubConversation);
        // Open add member dialog for sub-conversation
        setAddMemberDialogOpen(true);
      }
      // TODO: Implement other specific actions based on action type
    }
    handleSubConversationMenuClose();
  };

  const handleCategorySelect = async (category) => {
    try {
      // Get the main conversation ID from conversationInfo
      const mainConversationId = conversationInfo?._id;

      if (!mainConversationId) {
        toast.error("conversation id not available");
        return;
      }

      // Prepare the payload for createNestedChat API
      const payload = {
        mainConversationId: mainConversationId,
        categoryId: category._id,
      };

      // Call the createNestedChat API
      const response = await axiosInstance.post(
        ApiEndPoints.CHAT.createNestedChat,
        payload
      );

      toast.success("nested chat created  ");

      // Close the popup after successful creation
      handleCloseChatCategories();
    } catch (error) {
      toast.error("failed to create nested chat");
    }
  };

  // Group info panel handlers
  const handleOpenGroupInfo = () => {
    setGroupInfoState((prev) => ({
      ...prev,
      isOpen: true,
      isMinimized: false,
    }));
  };

  const handleCloseGroupInfo = () => {
    setGroupInfoState((prev) => ({ ...prev, isOpen: false }));
  };

  const handleMinimizeGroupInfo = () => {
    setGroupInfoState((prev) => ({
      ...prev,
      isMinimized: true,
      isOpen: false,
    }));
  };

  const handleFullscreenGroupInfo = () => {
    setGroupInfoState((prev) => ({
      ...prev,
      isFullscreen: !prev.isFullscreen,
    }));
  };

  // Sub-conversation info handlers
  const handleOpenSubConversationInfo = () => {
    setSubConversationInfoState((prev) => ({
      ...prev,
      isOpen: true,
      isMinimized: false,
    }));
  };

  const handleCloseSubConversationInfo = () => {
    setSubConversationInfoState((prev) => ({ ...prev, isOpen: false }));
    setInfoSubConversation(null);
  };

  // media upload functions
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    const validFiles = files.filter((file) => {
      const maxSize = 50 * 1024 * 1024; // 50MB limit
      if (file.size > maxSize) {
        toast.error(`${file.name} is too large. Maximum size is 50MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setMediaState((prev) => ({
        ...prev,
        selectedFiles: [...prev.selectedFiles, ...validFiles],
      }));
      toast.success(`${validFiles.length} file(s) selected`);
    }
  };

  // location dialog handlers
  const handleOpenLocationDialog = () => {
    setLocationDialogOpen(true);
  };

  // reset selected location when dialog closes
  useEffect(() => {
    if (!locationDialogOpen) {
      setSelectedLocation(null);
    }
  }, [locationDialogOpen]);

  // handle location selection from SelectLocation component
  const handleLocationSelect = async (locationData) => {
    if (!locationData || !locationData.latitude || !locationData.longitude) {
      toast.error("please select a valid location");
      return;
    }

    setSelectedLocation(locationData);
    
    // send location message
    // SelectLocation returns data with latitude/longitude, convert to lat/long for message model
    const messageText = locationData.physicalAddress || "";
    const latLong = {
      lat: locationData.latitude,
      long: locationData.longitude,
    };

    // send location message (SelectLocation component closes dialog automatically)
    await processMessageSending(messageText, [], "location", latLong);
  };

  // handle viewing location from message
  const handleViewLocation = (message) => {
    if (message?.type === "location" && message?.latLong) {
      setLocationViewData({
        latLong: message.latLong,
        location: message.location || message.text || "",
      });
      setLocationViewDialogOpen(true);
    }
  };

  const handleCloseLocationViewDialog = () => {
    setLocationViewDialogOpen(false);
    setLocationViewData(null);
  };

  // forward dialog handlers
  const handleOpenForwardDialog = (message) => {
    setForwardDialogState({
      open: true,
      message: message,
    });
  };

  const handleCloseForwardDialog = () => {
    setForwardDialogState({
      open: false,
      message: null,
    });
  };

  const handleOpenPaymentDialog = (message) => {
    const orderId =
      message?.orderId?._id || message?.orderId || orderDetailsState.orderId;

    if (!orderId) {
      toast.error("Order ID not found for payment");
      return;
    }

    // Calculate group member count - only count users with userType "user"
    let groupMemberCount = 1; // Default to 1 for one-to-one chats
    if (conversationInfo && conversationInfo.isGroup) {
      const { admin = [], users = [] } = conversationInfo;
      const allMembers = [...admin, ...users];

      // Remove duplicates based on user ID
      const uniqueMembers = allMembers.filter((member, index, self) => {
        const userId = member?.userId?._id;
        return (
          userId && self.findIndex((m) => m?.userId?._id === userId) === index
        );
      });

      // Count only members with userType "user" (exclude admins, team members, and beemzAi)
      const regularUsers = uniqueMembers.filter((member) => {
        // Check if member is beemzAi by userName
        const userName = member?.userId?.userName?.toLowerCase() || member?.userName?.toLowerCase();
        const isBeemzAi = userName === "beemzai" || userName === "beemz ai";
        
        // Exclude beemzAi even if it has userType "user"
        return member?.userType === "user" && !isBeemzAi;
      });
      groupMemberCount = regularUsers.length > 0 ? regularUsers.length : 1;
    }

    setPaymentDialogState({
      open: true,
      message: message,
      orderId: orderId,
      groupMemberCount: groupMemberCount,
    });
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogState({
      open: false,
      message: null,
      orderId: null,
      groupMemberCount: 1,
    });
  };

  // Payment flow dialog handlers
  const handleOpenPaymentStrategyDialog = (message) => {
    setPaymentStrategyDialogState({
      open: true,
      message: message,
      orderData: message?.orderId,
    });
  };

  const handleClosePaymentStrategyDialog = () => {
    setPaymentStrategyDialogState({
      open: false,
      message: null,
      orderData: null,
    });
  };

  const handleOpenPaymentRequestDialog = (message) => {
    setPaymentRequestDialogState({
      open: true,
      message: message,
    });
  };

  const handleClosePaymentRequestDialog = () => {
    setPaymentRequestDialogState({
      open: false,
      message: null,
    });
  };

  const handleOpenPaymentConfirmationDialog = (message) => {
    setPaymentConfirmationDialogState({
      open: true,
      message: message,
      orderData: message?.orderId,
      paymentType: message?.orderId?.paymentType,
    });
  };

  const handleClosePaymentConfirmationDialog = () => {
    setPaymentConfirmationDialogState({
      open: false,
      message: null,
      orderData: null,
      paymentType: null,
    });
  };

  const handlePaymentInitiated = async (paymentData) => {
    try {
      // Emit payment initiation event
      if (emit) {
        emit("initiatePayment", {
          orderId: paymentData.orderId,
          strategy: paymentData.strategy,
          initiatorId: paymentData.initiatorId,
          amounts: paymentData.amounts,
          expiresAt: paymentData.expiresAt,
        });
      }
    } catch (error) {
      console.error("Error initiating payment:", error);
    }
  };

  const handlePaymentResponse = async (id, response) => {
    try {
      // Emit payment response event
      if (emit) {
        await axiosInstance.post(ApiEndPoints.ORDER.respondToPayment(id), {
          response: response,
        });
        toast.success("Payment response sent  ");
      }
    } catch (error) {
      console.error("Error responding to payment:", error);
    }
  };

  const handleRetryPaymentRequest = async (retryData) => {
    try {
      // Emit retry payment request event
      if (emit) {
        emit("retryPaymentRequest", {
          orderId: retryData.orderId,
          initiatorId: retryData.initiatorId,
        });
      }
    } catch (error) {
      console.error("Error retrying payment request:", error);
    }
  };

  const handlePaymentProcessed = async (paymentData) => {
    try {
      // Emit payment processed event
      if (emit) {
        emit("processPayment", {
          orderId: paymentData.orderId,
          userId: paymentData.userId,
          amount: paymentData.amount,
          paymentMethod: paymentData.paymentMethod,
          paymentType: paymentData.paymentType,
        });
      }
      
      // Refetch conversationInfo to get updated businessLocationId after payment
      const conversationId = currentConversationRef.current;
      if (conversationId && emit) {
        emit("getConversationsInfo", { conversationId });
      }
      
      // Note: The effect watching conversationInfo.businessLocationId will automatically
      // trigger menu refetch when businessLocationId becomes available after conversationInfo updates
    } catch (error) {
      console.error("Error processing payment:", error);
    }
  };

  // Handle payment submission
  const handlePaymentSubmit = async (paymentType) => {
    const orderId = paymentDialogState.orderId;

    if (!orderId) {
      toast.error("Order ID is required for payment");
      return;
    }

    try {
      await axiosInstance.post(ApiEndPoints.ORDER.initiatePayment(orderId), {
        type: paymentType,
      });

      handleClosePaymentDialog();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to initiate payment"
      );
    }
  };

  // NEW: Reply handlers
  const handleReplyToMessage = (message) => {
    setReplyPreview({
      isActive: true,
      message: message,
    });
  };

  // Handle open notes action sheet - add to array if not already open
  const handleOpenNotes = (message) => {
    if (message?.miniConversationId) {
      setOpenNotesDialogs((prev) => {
        // Check if this dialog is already open
        const exists = prev.some(
          (dialog) => dialog.miniConversationId === message.miniConversationId
        );
        
        // If already open, don't add again
        if (exists) {
          return prev;
        }
        
        // Add new dialog to the array
        return [
          ...prev,
          {
            open: true,
            miniConversationId: message.miniConversationId,
            message: message,
          },
        ];
      });
    }
  };

  // Handle close notes action sheet - remove specific dialog by miniConversationId
  const handleCloseNotes = (miniConversationId) => {
    setOpenNotesDialogs((prev) =>
      prev.filter((dialog) => dialog.miniConversationId !== miniConversationId)
    );
  };

  const handleCancelReply = () => {
    setReplyPreview({
      isActive: false,
      message: null,
    });
  };

  const removeFile = (index) => {
    setMediaState((prev) => ({
      ...prev,
      selectedFiles: prev.selectedFiles.filter((_, i) => i !== index),
    }));
  };

  const getFileType = (file) => {
    if (file.type.startsWith("image/")) return "image";
    if (file.type.startsWith("video/")) return "video";
    if (file.type.startsWith("audio/")) return "audio";
    if (
      file.type.includes("pdf") ||
      file.type.includes("document") ||
      file.type.includes("text")
    )
      return "document";
    return "file";
  };

  const uploadMedia = async (files) => {
    if (files.length === 0) return [];

    setMediaState((prev) => ({
      ...prev,
      isUploading: true,
      uploadProgress: {},
    }));

    try {
      toast.loading("uploading media files...", { id: "mediaUpload" });

      // Use presigned URL upload via upload context
      const uploadResults = await uploadFiles(files, {
        conversationId: conversationInfo?.conversationId,
      });

      // Transform upload results to match message model structure
      const attachments = uploadResults.map((result) => {
        // Determine file type from category or file type
        let fileType = "document";
        if (result.fileType) {
          if (result.fileType.startsWith("image/")) fileType = "image";
          else if (result.fileType.startsWith("video/")) fileType = "video";
          else if (result.fileType.startsWith("audio/")) fileType = "audio";
          else fileType = "document";
        } else if (result.type) {
          fileType = result.type;
        }

        return {
          type: fileType,
          url: result.fileUrl || result.fileKey,
          fileName: result.fileName || null,
          size: result.fileSize || null,
          thumbnail: null,
          duration: null,
        };
      });

      toast.success("media uploaded successfully", { id: "mediaUpload" });
      return attachments;
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(
        error?.message || error?.response?.data?.message || "failed to upload media",
        { id: "mediaUpload" }
      );
      return [];
    } finally {
      setMediaState((prev) => ({
        ...prev,
        isUploading: false,
        uploadProgress: {},
      }));
    }
  };

  // FIXED: State for selected subconversation
  const [selectedSubConversationId, setSelectedSubConversationId] =
    useState(null);

  // FIXED: Use ref to track current sub-conversation ID to avoid stale closures in listeners
  // This ensures the listener always has access to the current sub-conversation ID
  const selectedSubConversationIdRef = useRef(null);

  // Handle subconversation selection change
  // FIXED: This function manages switching between main conversation and sub-conversation views
  const handleSubConversationSelectionChange = (subConvId) => {
    // FIXED: Update the selected sub-conversation ID
    setSelectedSubConversationId(subConvId);
    // FIXED: Also update ref to avoid stale closures in listeners
    selectedSubConversationIdRef.current = subConvId;

    if (subConvId) {
      // FIXED: Sub-conversation selected - fetch its messages

      if (!emit) {
        toast.error("socket connection error. please try again.");
        return;
      }

      if (!isConnected) {
        toast.error("socket not connected. please wait and try again.");
        return;
      }

      try {
        // FIXED: Clear existing messages before fetching sub-conversation messages
        // This prevents showing main conversation messages while sub-conversation is active
        setChatState((prev) => ({
          ...prev,
          messages: [],
          hasMoreMessages: false,
          isLoadingMore: false,
        }));

        // FIXED: Clear processed message IDs when switching sub-conversations
        // This ensures we start fresh and don't have stale duplicate checks
        processedMessageIds.current.clear();

        // Clear reply preview when switching to sub-conversation
        setReplyPreview({
          isActive: false,
          message: null,
        });

        const emitResult = emit("getMessagesSubConversation", {
          conversationId: subConvId,
        });

        if (emitResult) {
        } else {
          toast.error(
            "failed to fetch sub-conversation messages. please try again."
          );
        }
      } catch (error) {
        toast.error(
          "error fetching sub-conversation messages. please try again."
        );
      }
    } else {
      // FIXED: No sub-conversation selected - show main conversation messages
      if (selectedContact && !selectedContact.isNewConversation) {
        getMessages(selectedContact.id);

        // Clear reply preview when returning to main conversation
        setReplyPreview({
          isActive: false,
          message: null,
        });
      }
    }
  };

  // FIXED: Clear sub-conversation selection when main conversation changes
  // This ensures we don't show sub-conversation messages when switching to a different conversation
  useEffect(() => {
    // Clear sub-conversation selection
    setSelectedSubConversationId(null);
    // FIXED: Also update ref to avoid stale closures
    selectedSubConversationIdRef.current = null;

    // Clear sub-conversations when switching to one-to-one chat or different group
    if (selectedContact) {
      const isCurrentGroup = selectedContact.isGroup;
      const hasCurrentSubConversations = subConversations.length > 0;

      // If switching to one-to-one chat or different group, clear sub-conversations
      if (
        !isCurrentGroup ||
        (conversationInfo && conversationInfo.groupType !== "business")
      ) {
        if (hasCurrentSubConversations) {
          setSubConversations([]);
        }
      }
    }

    // Also clear any sub-conversation messages and return to main conversation messages
    if (selectedContact && !selectedContact.isNewConversation) {
      getMessages(selectedContact.id);

      // Clear reply preview when main conversation changes
      setReplyPreview({
        isActive: false,
        message: null,
      });
    }
  }, [
    selectedContact?.id,
    conversationInfo?._id,
    conversationInfo?.groupType,
    subConversations.length,
  ]);

  // NEW: Reply preview state
  const [replyPreview, setReplyPreview] = useState({
    isActive: false,
    message: null,
  });

  // state for message mentions
  const [messageMentions, setMessageMentions] = useState([]);

  // AI streaming state
  const [aiStreamingMessage, setAiStreamingMessage] = useState(null);
  const [isAiStreaming, setIsAiStreaming] = useState(false);

  // handle order details dialog
  const handleOpenOrderDetails = (message) => {
    // Check if order status is draft - show cart dialog instead
    if (message?.orderId?.status === "draft") {
      // Open cart dialog for draft orders
      setCartDialogOpen(true);
      // Fetch cart data when opening cart dialog - use sub-conversation ID if available
      const conversationIdToUse = selectedSubConversationId || currentConversationRef.current;
      if (conversationIdToUse) {
        fetchCartData(conversationIdToUse);
      }
      return;
    }

    // Extract order ID from message
    const orderId = message?.orderId?._id || message?.orderId;

    if (!orderId) {
      toast.error("order ID not found in message");
      return;
    }

    // Determine current user's role in the conversation
    // Check both admin and users arrays to find user type
    const userType =
      conversationInfo?.admin?.find(
        (member) => member.userId?._id === currentUserId
      )?.userType ||
      conversationInfo?.users?.find(
        (member) => member.userId?._id === currentUserId
      )?.userType;

    // If user is owner/teamMember, always open BusinessOrderDetailsDialog
    // This allows business owners and team members to view orders with business-specific features
    if (userType === "owner" || userType === "teamMember") {
      setCancellationDetailsState({
        open: true,
        orderId: orderId,
        messageType: message?.type, // Pass message type for dialog context
      });
    } else {
      // Otherwise open regular OrderDetailsDialog for regular users
      setOrderDetailsState({
        open: true,
        orderId: orderId,
      });
    }
  };

  const handleCloseOrderDetails = () => {
    setOrderDetailsState({ open: false, orderId: null });
  };

  const handleCloseCancellationDetails = () => {
    setCancellationDetailsState({
      open: false,
      orderId: null,
      messageType: null,
    });
  };
  // Add member dialog handlers
  // const handleOpenAddMemberDialog = () => {
  //   setAddMemberDialogOpen(true);
  // };

  const handleCloseAddMemberDialog = () => {
    setAddMemberDialogOpen(false);
    setAddMemberSubConversation(null);
  };

  const handleCloseParticipantSelectionDialog = () => {
    setParticipantSelectionDialogOpen(false);
  };

  // Handler for exit group confirmation dialog
  const handleCloseExitGroupDialog = () => {
    setExitGroupDialogOpen(false);
  };

  // Handler for exiting from group
  const handleExitGroup = async () => {
    try {
      const conversationId = selectedContact?.id || conversationInfo?._id;
      
      if (!conversationId) {
        toast.error("Conversation ID is required");
        return;
      }

      const response = await axiosInstance.put(
        ApiEndPoints.CONVERSATION.exitFromGroup(conversationId)
      );

      if (response.data?.data?.success !== false) {
        toast.success("you have been exited from group");
        setExitGroupDialogOpen(false);
        
        // Refresh conversation list to remove the exited group
        getConversations();
        
        // Close the conversation view after exiting
        if (onClose) {
          onClose();
        }
      } else {
        throw new Error(response.data?.message || "Failed to exit group");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Failed to exit group. Please try again."
      );
    }
  };

  // Handler for clear chat confirmation dialog
  const handleCloseClearChatDialog = () => {
    setClearChatDialogOpen(false);
  };

  // Handler for deleting group (remove from list when user already exited or was removed)
  const handleDeleteGroup = async () => {
    try {
      const conversationId = selectedContact?.id || conversationInfo?._id;
      if (!conversationId) {
        toast.error("Conversation ID is required");
        return;
      }
      const response = await axiosInstance.delete(
        ApiEndPoints.CONVERSATION.deleteConversation(conversationId)
      );
      if (response.data?.status === "success") {
        toast.success("conversation removed");
        getConversations();
        if (onClose) onClose();
      } else {
        throw new Error(response.data?.message || "Failed to remove conversation");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Failed to remove conversation. Please try again."
      );
    }
  };

  // Handler for clearing chat
  const handleClearChat = async () => {
    try {
      const conversationId = selectedContact?.id || conversationInfo?._id;
      
      if (!conversationId) {
        toast.error("Conversation ID is required");
        return;
      }

      const response = await axiosInstance.post(
        ApiEndPoints.CONVERSATION.clearChat,
        { conversationId }
      );

      if (response.data?.status === "success") {
        toast.success("chat cleared");
        setClearChatDialogOpen(false);
        
        // Refresh conversation list to update the UI
        getConversations();
        
        // Clear current messages from view
        setChatState((prev) => ({
          ...prev,
          messages: [],
        }));
      } else {
        throw new Error(response.data?.message || "Failed to clear chat");
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Failed to clear chat. Please try again."
      );
    }
  };

  // Handler for adding members to regular groups (Group type)
  const handleAddMembersToRegularGroup = async (selectedUsers, conversationId) => {
    try {
      const conversationIdToUse = conversationId || selectedContact?.id;
      
      if (!conversationIdToUse) {
        throw new Error("Conversation ID is required");
      }

      // Add each user one by one using the new API endpoint
      const addPromises = selectedUsers.map((user) =>
        axiosInstance.post(ApiEndPoints.CONVERSATION.addMemberToConversation, {
          conversationId: conversationIdToUse,
          userId: user._id,
        })
      );

      const results = await Promise.all(addPromises);
      
      // Check if all requests were successful
      const allSuccessful = results.every(
        (response) => response.data?.data?.success !== false
      );

      if (allSuccessful) {
        // Refresh conversation info to get updated member list
        if (conversationInfo) {
          queryClient.invalidateQueries(["conversation", conversationIdToUse]);
        }
        
        toast.success(`${selectedUsers.length} member(s) added  `);
        return { success: true };
      } else {
        throw new Error("Some members could not be added");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to add members";
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleAddMembers = async (selectedUsers, conversationId) => {
    try {
      // API call to add members to the group
      const response = await axiosInstance.post(
        `${ApiEndPoints.CONVERSATION.addMembers}/${conversationId}`,
        {
          userIds: selectedUsers.map((user) => user._id),
        }
      );

      if (response.data.data.success) {
        // refresh conversation info to get updated member list
        if (conversationInfo) {
          // trigger a refetch of conversation data
          queryClient.invalidateQueries(["conversation", conversationId]);
        }

        return response.data;
      } else {
        throw new Error(response.data.message || "failed to add members");
      }
    } catch (error) {
      throw error;
    }
  };

  // Handle adding members to sub-conversation
  // Function to remove mentions from message text
  const removeMentionsFromText = (text) => {
    // Remove @username patterns from the text
    return text.replace(/(@[\p{L}\p{N}._-]+|<:[\p{L}\p{N}._-]+>)\s*/gu, "").trim();
  };

  // Function to remove @ mentions from text for AI history (more comprehensive)
  const removeMentionsFromHistory = (text) => {
    if (!text) return "";
    // Remove @ mentions from the text (handles patterns like "@Lucky ", "@username", etc.)
    // Pattern matches @ followed by word characters (letters, numbers, underscore) and optional whitespace
    // This removes all @ mentions throughout the message
    let cleaned = text.replace(/(@[\p{L}\p{N}._-]+|<:[\p{L}\p{N}._-]+>)\s*/gu, "").trim();
    // Also remove any leading @mention pattern that might have been missed
    // This handles edge cases where mentions might have special formatting
    cleaned = cleaned.replace(/\s{2,}/g, "").trim();
    return cleaned;
  };

  // Function to check if message contains beemzAi mention
  const hasBeemzAiMention = (text, mentions) => {
    // Check if beemzAi is mentioned in the text or mentions array
    return (
      text.includes("@beɘmz.ai") ||
      text.includes("@beemzAi") ||
      text.includes("@beemzai") ||
      mentions.some(
        (mention) =>
          mention.name?.toLowerCase() === "beɘmz.ai" ||
          mention.name?.toLowerCase() === "beemzAi" ||
          mention.userName?.toLowerCase() === "beemzai"
      )
    );
  };

  // Function to check if AI user is present in the group
  const isAiUserPresentInGroup = () => {
    const info = conversationInfoRef.current ?? conversationInfo;
    if (!info) return false;

    const { admin = [], users = [] } = info;
    const allMembers = [...admin, ...users];
    const aiUserId = process.env.REACT_APP_AI_USER_ID;

    // Remove duplicates based on user ID to prevent counting the same user multiple times
    const uniqueMembers = allMembers.filter((member, index, self) => {
      const userId = member?.userId?._id;
      return (
        userId && self.findIndex((m) => m?.userId?._id === userId) === index
      );
    });

    const isPresent = uniqueMembers.some((member) => {
      const memberId = member?.userId?._id;
      return memberId === aiUserId;
    });

    return isPresent;
  };

  // Function to count customer members: only members with userType "user" (excluding AI, owner, teamMember)
  const getCustomerMemberCount = () => {
    const info = conversationInfoRef.current ?? conversationInfo;
    if (!info) return 0;

    const { admin = [], users = [] } = info;
    const allMembers = [...admin, ...users];
    const aiUserId = process.env.REACT_APP_AI_USER_ID;

    // Remove duplicates based on user ID (keep first occurrence)
    const uniqueMembers = allMembers.filter((member, index, self) => {
      const userId = member?.userId?._id;
      return (
        userId && self.findIndex((m) => m?.userId?._id === userId) === index
      );
    });

    // Count only members with userType "user" (single/multiple "user" type = customer count)
    const customerMembers = uniqueMembers.filter((member) => {
      const memberId = member?.userId?._id;
      const userType = member?.userType;
      const userName = (member?.userId?.userName || member?.userName || "").toLowerCase();
      const fullName = (member?.userId?.fullName || member?.fullName || "").toLowerCase();

      // Exclude AI user (by id or by name)
      if (memberId === aiUserId) return false;
      if (userName === "beɘmz.ai" || userName === "beemzai" || fullName === "beɘmz.ai" || fullName === "beemzai") return false;

      return userType === "user";
    });

    return customerMembers.length;
  };

  // Function to get beemzAi user ID
  const getBeemzAiUserId = useCallback(() => {
    // First, try to get from environment variable (static AI user ID)
    const envAiUserId = process.env.REACT_APP_AI_USER_ID;
    if (envAiUserId) {
      return envAiUserId;
    }

    // Get the actual beemzAi user ID from conversation members
    if (conversationInfo?.users && Array.isArray(conversationInfo.users)) {
      const beemzAiUser = conversationInfo.users.find((user) => {
        const userName =
          user?.userId?.userName?.toLowerCase() ||
          user?.userName?.toLowerCase();
        const fullName =
          user?.userId?.fullName?.toLowerCase() ||
          user?.fullName?.toLowerCase();

        return userName === "beemzai" || fullName === "beemzai";
      });

      if (beemzAiUser) {
        const userId =
          beemzAiUser.userId?._id || beemzAiUser._id || beemzAiUser.userId;
        return userId;
      }
    }

    // Also check in admin array
    if (conversationInfo?.admin && Array.isArray(conversationInfo.admin)) {
      const beemzAiAdmin = conversationInfo.admin.find((user) => {
        const userName =
          user?.userId?.userName?.toLowerCase() ||
          user?.userName?.toLowerCase();
        const fullName =
          user?.userId?.fullName?.toLowerCase() ||
          user?.fullName?.toLowerCase();

        return userName === "beemzai" || fullName === "beemzai";
      });

      if (beemzAiAdmin) {
        const userId =
          beemzAiAdmin.userId?._id || beemzAiAdmin._id || beemzAiAdmin.userId;
        return userId;
      }
    }

    // Fallback to static ID if not found in conversation (default hardcoded value)
    const fallbackId = "68ec9178dcf34df3f7c9e11d";
    return fallbackId;
  }, [conversationInfo]);

  // Function to get full beemzAi user object (with fullName, userName, etc.)
  const getBeemzAiUserObject = useCallback(() => {
    const aiUserId = process.env.REACT_APP_AI_USER_ID;

    // Try to find the AI user in conversation members first
    if (conversationInfo) {
      const { admin = [], users = [] } = conversationInfo;
      const allMembers = [...admin, ...users];

      // Remove duplicates
      const uniqueMembers = allMembers.filter((member, index, self) => {
        const userId = member?.userId?._id;
        return (
          userId && self.findIndex((m) => m?.userId?._id === userId) === index
        );
      });

      const beemzAiMember = uniqueMembers.find((member) => {
        const memberId = member?.userId?._id;
        return memberId === aiUserId;
      });

      if (beemzAiMember && beemzAiMember.userId) {
        return beemzAiMember.userId;
      }
    }

    // Fallback to a default AI user object
    const fallbackAiUser = {
      _id: aiUserId,
      userName: "beemzai",
      fullName: "beemzAi",
      email: "beemzai@example.com",
    };

    return fallbackAiUser;
  }, [conversationInfo]);

  // Test AI endpoint on component mount - REMOVED automatic test call
  useEffect(() => {
    // Removed automatic test endpoint call to prevent unwanted API calls on page refresh

    // Make functions available globally for testing
    window.testAiManually = () => {
      handleAiStreaming(
        "test message",
        "Order",
        conversationInfo?.businessLocationId?._id ?? conversationInfo?.businessLocationId
      );
    };

    window.testMentionDetection = (text) => {
      return {
        result: hasBeemzAiMention(text, []),
        cleanedText: removeMentionsFromText(text),
      };
    };

    window.forceAiResponse = () => {
      const conversationCase = getConversationCase();
      handleAiStreaming(
        "test message for AI",
        conversationCase,
        conversationInfo?.businessLocationId?._id ?? conversationInfo?.businessLocationId
      );
    };

    window.testWithActualMessage = () => {
      const messageText = "@beɘmz.ai can you please help me for order any thali";
      const hasMention = hasBeemzAiMention(messageText, []);
      const cleanedText = removeMentionsFromText(messageText);

      if (hasMention && cleanedText.trim()) {
        const conversationCase = getConversationCase();
        handleAiStreaming(
          cleanedText,
          conversationCase,
          conversationInfo?.businessLocationId?._id ?? conversationInfo?.businessLocationId
        );
      }
    };

    window.testCorsIssue = async () => {
      const aiEndpoint = process.env.REACT_APP_AI_URL;

      try {
        await fetch(
          process.env.NODE_ENV === "development"
            ? "/chat"
            : `${aiEndpoint}/chat`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              message: "test",
              case: "Order",
            }),
          }
        );
      } catch (error) {
        // Error handling
      }
    };

    window.testDirectAI = () => {
      const conversationCase = getConversationCase();
      handleAiStreaming(
        "test message for direct AI call",
        conversationCase,
        conversationInfo?.businessLocationId?._id ?? conversationInfo?.businessLocationId
      );
    };

    window.debugConversationInfo = () => {
      const beemzAiUser = conversationInfo?.users?.find(
        (user) =>
          user?.userId?.userName?.toLowerCase() === "beemzai" ||
          user?.userId?.fullName?.toLowerCase() === "beemzai" ||
          user?.userName?.toLowerCase() === "beemzai" ||
          user?.fullName?.toLowerCase() === "beemzai"
      );

      return {
        conversationInfo,
        beemzAiUser,
        beemzAiUserId: getBeemzAiUserId(),
      };
    };

    window.testStreamingFormat = async () => {
      const url = process.env.REACT_APP_AI_URL + "/chat";

      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            message: "test streaming format",
            case: "Order",
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");

        let chunkCount = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          decoder.decode(value, { stream: true });
          chunkCount++;

          if (chunkCount > 5) break; // Limit for testing
        }
      } catch (error) {
        // Error handling
      }
    };

    window.testBeemzAiUserId = () => {
      return getBeemzAiUserId();
    };

    window.setBeemzAiUserId = (userId) => {
      window.manualBeemzAiUserId = userId;

      // Override the function temporarily
      window.getBeemzAiUserId = () => {
        return userId;
      };
    };

    // New debugging functions for AI logic
    window.debugAiLogic = () => {
      const debugInfo = {
        aiUserId: process.env.REACT_APP_AI_USER_ID,
        isAiPresent: isAiUserPresentInGroup(),
        customerCount: getCustomerMemberCount(),
        conversationInfo,
      };

      if (conversationInfo) {
        const { admin = [], users = [] } = conversationInfo;
        const allMembers = [...admin, ...users];
        debugInfo.members = allMembers.map((m) => ({
          id: m?.userId?._id,
          userName: m?.userId?.userName,
          userType: m?.userType,
          fullName: m?.userId?.fullName,
        }));
      }

      return debugInfo;
    };

    window.testAiTriggerLogic = (messageText) => {
      const hasAiMention = hasBeemzAiMention(messageText, []);
      const isAiPresent = isAiUserPresentInGroup();
      const customerCount = getCustomerMemberCount();

      let shouldTriggerAI = false;
      let reason = "";

      if (isAiPresent) {
        if (customerCount === 1) {
          shouldTriggerAI = true;
          reason = "AI AUTO-TRIGGER (single customer)";
        } else if (customerCount > 1) {
          shouldTriggerAI = hasAiMention;
          reason = `AI MENTION-TRIGGER (multiple customers): ${hasAiMention}`;
        } else {
          shouldTriggerAI = false;
          reason = "NO TRIGGER (no customers)";
        }
      } else {
        shouldTriggerAI = false;
        reason = "NO TRIGGER (AI not present)";
      }

      return {
        shouldTriggerAI,
        reason,
        hasAiMention,
        isAiPresent,
        customerCount,
      };
    };

    // Comprehensive debug function for the current issue
    window.debugCurrentIssue = () => {
      const debugInfo = {
        currentUserId,
        aiUserId: process.env.REACT_APP_AI_USER_ID,
        conversationInfo,
      };

      if (conversationInfo) {
        const { admin = [], users = [] } = conversationInfo;
        const allMembers = [...admin, ...users];

        debugInfo.members = allMembers.map((member, index) => {
          const user = member?.userId;
          return {
            index: index + 1,
            id: user?._id,
            userName: user?.userName,
            fullName: user?.fullName,
            userType: member?.userType,
            isCurrentUser: user?._id === currentUserId,
            isAiUser: user?._id === process.env.REACT_APP_AI_USER_ID,
            isInAdminArray: admin.includes(member),
            isInUsersArray: users.includes(member),
          };
        });

        const isAiPresent = isAiUserPresentInGroup();
        const customerCount = getCustomerMemberCount();

        debugInfo.isAiPresent = isAiPresent;
        debugInfo.customerCount = customerCount;

        if (isAiPresent && customerCount === 1) {
          debugInfo.expectedBehavior = "SHOULD AUTO-RESPOND to every message";
        } else if (isAiPresent && customerCount > 1) {
          debugInfo.expectedBehavior =
            "SHOULD ONLY RESPOND when @beɘmz.ai is mentioned";
        } else if (!isAiPresent) {
          debugInfo.expectedBehavior = "AI NOT PRESENT - no responses";
        } else {
          debugInfo.expectedBehavior = "UNKNOWN STATE";
        }
      }

      return debugInfo;
    };

    // Debug function for AI user object
    window.debugAiUserObject = () => {
      const aiUserObject = getBeemzAiUserObject();
      return {
        aiUserObject,
        fullName: aiUserObject?.fullName,
        userName: aiUserObject?.userName,
        _id: aiUserObject?._id,
      };
    };
  }, []);

  // Function to get conversation case/category
  const getConversationCase = () => {
    // Check if we're in a sub-conversation
    if (selectedSubConversationId) {
      const selectedSubConv = subConversations.find(
        (sc) => sc._id === selectedSubConversationId
      );
      return selectedSubConv?.categoryId?.name || "General";
    }

    // Check main conversation category
    return conversationInfo?.chatCategory?.name || "General";
  };

  // AI streaming handler
  const handleAiStreaming = useCallback(async (
    messageText,
    conversationCase,
    businessLocationId
  ) => {
    try {
      // Check AI request limit before proceeding
      if (!businessLocationId) {
        toast.error("Business location not available");
        return;
      }

      try {
        const limitResponse = await axiosInstance.get(
          ApiEndPoints.CHAT.getAiRequestCount(businessLocationId)
        );
        console.log("limitResponse", limitResponse);

        if (
          limitResponse.data.status === "success" &&
          limitResponse.data.data
        ) {
          const { limitStatus } = limitResponse.data.data.data;

          // Check if any AI limit is reached
          if (limitStatus && limitStatus.anyLimitReached) {
            toast.error(
              "AI request limit has been reached for your current plan"
            );
            return;
          }
        }
      } catch (error) {
        console.error("Failed to check AI request limit:", error);
        toast.error("Failed to check AI request limit. Please try again.");
        return;
      }

      setIsAiStreaming(true);

      // Removed automatic endpoint test to prevent unwanted test API calls

      // Create temporary AI message for streaming
      // Generate a valid MongoDB ObjectId for the message
      const aiMessageId = new ObjectId().toString();
      const aiUserObject = getBeemzAiUserObject();

      // Create temporary AI message for streaming (show animation for both main and sub-conversations)
      const aiTempMessage = {
        id: aiMessageId,
        _id: aiMessageId,
        text: "...",
        type: "text",
        senderId: aiUserObject, // Use full user object instead of just ID
        sender: aiUserObject.fullName || aiUserObject.userName || "beemzAi",
        timestamp: new Date().toLocaleString([], {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
        isOwn: false,
        isAiStreaming: true,
        isTemp: true,
        reactions: [],
        deliveredTo: [],
        readBy: [],
        isDeletedForMe: [],
        isDeletedForEveryone: false,
        isEdited: false,
        isForwarded: false,
        forwardedFrom: null,
        forwardedBy: [],
        editedBy: [],
      };

      // Add temporary AI message to chat (for both main conversation and sub-conversations)
      // The temp message will be replaced when the real message arrives from socket
      setChatState((prev) => ({
        ...prev,
        messages: [...prev.messages, aiTempMessage],
      }));

      setAiStreamingMessage(aiTempMessage);

      // Get conversation history for context as plain text
      // Filter to only include messages from current user and AI responses that mention current user
      // This ensures personalized context for each user in group conversations
      // Use fullName for mentions (prioritize fullName over userName)
      const currentUserName =
        user?.fullName || user?.userName || user?.name || null;
      const aiUserId = getBeemzAiUserId();

      const historyMessages = chatState.messages
        .filter((msg) => {
          // Exclude system messages
          if (msg.type === "add-remove") return false;

          // Include messages from current user
          if (msg.isOwn) return true;

          // For AI messages only, check if they mention the current user
          const msgSenderId = msg.senderId?._id || msg.senderId || null;
          const isAiMessage =
            msgSenderId && String(msgSenderId) === String(aiUserId);

          if (isAiMessage && currentUserId && currentUserName) {
            const messageText = msg.text || msg.message || "";
            const messageMentions = msg.mentions || [];

            // Get all possible username variations for matching (prioritize fullName)
            const userNameVariations = [
              user?.fullName,
              user?.userName,
              user?.name,
              currentUserName,
            ]
              .filter(Boolean)
              .map((name) => name.toLowerCase());

            // Check if message mentions current user in mentions array
            const mentionsCurrentUser = messageMentions.some((mention) => {
              const mentionUserId = mention.userId?._id || mention.userId;
              // Prioritize fullName when checking mention name
              const mentionName =
                mention.name || mention.fullName || mention.userName;

              return (
                mentionUserId === currentUserId ||
                String(mentionUserId) === String(currentUserId) ||
                userNameVariations.includes(mentionName?.toLowerCase())
              );
            });

            // Check if message text starts with @fullName for current user (case-insensitive)
            const messageTextLower = messageText.trim().toLowerCase();
            const textMentionsCurrentUser = userNameVariations.some(
              (userNameVar) => messageTextLower.startsWith(`@${userNameVar}`)
            );

            return mentionsCurrentUser || textMentionsCurrentUser;
          }

          return false;
        })
        .slice(-20) // Get last 20 filtered messages
        .map((msg) => {
          const role = msg.isOwn ? "user" : "assistant";
          let content = msg.text || msg.message || "";
          // Remove @ mentions from content before adding to history
          content = removeMentionsFromHistory(content);
          return `${role}: ${content}`;
        })
        .filter((msg) => msg.split(": ")[1]); // Filter out empty messages

      // Add the current user message to history (no need to remove mentions as it's user input)
      const currentMessage = `user: ${messageText}`;
      const conversationHistory = [...historyMessages, currentMessage].join(
        ". "
      );

      // console.log("conversationHistory", conversationHistory);
      // Make API call to AI endpoint
      // const aiEndpoint = process.env.NODE_ENV === 'development' ? '' : (AI_BASE_URL || "http://45.248.33.161:5015");
      const url = process.env.REACT_APP_AI_URL + "/chat";
      // const url = `http://localhost:3000/chat`;
      const aiModel = conversationInfo?.aiModel || "gpt-4o-mini";
      const payLoad = JSON.stringify({
        message: conversationHistory, //messageText,//conversationHistory ,//+ "user: "+
        case: conversationCase,
        business_id: businessLocationId,
        conversation_id: selectedContact.id,
        user_id: currentUserId,
        ai_model: aiModel,
        // conversation_history: conversationHistory, // Include conversation history
      });
      console.log("payLoad-->", payLoad);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payLoad,
        mode: "cors", // Explicitly set CORS mode
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("AI API error:", response.status, errorText);
        throw new Error(`AI API error: ${response.status} - ${errorText}`);
      }

      // Check if streaming response is supported
      if (!response.body) {
        throw new Error("Response body is null - streaming not supported");
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder("utf-8");

      let fullText = "";
      let imageData = null;
      let currentEvent = null;

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter(Boolean);

        for (const line of lines) {
          try {
            // Check for event type
            if (line.startsWith("event: ")) {
              currentEvent = line.substring(7).trim(); // Remove 'event: ' prefix
              continue;
            }

            // Handle Server-Sent Events format
            if (line.startsWith("data: ")) {
              const jsonData = line.substring(6); // Remove 'data: ' prefix
              const data = JSON.parse(jsonData);

              // Handle image_names event
              if (currentEvent === "image_names" && data.entries) {
                imageData = data.entries;
                console.log("Received image data:", imageData);
              }
              // Handle regular message chunks
              else if (data.chunk) {
                const chunkText = data.chunk;
                fullText += chunkText; // accumulate without per-character animation
              }
            } else {
              // Try direct JSON parsing for non-SSE format
              const data = JSON.parse(line);

              if (data.chunk) {
                const chunkText = data.chunk;
                fullText += chunkText; // accumulate without per-character animation
              }
            }
          } catch (error) {
            // Skip malformed chunks
            console.warn("Skipping malformed chunk:", line, error);
          }
        }
      }

      // Store AI menu images in ref for later retrieval
      if (imageData) {
        aiMenuImagesRef.current.set(aiMessageId, imageData);
        console.log(
          "Stored AI menu images for message:",
          aiMessageId,
          imageData
        );
      }

      // Streaming complete - create final message object
      // Include mention of the user who asked the question
      const userName = user?.fullName || user?.userName || user?.name || null;
      const userMention =
        currentUserId && user
          ? {
              userId: currentUserId,
              name: user.fullName || user.userName || user.name || "User",
            }
          : null;

      // Remove markdown image syntax from fullText if we have imageData
      // This prevents markdown syntax from appearing as plain text in chat
      let cleanedText = fullText;
      if (imageData && imageData.length > 0) {
        const markdownImageRegex = /!\[([^\]]*)\]\(([^)]+)\)/g;
        cleanedText = fullText.replace(markdownImageRegex, '').trim();
        // Clean up extra whitespace and newlines
        cleanedText = cleanedText.replace(/\n\s*\n\s*\n/g, '\n\n'); // Max 2 consecutive newlines
        cleanedText = cleanedText.replace(/ {2,}/g, ' '); // Multiple spaces to single
      }

      // Prepend user mention to the message text (e.g., "@aish Here is the menu for you:")
      const messageWithMention = userName
        ? `@${userName} ${cleanedText}`
        : cleanedText;

      const conversationId = selectedContact.isNewConversation ? null : selectedSubConversationId ? selectedSubConversationId : selectedContact.id
      const items = ["placed"];
      const matches = items.filter(item =>
        fullText.toLowerCase().includes(item)
      );
      const isOrder = matches.length > 0 ? true : false

      const finalAiMessage = {
        _id: aiMessageId,
        senderId: getBeemzAiUserId(),
        conversationId: conversationId,
        conversationType: selectedSubConversationId
          ? "SubConversation"
          : "Conversation",
        type: isOrder ? "order" : "text",
        message: messageWithMention, // Include @username at the beginning of the message
        attachments: [],
        mentions: userMention ? [userMention] : [], // Tag the user who asked the question
        reactions: [],
        replyTo: null,
        repliedBy: [],
        forwardedFrom: null,
        forwardedBy: [],
        isDeletedForMe: [],
        isDeletedForEveryone: false,
        isEdited: false,
        editedBy: [],
        deliveredTo: [],
        readBy: [],
        location: null,
        aiMenuImages: imageData, // Include AI menu images data
      };

      // Send AI message to server
      if (selectedContact?.isNewConversation) {
        finalAiMessage.receiverId = selectedContact.id;
      }
      let success = false;

      if (selectedSubConversationId) {
      
        // Prepare sub-conversation message payload
        const subConversationPayload = {
          _id: aiMessageId,
          senderId: getBeemzAiUserId(),
          conversationId: selectedSubConversationId,
          conversationType: "SubConversation",
          type: "text",
          message: messageWithMention, // Include @username at the beginning of the message
          attachments: [],
          mentions: userMention ? [userMention] : [], // Tag the user who asked the question
          reactions: [],
          replyTo: null,
          repliedBy: [],
          forwardedFrom: null,
          forwardedBy: [],
          isDeletedForMe: [],
          isDeletedForEveryone: false,
          isEdited: false,
          editedBy: [],
          deliveredTo: [],
          readBy: [],
          location: null,
          aiMenuImages: imageData,
        };
        success = sendMessageOnSubConversation(subConversationPayload);
      } else {
        // Regular conversation message

        success = sendMessage(finalAiMessage);
      }
      // Emit AI message via socket
      // const success = sendMessage(finalAiMessage);

      if (success) {
        let orderId = null
        let orderNumber = ""

        if (isOrder) {
          const matchOrderNumber = fullText.match(/ord-\d{8}-\d{4}/i);
          orderNumber = matchOrderNumber ? matchOrderNumber[0].trim().toUpperCase() : null;

          if (orderNumber) {
            const res = await axiosInstance.get(
              ApiEndPoints.ORDER.getOrderByNumber(orderNumber, conversationId)
            );

            const order_response = res.data;
            orderId = order_response.orderId ?? null;
          }
        }

        // Replace temp message with final AI message in UI immediately so response shows without refresh.
        // If the same message later arrives via socket, duplicate check by _id will keep one copy.
        const aiUserObject = getBeemzAiUserObject();
        const displayAiMessage = {
          id: aiMessageId,
          _id: aiMessageId,
          text: messageWithMention,
          message: messageWithMention,
          type: finalAiMessage.type || "text",
          senderId: aiUserObject,
          sender: aiUserObject?.fullName || aiUserObject?.userName || "beemzAi",
          timestamp: new Date().toLocaleString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
          isOwn: false,
          isTemp: false,
          isAiStreaming: false,
          reactions: finalAiMessage.reactions || [],
          attachments: finalAiMessage.attachments || [],
          mentions: finalAiMessage.mentions || [],
          conversationId: finalAiMessage.conversationId,
          conversationType: finalAiMessage.conversationType,
          deliveredTo: [],
          readBy: [],
          aiMenuImages: imageData,
        };

        setChatState((prev) => ({
          ...prev,
          messages: prev.messages.map((msg) =>
            msg._id === aiMessageId ? displayAiMessage : msg
          ),
        }));

        // Mark as processed so if the same message arrives via socket later, we don't duplicate
        if (typeof processedMessageIds?.current?.add === "function") {
          processedMessageIds.current.add(aiMessageId);
        }
        if (imageData) {
          aiMenuImagesRef.current.set(aiMessageId, imageData);
        }
      } else {
        // Remove failed AI message (for both main and sub-conversations)
        setChatState((prev) => ({
          ...prev,
          messages: prev.messages.filter((msg) => msg._id !== aiMessageId),
        }));
        toast.error("Failed to send AI response");
      }

      console.log("ai output size (cleaned):", cleanedText.trim().length);
      console.log("ai input size:", messageText.trim().length);

      // Call increment AI request count API
      if (success && businessLocationId) {
        try {
          await axiosInstance.post(ApiEndPoints.CHAT.incrementAiRequestCount, {
            businessLocationId: businessLocationId,
            aiInputCount: messageText.trim().length,
            aiOutputCount: cleanedText.trim().length,
          });
        } catch (error) {
          console.error("Failed to increment AI request count:", error);
        }
      }
    } catch (error) {
      console.error("AI streaming error:", error);
      console.error("Error details:", {
        message: error.message,
        stack: error.stack,
        name: error.name,
      });

      toast.error(`Failed to get AI response: ${error.message}`);

      // Remove failed AI message
      setChatState((prev) => ({
        ...prev,
        messages: prev.messages.filter(
          (msg) => msg._id !== aiStreamingMessage?._id
        ),
      }));
    } finally {
      setIsAiStreaming(false);
      setAiStreamingMessage(null);

      // FIXED: Fallback cleanup - remove any remaining temp AI messages after a delay
      // This ensures temp messages are cleaned up even if replacement didn't happen
      setTimeout(() => {
        setChatState((prev) => {
          const now = Date.now();
          const cleanedMessages = prev.messages.filter((msg) => {
            // Remove temp AI messages that are older than 5 seconds
            if (msg.isTemp && msg.isAiStreaming) {
              const msgTime = new Date(msg.timestamp || msg.createdAt || Date.now()).getTime();
              const timeDiff = now - msgTime;
              // Remove if older than 5 seconds (should have been replaced by now)
              if (timeDiff > 5000) {
                return false; // Remove this old temp message
              }
            }
            return true; // Keep other messages
          });

          // Only update if messages were actually removed
          if (cleanedMessages.length !== prev.messages.length) {
            return { ...prev, messages: cleanedMessages };
          }
          return prev;
        });
      }, 5000); // 5 second delay
    }
  }, [
    conversationInfo,
    user,
    currentUserId,
    chatState.messages,
    aiStreamingMessage?._id,
    getBeemzAiUserId,
    getBeemzAiUserObject
  ]);

  const handleAddMembersToSubConversation = async (
    selectedUsers,
    subConversationId
  ) => {
    try {
      // API call to add members to the sub-conversation using the same endpoint as main conversation
      const response = await axiosInstance.post(
        `${ApiEndPoints.CONVERSATION.addMembers}/${subConversationId}`,
        {
          userIds: selectedUsers.map((user) => user._id),
        }
      );

      if (response.data.data.success) {
        // Show success message
        toast.success(
          `${selectedUsers.length} member(s) request sent   to sub-conversation`
        );

        // Refresh sub-conversations to show updated member list
        if (
          conversationInfo?.groupType === "business" &&
          conversationInfo?._id
        ) {
          emit("getSubConversations", {
            conversationId: conversationInfo._id,
          });
        }
      } else {
        throw new Error(
          response.data.message || "failed to add members to sub-conversation"
        );
      }
    } catch (error) {
      toast.error(
        "failed to add members to sub-conversation. please try again."
      );
    }
  };

  // Memoize processed messages to optimize rendering
  const processedMessages = useMemo(() => {
    return chatState.messages
      .filter((message) => {
        // Filter out payment messages for owners and team members
        if (
          message.type === "payment" &&
          isCurrentUserOwnerOrTeamMember()
        ) {
          return false;
        }

        // Filter out payment-request messages for users who have null payment type
        if (message.type === "payment-request") {
          // Check if current user has a non-null payment type in their order
          const currentUserOrder =
            message.orderId?.groupOrder?.userOrders?.find(
              (order) => String(order.userId) === String(currentUserId)
            );
          const hasValidPaymentType =
            currentUserOrder?.paymentType !== null &&
            currentUserOrder?.paymentType !== undefined;

          // Only show payment-request messages to users who have a valid payment type (not null)
          if (!hasValidPaymentType) {
            return false;
          }
        }

        return true;
      })
      .reduce((acc, message) => {
        // For payment-request messages, handle duplicates based on type
        if (message.type === "payment-request") {
          const orderId = message.orderId?._id;
          const messageText = message.text || "";

          // Check if this is a user-specific payment request (contains "needs to pay")
          const isUserSpecificRequest =
            messageText.includes("needs to pay");

          if (isUserSpecificRequest) {
            // For user-specific requests, filter by orderId + target user
            const targetUserMatch = messageText.match(
              /^(\w+)\s+needs to pay/
            );
            const targetUser = targetUserMatch ? targetUserMatch[1] : null;

            if (targetUser) {
              const uniqueKey = `${orderId}-${targetUser}`;
              const existingMessageIndex = acc.findIndex(
                (existingMsg) =>
                  existingMsg.type === "payment-request" &&
                  existingMsg.orderId?._id === orderId &&
                  existingMsg.text &&
                  existingMsg.text.includes(`${targetUser} needs to pay`)
              );

              if (existingMessageIndex === -1) {
                acc.push({
                  ...message,
                  isLatestPaymentRequest: true,
                  uniqueKey,
                });
                return acc;
              }

              if (existingMessageIndex !== -1) {
                acc[existingMessageIndex] = {
                  ...message,
                  isLatestPaymentRequest: true,
                  uniqueKey,
                };
                return acc;
              }
            }
          } else {
            const existingMessages = acc.filter(
              (existingMsg) =>
                existingMsg.type === "payment-request" &&
                existingMsg.orderId?._id === orderId
            );

            if (existingMessages.length === 0) {
              acc.push({ ...message, isLatestPaymentRequest: true });
              return acc;
            }

            if (existingMessages.length > 0) {
              acc = acc.map((existingMsg) => {
                if (
                  existingMsg.type === "payment-request" &&
                  existingMsg.orderId?._id === orderId
                ) {
                  return { ...existingMsg, isLatestPaymentRequest: false };
                }
                return existingMsg;
              });

              acc.push({ ...message, isLatestPaymentRequest: true });
              return acc;
            }
          }
        }

        acc.push(message);
        return acc;
      }, []);
  }, [chatState.messages, currentUserId, conversationInfo, isCurrentUserOwnerOrTeamMember]);


  if (!selectedContact) {
    return (
      <MainChat>
        <NoConversationMessage>
          <Typography
            variant="h5"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "1.5rem", sm: "2rem" },
            }}
          >
            no conversation selected
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              fontSize: { xs: "0.875rem", sm: "1rem" },
            }}
          >
            select a conversation from the list to start chatting
          </Typography>
        </NoConversationMessage>
      </MainChat>
    );
  }

  return (
    <MainChat className="chat-view-mobile">
      {/* Chat Header - Wrapped for better sticky positioning */}
      {!hideHeader && (
        <Box
          sx={{
            position: "relative",
            zIndex: 1200,
            [theme.breakpoints.down("md")]: {
              position: "sticky",
              top: 0,
              zIndex: 1200,
            },
          }}
        >
          <ChatHeader
            selectedContact={selectedContact}
            isConnected={selectedContact.isGroup ? isConnected : oppositeUserOnline}
            onClose={onClose}
            handleOpenGroupInfo={handleOpenGroupInfo}
            handleMenuOpen={handleMenuOpen}
            handleMenuClose={handleMenuClose}
            handleMenuAction={handleMenuAction}
            menuAnchorEl={menuAnchorEl}
            isMenuOpen={isMenuOpen}
            isOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember()}
            conversationInfo={conversationInfo}
            currentUserId={currentUserId}
          />
        </Box>
      )}

      {/* Chat Banner */}
      {conversationInfo && conversationInfo.chatCategory && (
        <ChatBanner>{conversationInfo.chatCategory.name}</ChatBanner>
      )}

      {/* FIXED: Conversation Type Indicator */}
      {conversationInfo?.groupType === "business" && (
        <Box
          sx={{
            padding: theme.spacing(1, 2),
            backgroundColor: selectedSubConversationId ? "#E0E7FF" : "#F3F4F6",
            color: selectedSubConversationId ? "#3730A3" : "#6B7280",
            textAlign: "center",
            fontSize: "0.75rem",
            fontWeight: 500,
            borderBottom: "1px solid #E5E7EB",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          {selectedSubConversationId ? (
            <>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#8B5CF6",
                  display: "inline-block",
                }}
              />
              sub-conversation active
            </>
          ) : (
            <>
              <Box
                component="span"
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: "#10B981",
                  display: "inline-block",
                }}
              />
              main conversation
            </>
          )}
        </Box>
      )}

      {/* FIXED: Sub-Conversations Section - Only show for business groups */}
      {/* When a sub-conversation is selected, it shows sub-conversation messages */}
      {/* When no sub-conversation is selected, it shows main conversation messages */}
      {conversationInfo?.groupType === "business" && (
        <SubConversations
          subConversations={subConversations}
          selectedSubConversationId={selectedSubConversationId}
          onSelectionChange={handleSubConversationSelectionChange}
          onSubConversationMenuOpen={handleSubConversationMenuOpen}
        />
      )}


      {/* Messages List */}
      <ChatMessageList
        messages={processedMessages}
        isLoadingMore={chatState.isLoadingMore}
        messageContainerRef={messageContainerRef}
        onMediaClick={handleMediaClick}
        onEmojiReaction={handleEmojiReaction}
        onForwardMessage={handleOpenForwardDialog}
        onReplyToMessage={handleReplyToMessage}
        onViewOrder={handleOpenOrderDetails}
        onOpenPaymentDialog={handleOpenPaymentDialog}
        onPaymentResponse={handlePaymentResponse}
        onOpenPaymentStrategyDialog={handleOpenPaymentStrategyDialog}
        onOpenPaymentRequestDialog={handleOpenPaymentRequestDialog}
        onOpenPaymentConfirmationDialog={handleOpenPaymentConfirmationDialog}
        onSearchMenuItemByName={handleSearchMenuItemByName}
        selectedContact={selectedContact}
        conversationInfo={conversationInfo}
        isCurrentUserOwnerOrTeamMember={isCurrentUserOwnerOrTeamMember()}
        enableInAppWalletPayments={enableInAppWalletPayments}
        onOpenNotes={handleOpenNotes}
        onViewLocation={handleViewLocation}
      />

      {/* Menu Button - Show when showMenu is true (sub-conversation or main conversation) */}
      {(() => {
        // hide menu when conversation is closed
        const isConversationOpen = conversationInfo?.isOpen !== false;
        if (!isConversationOpen) return null;

        // Check if the latest order is closed
        const orderMessages = chatState.messages.filter(
          (msg) => msg.type === "order" && msg.orderId
        );
        const latestOrderMessage = orderMessages.length > 0 
          ? orderMessages[orderMessages.length - 1] 
          : null;
        const isOrderClosed = latestOrderMessage?.orderId?.orderStatus === "closed order";
        
        // Hide menu if order is closed
        if (isOrderClosed) return null;

        // Find the selected sub-conversation
        const selectedSubConv = subConversations.find(
          (sc) => sc._id === selectedSubConversationId
        );

        // Check if the selected sub-conversation has showMenu enabled
        const isSubConversationShowMenu =
          selectedSubConv && selectedSubConv.showMenu === true;

        // Check if the main conversation has showMenu enabled (when no sub-conversation is selected)
        const isMainConversationShowMenu =
          !selectedSubConversationId &&
          conversationInfo?.showMenu === true;

        // Show menu button if either condition is true
        const shouldShowMenu =
          isSubConversationShowMenu || isMainConversationShowMenu;

        return shouldShowMenu ? (
          <Box
            sx={{
              padding: { xs: 1.5, sm: 2 },
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              // mobile-specific positioning
              [theme.breakpoints.down("md")]: {
                position: "fixed",
                bottom: "64px", // above the input field
                left: 0,
                right: 0,
                width: "100%",
                zIndex: 9997, // below input but above other content
                boxShadow: "0 -2px 8px rgba(0,0,0,0.1)",
              },
            }}
          >
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenMenuDialog}
              sx={{
                borderColor: "#6D28D9",
                backgroundColor: "rgba(124, 58, 237, 0.04)",
                textTransform: "lowercase",
                fontWeight: 500,
                fontSize: { xs: "0.875rem", sm: "1rem" },
                padding: { xs: "8px 16px", sm: "10px 20px" },
                borderRadius: 2,
                "&:hover": {
                  borderColor: "#7C3AED",
                  color: "#fff",
                },
              }}
            >
              menu & catalog
            </Button>
          </Box>
        ) : null;
      })()}

      {/* Message Input */}
      {conversationInfo?.isOpen !== false && !isCurrentUserRemovedByAdmin && (
        <MessageInput
          newMessage={chatState.newMessage}
          onInputChange={handleInputChange}
          onSendMessage={handleSendMessage}
          onSendMessageWithFormattedText={handleSendMessageWithFormattedText}
          onFileSelect={handleFileSelect}
          onOpenChatCategories={handleOpenChatCategories}
          onKeyPress={handleKeyPress}
          isConnected={isConnected}
          isUploading={mediaState.isUploading}
          selectedFiles={mediaState.selectedFiles}
          onRemoveFile={removeFile}
          onClearAllFiles={() =>
            setMediaState((prev) => ({ ...prev, selectedFiles: [] }))
          }
          uploadProgress={mediaState.uploadProgress}
          conversationInfo={conversationInfo}
          isSidebarOpen={isSidebarOpen}
          replyPreview={replyPreview}
          onCancelReply={handleCancelReply}
          onMentionsChange={setMessageMentions}
          isAiStreaming={isAiStreaming}
          onOpenLocationDialog={handleOpenLocationDialog}
          quickReplySuggestions={quickReplySuggestions}
          onQuickReplySelect={handleQuickReplySelect}
        />
      )}

      {/* media viewer dialog */}
      <MediaViewerDialog
        open={mediaState.mediaViewerOpen}
        onClose={handleCloseMediaViewer}
        selectedMedia={mediaState.selectedMedia}
        currentMediaIndex={mediaState.currentMediaIndex}
        onPrevMedia={handlePrevMedia}
        onNextMedia={handleNextMedia}
        onDownloadMedia={handleDownloadMedia}
        onDownloadAllMedia={handleDownloadAllMedia}
      />

      {/* Chat Categories Popup - Only show for business groups */}
      {conversationInfo?.groupType === "business" && (
        <ChatCategoriesPopup
          open={chatCategoriesOpen}
          onClose={handleCloseChatCategories}
          chatCategories={chatCategories}
          chatCategoriesLoading={chatCategoriesLoading}
          onCategorySelect={handleCategorySelect}
          subConversations={subConversations}
          conversationInfo={conversationInfo}
        />
      )}


      {/* Sub-Conversation Options Menu */}
      <SubConversationMenu
        anchorEl={subConversationMenuAnchor}
        open={Boolean(subConversationMenuAnchor)}
        onClose={handleSubConversationMenuClose}
        onAction={handleSubConversationMenuAction}
      />

      {/* Group Info Panel */}
      <GroupInfo
        isOpen={groupInfoState.isOpen}
        onClose={handleCloseGroupInfo}
        conversationId={selectedContact?.id}
        groupName={selectedContact?.name}
        isGroup={selectedContact?.isGroup || conversationInfo?.isGroup}
        conversationInfo={conversationInfo}
        onMinimize={handleMinimizeGroupInfo}
        onFullscreen={handleFullscreenGroupInfo}
      />

      <SubConversationInfo
        isOpen={subConversationInfoState.isOpen}
        onClose={handleCloseSubConversationInfo}
        subConversationId={infoSubConversation?._id}
        subConversationData={infoSubConversation}
      />

      {/* Forward Dialog */}
      <ForwardDialog
        open={forwardDialogState.open}
        onClose={handleCloseForwardDialog}
        message={forwardDialogState.message}
        socket={socket}
      />

      {/* Order Details Dialog */}
      <OrderDetailsDialog
        open={orderDetailsState.open}
        onClose={handleCloseOrderDetails}
        orderId={orderDetailsState.orderId}
        locationId={conversationInfo?.businessLocationId?._id}
      />

      {/* Cancellation Details Dialog (for owner/team members) */}
      <BusinessOrderDetailsDialog
        open={cancellationDetailsState.open}
        onClose={handleCloseCancellationDetails}
        orderId={cancellationDetailsState.orderId}
        messageType={cancellationDetailsState.messageType}
        conversationId={selectedContact?.id}
        conversationInfo={conversationInfo}
        storedUserId={currentUserId}
        onPayAsCustomer={(oid) => setOrderDetailsState({ open: true, orderId: oid })}
      />

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialogState.open}
        onClose={handleClosePaymentDialog}
        paymentData={paymentDialogState.message || {}}
        orderId={paymentDialogState.orderId}
        onPaymentSubmit={handlePaymentSubmit}
        groupMemberCount={paymentDialogState.groupMemberCount}
        messageType={paymentDialogState.message?.type}
      />

      {/* Payment Strategy Dialog */}
      <PaymentStrategyDialog
        open={paymentStrategyDialogState.open}
        onClose={handleClosePaymentStrategyDialog}
        orderData={paymentStrategyDialogState.orderData}
        onPaymentInitiated={handlePaymentInitiated}
        currentUserId={user?.id}
      />

      {/* Payment Request Dialog */}
      <PaymentRequestDialog
        open={paymentRequestDialogState.open}
        onClose={handleClosePaymentRequestDialog}
        message={paymentRequestDialogState.message}
        currentUserId={user?.id}
        onPaymentResponse={handlePaymentResponse}
        onRetryRequest={handleRetryPaymentRequest}
      />

      {/* Payment Confirmation Dialog */}
      <PaymentConfirmationDialog
        open={paymentConfirmationDialogState.open}
        onClose={handleClosePaymentConfirmationDialog}
        orderData={paymentConfirmationDialogState.orderData}
        paymentType={paymentConfirmationDialogState.paymentType}
        onPaymentProcessed={handlePaymentProcessed}
        currentUserId={user?.id}
      />

      {/* Menu Dialog */}
      <MenuDialog
        open={menuDialogOpen}
        onClose={handleCloseMenuDialog}
        businessLocationId={conversationInfo?.businessLocationId?._id ?? conversationInfo?.businessLocationId}
        conversationId={selectedSubConversationId || currentConversationRef.current}
        onItemClick={handleOpenItemOrderDetail}
        onCartClick={handleOpenCartDialog}
        onRefetchReady={(refetchFn) => {
          menuRefetchRef.current = refetchFn;
        }}
      />

      {/* Item Order Detail Dialog */}
      <ItemOrderDetailDialog
        open={itemOrderDetailOpen}
        onClose={handleCloseItemOrderDetail}
        onBack={handleBackToMenu}
        itemData={selectedMenuItem}
        cartItemData={selectedCartItem}
        businessLocationId={conversationInfo?.businessLocationId?._id ?? conversationInfo?.businessLocationId}
        businessId={selectedMenuItem?.businessId}
        conversationId={selectedSubConversationId || currentConversationRef.current}
      />

      {/* Cart Dialog */}
      <CartDialog
        open={cartDialogOpen}
        onClose={handleCloseCartDialog}
        cartData={cartData}
        onEditItem={handleEditCartItem}
        onRemoveItem={handleRemoveCartItem}
        onUpdateQuantity={handleUpdateCartQuantity}
        onPlaceOrder={handlePlaceOrder}
        onBack={handleBackToMenuFromCart}
        onItemClick={handleOpenItemOrderDetail} // Add this new prop
        loading={cartLoading}
        conversationType={selectedSubConversationId ? "sub" : "main"}
        conversationId={selectedSubConversationId || currentConversationRef.current}
        businessLocationId={conversationInfo?.businessLocationId?._id ?? conversationInfo?.businessLocationId}
      />

      {/* Add Member Dialog - for business groups */}
      <AddMemberDialog
        open={addMemberDialogOpen}
        onClose={handleCloseAddMemberDialog}
        conversationId={addMemberSubConversation?._id || selectedContact?.id}
        currentMembers={conversationInfo?.users || []}
        onAddMembers={handleAddMembers}
        // Sub-conversation specific props
        isSubConversation={!!addMemberSubConversation}
        subConversationData={addMemberSubConversation}
        onAddMembersToSubConversation={handleAddMembersToSubConversation}
      />

      {/* Participant Selection Dialog - for regular groups (Group type) */}
      <ParticipantSelectionDialog
        open={participantSelectionDialogOpen}
        onClose={handleCloseParticipantSelectionDialog}
        onConfirm={async (selectedUsers) => {
          try {
            await handleAddMembersToRegularGroup(selectedUsers, selectedContact?.id);
            setParticipantSelectionDialogOpen(false);
          } catch (error) {
            // Error is already handled in handleAddMembersToRegularGroup
            // Keep dialog open so user can try again if needed
          }
        }}
        selectedParticipantIds={existingParticipantIds}
      />

      {/* Location Dialog (for selecting location to send) */}
      <SelectLocation
        open={locationDialogOpen}
        setOpen={setLocationDialogOpen}
        onSelect={handleLocationSelect}
      />

      {/* Location View Dialog (for viewing location from messages) */}
      <LocationViewDialog
        open={locationViewDialogOpen}
        onClose={handleCloseLocationViewDialog}
        locationData={locationViewData}
      />


      {/* Exit Group Confirmation Dialog */}
      <ExitGroupDialog
        open={exitGroupDialogOpen}
        onClose={handleCloseExitGroupDialog}
        onConfirm={handleExitGroup}
      />

      {/* Clear Chat Confirmation Dialog */}
      <ClearChatDialog
        open={clearChatDialogOpen}
        onClose={handleCloseClearChatDialog}
        onConfirm={handleClearChat}
      />

      {/* Notes Action Sheets - Render multiple dialogs */}
      {openNotesDialogs.map((dialog) => (
        <NotesActionSheet
          key={dialog.miniConversationId}
          open={dialog.open}
          onClose={() => handleCloseNotes(dialog.miniConversationId)}
          miniConversationId={dialog.miniConversationId}
          message={dialog.message}
        />
      ))}
    </MainChat>
  );
});

// Set display name for better debugging
ChatView.displayName = 'ChatView';

export default memo(ChatView);

